/* eslint-disable react/no-unescaped-entities */

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useAuth } from "../App";
import Sidebar from "../components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import {
  Plus, Search, Send, Eye, Edit, Trash2,
  Mail, Users, BarChart3, Clock,
  MousePointer, Upload, FileText, RefreshCw,
  AlertTriangle, Download, List, UserPlus, LayoutTemplate, Check, Paintbrush,
  Globe, PieChart, Monitor, Smartphone, Tablet, MapPin, Chrome, X,
  Server, Loader2, Copy, Image as ImageIcon, RotateCw, Filter, UserX,
  Eraser, Ban, Wrench,
  Activity, CheckCircle2, AlertCircle, ShieldCheck, Gauge, Pause,
  ArrowUp, ArrowDown, ArrowUpDown, CalendarClock
} from "lucide-react";
import {
  PieChart as RechartsPieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, LabelList,
  LineChart, Line
} from "recharts";
import PlantillasGallery from "../components/marketing/PlantillasGallery";
import VisualEmailEditor from "../components/marketing/VisualEmailEditor";

const API_URL = (process.env.REACT_APP_BACKEND_URL || "");

export default function ArticulosPage() {
  const { user, token } = useAuth();
  // Permiso "solo estadísticas": el usuario solo puede ver estadísticas de artículos.
  // Se ocultan todas las acciones de crear/editar/borrar/clonar/enviar en todas las pestañas.
  const soloEstadisticas = !!(user && user.marketing_solo_estadisticas);
  const [loading, setLoading] = useState(true);
  // Migration state (admin only)
  const [migratingSpacing, setMigratingSpacing] = useState(false);
  const [showMigrationDialog, setShowMigrationDialog] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);
  const [activeTab, setActiveTab] = useState("articulos");
  
  // Articulos state
  const [articulos, setArticulos] = useState([]);
  const [searchArticulo, setSearchArticulo] = useState("");
  const [debouncedSearchArticulo, setDebouncedSearchArticulo] = useState("");
  const [articuloSort, setArticuloSort] = useState({ key: "fecha_envio", dir: "desc" });
  
  // Listas de contactos state
  const [listas, setListas] = useState([]);
  const [selectedLista, setSelectedLista] = useState(null);
  const [contactosLista, setContactosLista] = useState([]);
  
  // Envios state
  const [envios, setEnvios] = useState([]);
  const [programados, setProgramados] = useState([]);
  const [schedulerHealth, setSchedulerHealth] = useState(null);

  // Desuscritos state
  const [desuscritos, setDesuscritos] = useState([]);
  const [searchDesuscrito, setSearchDesuscrito] = useState("");
  // Búsqueda dentro de la lista seleccionada y búsqueda global en todas las listas
  const [searchContactoLista, setSearchContactoLista] = useState("");
  const [searchContactoGlobal, setSearchContactoGlobal] = useState("");
  const [resultadosGlobal, setResultadosGlobal] = useState(null);
  const [buscandoGlobal, setBuscandoGlobal] = useState(false);
  const [exportandoDesuscritos, setExportandoDesuscritos] = useState(false);
  const [loadingDesuscritos, setLoadingDesuscritos] = useState(false);

  // No deseados (blocklist) state
  const [blocklist, setBlocklist] = useState([]);
  const [searchBlocklist, setSearchBlocklist] = useState("");
  const [loadingBlocklist, setLoadingBlocklist] = useState(false);
  const [newBlocklistValue, setNewBlocklistValue] = useState("");
  const [newBlocklistReason, setNewBlocklistReason] = useState("");
  const [addingBlocklist, setAddingBlocklist] = useState(false);
  
  // Estadisticas state
  const [estadisticasGenerales, setEstadisticasGenerales] = useState(null);
  // Estadísticas separadas por audiencia (interno = usuarios del sistema, externo = listas/contactos)
  const [statsExterno, setStatsExterno] = useState(null);
  const [statsInterno, setStatsInterno] = useState(null);
  // Vista de tarjetas: "todos" | "interno" | "externo". Por defecto externo.
  const [audienciaView, setAudienciaView] = useState("externo");
  // When set, the KPI cards at the top are filtered to this article only.
  // Click a row in the table to toggle; click again to clear back to global.
  const [filtroArticuloId, setFiltroArticuloId] = useState(null);
  
  // Dialogs
  const [showArticuloDialog, setShowArticuloDialog] = useState(false);
  const [showListaDialog, setShowListaDialog] = useState(false);
  const [showEnvioDialog, setShowEnvioDialog] = useState(false);
  const [showEnvioPreview, setShowEnvioPreview] = useState(false);
  const [envioPreview, setEnvioPreview] = useState(null);
  const [envioPreviewLoading, setEnvioPreviewLoading] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [showEstadisticasDialog, setShowEstadisticasDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importUpdateExisting, setImportUpdateExisting] = useState(false);
  const [importFileObj, setImportFileObj] = useState(null);
  const [importSheets, setImportSheets] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState("");
  const [importSheetsLoading, setImportSheetsLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [importPreviewLoading, setImportPreviewLoading] = useState(false);
  const [manualMapping, setManualMapping] = useState({});
  const [showContactoDialog, setShowContactoDialog] = useState(false);
  const [showPlantillasGallery, setShowPlantillasGallery] = useState(false);
  const [showVisualEditor, setShowVisualEditor] = useState(false);
  const [showDominioStatsDialog, setShowDominioStatsDialog] = useState(false);
  const [dominioStats, setDominioStats] = useState(null);
  const [loadingDominioStats, setLoadingDominioStats] = useState(false);
  const [showDetalleCorreosDialog, setShowDetalleCorreosDialog] = useState(false);
  const [detalleCorreos, setDetalleCorreos] = useState(null);
  const [loadingDetalleCorreos, setLoadingDetalleCorreos] = useState(false);
  const [currentEnvioId, setCurrentEnvioId] = useState(null);
  const [currentArticuloId, setCurrentArticuloId] = useState(null);
  const [articuloDomainStats, setArticuloDomainStats] = useState(null);
  const [loadingArticuloDomainStats, setLoadingArticuloDomainStats] = useState(false);
  const [showReenvioConfirm, setShowReenvioConfirm] = useState(false);
  const [reenvioLoading, setReenvioLoading] = useState(false);
  const [reenvioProgramar, setReenvioProgramar] = useState(false);
  const [reenvioFecha, setReenvioFecha] = useState("");
  const [engagementStats, setEngagementStats] = useState(null);
  const [loadingEngagement, setLoadingEngagement] = useState(false);
  const [engagementMode, setEngagementMode] = useState("aperturas"); // "aperturas" | "clicks"
  const [engDetalle, setEngDetalle] = useState({ open: false, loading: false, tipo: null, valor: null, modo: "aperturas", correos: [] });

  const handleViewEngagementDetalle = async (tipo, valor) => {
    const articuloId = currentArticuloId || estadisticasArticulo?.articulo?.id;
    if (!articuloId || !tipo || !valor) return;
    setEngDetalle({ open: true, loading: true, tipo, valor, modo: engagementMode, correos: [] });
    try {
      const url = `${API_URL}/api/articulos/estadisticas/${articuloId}/engagement-detalle?tipo=${encodeURIComponent(tipo)}&valor=${encodeURIComponent(valor)}&modo=${engagementMode}`;
      const res = await fetch(url, getAuthHeaders());
      const data = await res.json();
      if (res.ok) {
        setEngDetalle({ open: true, loading: false, tipo, valor, modo: engagementMode, correos: data.correos || [] });
      } else {
        toast.error(data.detail || "No se pudo cargar el detalle");
        setEngDetalle(prev => ({ ...prev, loading: false }));
      }
    } catch (e) {
      toast.error("Error al cargar el detalle del segmento");
      setEngDetalle(prev => ({ ...prev, loading: false }));
    }
  };

  // ====== Interacción a lo largo del tiempo ======
  const [timelineData, setTimelineData] = useState(null);
  const [timelineRango, setTimelineRango] = useState("24h"); // "24h" | "48h" | "7d" | "30d"
  const [loadingTimeline, setLoadingTimeline] = useState(false);

  // ====== Reporte Mensual de KPIs ======
  const [showReporteMensualDialog, setShowReporteMensualDialog] = useState(false);
  const [reporteMensualConfig, setReporteMensualConfig] = useState(null);
  const [reporteMensualLoading, setReporteMensualLoading] = useState(false);
  const [reporteMensualSaving, setReporteMensualSaving] = useState(false);
  const [reporteMensualSending, setReporteMensualSending] = useState(false);
  const [nuevoEmailReporte, setNuevoEmailReporte] = useState("");

  // ====== SMTP Marketing Configuration (admin only) ======
  const [showSmtpMarketingDialog, setShowSmtpMarketingDialog] = useState(false);
  const [smtpMarketingLoading, setSmtpMarketingLoading] = useState(false);
  const [smtpMarketingSaving, setSmtpMarketingSaving] = useState(false);
  const [smtpTesting, setSmtpTesting] = useState(false);
  const [smtpTestEmail, setSmtpTestEmail] = useState("");
  const [dnsChecking, setDnsChecking] = useState(false);
  const [dnsCheck, setDnsCheck] = useState(null);
  const [smtpMarketingConfig, setSmtpMarketingConfig] = useState({
    smtp_server: "",
    smtp_port: 587,
    username: "",
    password: "",
    from_name: "QAsesores Marketing",
    from_email: "",
    use_tls: true,
    use_ssl: false,
    enabled: true,
    throttle_enabled: true,
    batch_size: 50,
    batch_pause_seconds: 60,
  });

  // Form state
  const [articuloForm, setArticuloForm] = useState({
    titulo: "",
    asunto: "",
    contenido_html: "",
    contenido_tipo: "editor",
    imagen_url: "",
    categoria: "",
    editor_config: null // Store visual editor configuration
  });
  const [editingArticulo, setEditingArticulo] = useState(null);
  
  const [listaForm, setListaForm] = useState({ nombre: "", descripcion: "" });
  const [editingLista, setEditingLista] = useState(null);
  const [contactoForm, setContactoForm] = useState({ email: "", nombre: "", empresa: "", telefono: "", clase_cliente: "" });
  const [editingContacto, setEditingContacto] = useState(null);
  
  const [envioForm, setEnvioForm] = useState({
    articulo_id: "",
    lista_ids: [],
    contactos_adicionales: "",
    programado: false,
    fecha_programada: "",
    enviar_usuarios_sistema: false,
    usuarios_ids: [],
    nombre_remitente: "Qasesores Corredores y Asesores de Seguros SRL",
    enviar_como_imagen: false,
    imagen_ancho: 600,
    imagen_alto: 1056,
    imagen_autoajuste: true,
    imagen_link_url: "",
    incluir_link_ver_online: true,
  });
  
  const [estadisticasArticulo, setEstadisticasArticulo] = useState(null);
  // Modal de destinatarios (preview antes de enviar y segmentación de enviados)
  const [destinatariosModal, setDestinatariosModal] = useState({ open: false, loading: false, titulo: "", grupos: [], total: 0, sinEnvios: false });
  const [destinatariosSearch, setDestinatariosSearch] = useState("");


  // Re-render every second while a send is in pausa_lote so the
  // "reanuda en Xs" countdown decreases smoothly (without waiting 5s
  // for the next stats repoll).
  const [, setLiveTick] = useState(0);
  useEffect(() => {
    const estado = estadisticasArticulo?.resumen?.envio_activo?.estado;
    if (estado !== "pausa_lote") return;
    const t = setInterval(() => setLiveTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, [estadisticasArticulo?.resumen?.envio_activo?.estado]);
  const [usuariosSistema, setUsuariosSistema] = useState([]);
  // Modal de selección de usuarios del sistema para el envío de artículos
  const [showUsuariosModal, setShowUsuariosModal] = useState(false);
  const [searchUsuarioModal, setSearchUsuarioModal] = useState("");
  const [plantillas, setPlantillas] = useState([]);
  const [testingEmail, setTestingEmail] = useState(false);

  const getAuthHeaders = useCallback(() => ({
    headers: { Authorization: `Bearer ${token}` }
  }), [token]);

  const openDestinatariosPreview = useCallback(async () => {
    const adicionales = (envioForm.contactos_adicionales || "")
      .split(/[,;\n]/).map(e => e.trim()).filter(e => e);
    if ((envioForm.lista_ids || []).length === 0 && adicionales.length === 0 && !envioForm.enviar_usuarios_sistema) {
      toast.error("Selecciona al menos una lista, usuarios del sistema o emails adicionales");
      return;
    }
    setDestinatariosSearch("");
    setDestinatariosModal({ open: true, loading: true, titulo: "Destinatarios a enviar", grupos: [], total: 0, sinEnvios: false });
    try {
      const res = await fetch(`${API_URL}/api/articulos/destinatarios-preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders().headers },
        body: JSON.stringify({
          lista_ids: envioForm.lista_ids || [],
          contactos_adicionales: adicionales,
          enviar_usuarios_sistema: envioForm.enviar_usuarios_sistema,
          usuarios_ids: envioForm.usuarios_ids || [],
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Error");
      setDestinatariosModal({ open: true, loading: false, titulo: "Destinatarios a enviar", grupos: data.grupos || [], total: data.total_unico || 0, sinEnvios: false });
    } catch (e) {
      toast.error("No se pudieron cargar los destinatarios");
      setDestinatariosModal((m) => ({ ...m, loading: false }));
    }
  }, [envioForm, getAuthHeaders]);

  const openDestinatariosEnviados = useCallback(async (articuloId, titulo) => {
    setDestinatariosSearch("");
    setDestinatariosModal({ open: true, loading: true, titulo: `Destinatarios por lista${titulo ? `: ${titulo}` : ""}`, grupos: [], total: 0, sinEnvios: false });
    try {
      const res = await fetch(`${API_URL}/api/articulos/${articuloId}/destinatarios-por-lista`, getAuthHeaders());
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Error");
      setDestinatariosModal({ open: true, loading: false, titulo: `Destinatarios por lista${titulo ? `: ${titulo}` : ""}`, grupos: data.grupos || [], total: data.total_unico || 0, sinEnvios: !!data.sin_envios });
    } catch (e) {
      toast.error("No se pudieron cargar los destinatarios");
      setDestinatariosModal((m) => ({ ...m, loading: false }));
    }
  }, [getAuthHeaders]);

  const openDestinatariosEnvio = useCallback(async (envioId, titulo) => {
    setDestinatariosSearch("");
    const t = `Destinatarios${titulo ? `: ${titulo}` : ""}`;
    setDestinatariosModal({ open: true, loading: true, titulo: t, grupos: [], total: 0, sinEnvios: false });
    try {
      const res = await fetch(`${API_URL}/api/articulos/envios/${envioId}/destinatarios-por-lista`, getAuthHeaders());
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Error");
      setDestinatariosModal({ open: true, loading: false, titulo: t, grupos: data.grupos || [], total: data.total_unico || 0, sinEnvios: !!data.sin_snapshot });
    } catch (e) {
      toast.error("No se pudieron cargar los destinatarios");
      setDestinatariosModal((m) => ({ ...m, loading: false }));
    }
  }, [getAuthHeaders]);

  // ===== Auto-refresh stats while a send is in progress =====
  // When the user is viewing the Estadísticas modal of an article whose
  // last envio is still `enviando` or `pausa_lote`, repoll the stats
  // endpoint every 5s so the progress bar updates in near-real-time.
  // Stops automatically when the dialog closes or the send completes.
  useEffect(() => {
    if (!showEstadisticasDialog) return;
    const estado = estadisticasArticulo?.resumen?.envio_activo?.estado;
    const isLive = estado === "enviando" || estado === "pausa_lote" || estado === "en_cola";
    if (!isLive) return;
    const articuloId = estadisticasArticulo?.articulo?.id;
    if (!articuloId) return;
    const interval = setInterval(async () => {
      try {
        const res = await fetch(
          `${API_URL}/api/articulos/estadisticas/${articuloId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.ok) {
          const data = await res.json();
          setEstadisticasArticulo(data);
        }
      } catch { /* keep polling — silent retry */ }
    }, 5000);
    return () => clearInterval(interval);
  }, [
    showEstadisticasDialog,
    estadisticasArticulo?.resumen?.envio_activo?.estado,
    estadisticasArticulo?.articulo?.id,
    token,
  ]);

  // ===== Admin migration: limpiar paddings de artículos existentes =====
  const handleMigrateRemoveSpacing = async () => {
    setMigratingSpacing(true);
    setMigrationResult(null);
    try {
      const res = await fetch(`${API_URL}/api/articulos/migrate/remove-section-spacing`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.detail || "Error en la migración");
      }
      setMigrationResult(data);
      toast.success(`Migración completada: ${data.actualizados_contenido_html} artículos limpiados`);
      // Refrescar lista de artículos para que el usuario vea los cambios
      try { fetchArticulos(); } catch (e) { /* noop */ }
    } catch (err) {
      toast.error(err.message || "No se pudo ejecutar la migración");
    } finally {
      setMigratingSpacing(false);
    }
  };

  // Fetch functions
  // Debounce de la búsqueda: evita disparar una llamada API por cada letra.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearchArticulo(searchArticulo), 400);
    return () => clearTimeout(t);
  }, [searchArticulo]);

  const fetchArticulos = useCallback(async () => {
    try {
      let url = `${API_URL}/api/articulos?limit=100`;
      if (debouncedSearchArticulo) url += `&busqueda=${encodeURIComponent(debouncedSearchArticulo)}`;
      
      const res = await fetch(url, getAuthHeaders());
      const data = await res.json();
      setArticulos(data.items || []);
    } catch (error) {
      console.error("Error fetching articulos:", error);
    }
  }, [getAuthHeaders, debouncedSearchArticulo]);

  // ====== Orden de la tabla Mis Artículos ======
  const handleArticuloSort = (key) => {
    setArticuloSort((prev) => prev.key === key
      ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
      : { key, dir: ["titulo", "asunto", "contenido_tipo"].includes(key) ? "asc" : "desc" });
  };

  const sortedArticulos = useMemo(() => {
    const base = audienciaView === "todos" ? articulos : articulos.filter((a) => (a.audiencias || []).includes(audienciaView));
    const { key, dir } = articuloSort;
    const mult = dir === "asc" ? 1 : -1;
    const arr = [...base];
    if (key === "fecha_envio") {
      // Los artículos con envío programado pendiente van SIEMPRE al final del
      // bloque de enviados (ordenados por su fecha programada), aunque tengan
      // envíos anteriores; al final los que no tienen envío ni programación.
      const rank = (a) => (a.proxima_programada ? 1 : a.ultima_fecha_envio ? 0 : 2);
      arr.sort((a, b) => {
        const ra = rank(a), rb = rank(b);
        if (ra !== rb) return ra - rb;
        if (ra === 0) return ((a.ultima_fecha_envio || "").localeCompare(b.ultima_fecha_envio || "")) * mult;
        if (ra === 1) return (a.proxima_programada || "").localeCompare(b.proxima_programada || "");
        return (b.created_at || "").localeCompare(a.created_at || "");
      });
    } else if (["total_enviados", "total_rebotados", "total_abiertos", "total_clicks"].includes(key)) {
      arr.sort((a, b) => ((a[key] || 0) - (b[key] || 0)) * mult);
    } else {
      arr.sort((a, b) => ((a[key] || "").toString().toLowerCase().localeCompare((b[key] || "").toString().toLowerCase())) * mult);
    }
    return arr;
  }, [articulos, audienciaView, articuloSort]);

  // Desde "Programados": ir al artículo en la pestaña Artículos (scroll + resaltado)
  const irAlArticulo = useCallback((articuloId) => {
    if (!articuloId) return;
    const art = articulos.find((a) => a.id === articuloId);
    if (art && audienciaView !== "todos" && !(art.audiencias || []).includes(audienciaView)) {
      setAudienciaView("todos");
    }
    setActiveTab("articulos");
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="articulo-row-${articuloId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("ring-2", "ring-amber-400");
        setTimeout(() => el.classList.remove("ring-2", "ring-amber-400"), 2500);
      }
    }, 350);
  }, [articulos, audienciaView]);

  // ===== Artículos del Mes (correo recopilatorio mensual) =====
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [showRecapModal, setShowRecapModal] = useState(false);

  const buildEmailPreviewHtml = () => {
    let html = articuloForm.contenido_html || "";
    if (articuloForm.contenido_tipo === "imagen" && articuloForm.imagen_url) {
      html = `<div style="text-align:center;"><img src="${articuloForm.imagen_url}" style="max-width:100%;height:auto;" alt="${articuloForm.titulo || ""}" /></div>`;
    }
    html = html
      .replace(/\{\{\s*nombre\s*\}\}/gi, "Juan Pérez")
      .replace(/\{\{\s*email\s*\}\}/gi, "cliente@ejemplo.com")
      .replace(/\{\{\s*empresa\s*\}\}/gi, "Empresa Ejemplo SRL")
      .replace(/\{\{\s*telefono\s*\}\}/gi, "809-555-0000")
      .replace(/\{\{\s*clase_cliente\s*\}\}/gi, "PERSONAL");
    return `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"></head><body style="margin:0;padding:24px;background:#eef1f5;">
<div style="max-width:680px;margin:0 auto;background:#ffffff;border:1px solid #e2e8f0;border-radius:8px;overflow:hidden;box-shadow:0 1px 4px rgba(0,0,0,0.06);">
<div style="padding:12px 16px;border-bottom:1px solid #eeeeee;font-family:Arial,sans-serif;font-size:12px;color:#555;line-height:1.6;background:#fafbfc;">
<b>Asunto:</b> ${articuloForm.asunto || articuloForm.titulo || "(sin asunto)"}<br/>
<b>Para:</b> cliente@ejemplo.com
</div>
<div>${html}</div>
</div>
<p style="text-align:center;font-family:Arial,sans-serif;font-size:11px;color:#98a2b3;margin-top:14px;">Vista previa — así lo recibirá el cliente (datos de contacto de ejemplo)</p>
</body></html>`;
  };

  const [pruebaEmail, setPruebaEmail] = useState("");
  const [enviandoPrueba, setEnviandoPrueba] = useState(false);
  const enviarCorreoPrueba = async () => {
    const destino = (pruebaEmail || user?.email || "").trim();
    if (!destino.includes("@")) {
      toast.error("Introduce un correo válido para la prueba");
      return;
    }
    setEnviandoPrueba(true);
    try {
      const res = await fetch(`${API_URL}/api/articulos/enviar-prueba`, {
        method: "POST",
        ...getAuthHeaders(),
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify({
          email_destino: destino,
          asunto: articuloForm.asunto || articuloForm.titulo,
          contenido_html: articuloForm.contenido_html,
          contenido_tipo: articuloForm.contenido_tipo,
          imagen_url: articuloForm.imagen_url,
          titulo: articuloForm.titulo,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Error al enviar la prueba");
      toast.success(data.message || `Correo de prueba enviado a ${destino}`, { duration: 6000 });
    } catch (e) {
      toast.error(e.message || "Error al enviar la prueba");
    } finally {
      setEnviandoPrueba(false);
    }
  };

  const [recapMes, setRecapMes] = useState(() => new Date().toISOString().slice(0, 7));
  const [recapBusqueda, setRecapBusqueda] = useState("");
  const [recapSeleccion, setRecapSeleccion] = useState([]);
  const [recapFormato, setRecapFormato] = useState("profesional");
  const [recapLista, setRecapLista] = useState([]);
  const [recapLoading, setRecapLoading] = useState(false);
  const contenidoHtmlRef = useRef(null);

  const abrirRecapModal = async () => {
    setShowRecapModal(true);
    setRecapSeleccion([]);
    setRecapBusqueda("");
    // Si el artículo ya tiene un recap insertado, recordar los artículos
    // marcados, el mes y el formato para no tener que seleccionarlos de nuevo.
    const contenido = articuloForm.contenido_html || "";
    const mark = contenido.indexOf("<!-- ARTICULOS DEL MES");
    if (mark !== -1) {
      const finIdx = contenido.indexOf("<!-- FIN ARTICULOS DEL MES -->", mark);
      const bloque = finIdx !== -1 ? contenido.slice(mark, finIdx) : contenido.slice(mark);
      const cab = bloque.match(/ARTICULOS DEL MES\s*(\d{4}-\d{2})?\s*\(formato\s+(\w+)\)/i);
      if (cab && (cab[2] === "profesional" || cab[2] === "columna")) setRecapFormato(cab[2]);
      setRecapMes(cab && cab[1] ? cab[1] : "");
      const ids = [];
      const re = /read-online\/([A-Za-z0-9-]+)/g;
      let m;
      while ((m = re.exec(bloque)) !== null) { if (!ids.includes(m[1])) ids.push(m[1]); }
      if (ids.length) setRecapSeleccion(ids);
    }
    setRecapLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/articulos?limit=200`, getAuthHeaders());
      const data = await res.json();
      setRecapLista(data.items || []);
    } catch {
      toast.error("No se pudieron cargar los artículos");
    } finally {
      setRecapLoading(false);
    }
  };

  const recapImagenDe = (a) => {
    if (a.imagen_url) return a.imagen_url;
    const m = (a.contenido_html || "").match(/<img[^>]+src=["']([^"']+)["']/i);
    return m ? m[1] : null;
  };

  const recapResumenDe = (a) => {
    const txt = (a.contenido_html || "").replace(/<style[\s\S]*?<\/style>/gi, "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
    return txt.length > 30 ? txt.slice(0, 140) + (txt.length > 140 ? "…" : "") : "";
  };

  const generarHtmlRecap = (items, formato) => {
    const NAVY = "#1F3864", GREEN = "#5CBB00";
    const btn = (a) => `<a href="${API_URL}/api/articulos/read-online/${a.id}" target="_blank" style="display:inline-block;background:${GREEN};color:#ffffff;text-decoration:none;font-family:Arial,sans-serif;font-size:13px;font-weight:bold;padding:9px 22px;border-radius:4px;">Leer art&iacute;culo</a>`;
    const imgTag = (a, h) => { const u = recapImagenDe(a); return u ? `<a href="${API_URL}/api/articulos/read-online/${a.id}" target="_blank"><img src="${u}" alt="${(a.titulo || "").replace(/"/g, "")}" width="100%" style="display:block;width:100%;max-height:${h}px;object-fit:cover;border-radius:6px 6px 0 0;" /></a>` : "";
    };
    const tituloDe = (a, size) => `<div style="font-family:Arial,sans-serif;font-size:${size}px;font-weight:bold;color:${NAVY};padding:14px 16px 6px;">${a.asunto || a.titulo || ""}</div>`;
    const resumen = (a) => { const r = recapResumenDe(a); return r ? `<div style="font-family:Arial,sans-serif;font-size:13px;color:#555555;line-height:1.5;padding:0 16px 4px;">${r}</div>` : ""; };
    const bloque = (a, size, imgH) => `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:#ffffff;border:1px solid #e2e8f0;border-radius:6px;"><tr><td>${imgTag(a, imgH)}${tituloDe(a, size)}${resumen(a)}<div style="padding:10px 16px 16px;">${btn(a)}</div></td></tr></table>`;

    let cuerpo = "";
    if (formato === "columna") {
      cuerpo = items.map(a => `<tr><td style="padding:0 0 18px;">${bloque(a, 20, 300)}</td></tr>`).join("");
    } else {
      const [destacado, ...resto] = items;
      cuerpo = `<tr><td style="padding:0 0 18px;">${bloque(destacado, 22, 320)}</td></tr>`;
      for (let i = 0; i < resto.length; i += 2) {
        const a = resto[i], b = resto[i + 1];
        cuerpo += `<tr><td style="padding:0 0 18px;"><table role="presentation" width="100%" cellpadding="0" cellspacing="0"><tr><td width="49%" valign="top" style="width:49%;">${bloque(a, 16, 180)}</td><td width="2%" style="width:2%;">&nbsp;</td><td width="49%" valign="top" style="width:49%;">${b ? bloque(b, 16, 180) : "&nbsp;"}</td></tr></table></td></tr>`;
      }
    }
    return `\n<!-- ARTICULOS DEL MES ${recapMes} (formato ${formato}) -->\n<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="width:100%;background:#f4f6f9;"><tr><td style="padding:16px;"><table role="presentation" width="100%" cellpadding="0" cellspacing="0">${cuerpo}</table></td></tr></table>\n<!-- FIN ARTICULOS DEL MES -->\n`;
  };

  // Elimina cualquier recap "Artículos del Mes" previamente insertado (incluye
  // versiones viejas con títulos/680px) balanceando las tablas anidadas.
  const removerRecapExistente = (contenido) => {
    const MARK = "<!-- ARTICULOS DEL MES";
    let out = contenido;
    let removido = false;
    let i;
    while ((i = out.indexOf(MARK)) !== -1) {
      let start = i;
      const ventana = out.slice(Math.max(0, i - 80), i);
      const wrapIdx = ventana.lastIndexOf('<tr><td style="padding:0;">');
      const conWrapper = wrapIdx !== -1;
      if (conWrapper) start = Math.max(0, i - 80) + wrapIdx;
      const tOpen = out.indexOf("<table", i);
      if (tOpen === -1) break;
      const re = /<table\b|<\/table>/gi;
      re.lastIndex = tOpen;
      let depth = 0, end = -1, m;
      while ((m = re.exec(out)) !== null) {
        if (m[0].toLowerCase() === "</table>") { depth--; if (depth === 0) { end = m.index + 8; break; } }
        else depth++;
      }
      if (end === -1) break;
      const fin = out.slice(end).match(/^\s*<!-- FIN ARTICULOS DEL MES -->/);
      if (fin) end += fin[0].length;
      if (conWrapper) {
        const cierre = out.slice(end).match(/^\s*<\/td><\/tr>/);
        if (cierre) end += cierre[0].length;
      }
      out = out.slice(0, start) + out.slice(end);
      removido = true;
    }
    return { contenido: out, removido };
  };

  const insertarRecap = () => {
    const items = recapSeleccion.map(id => recapLista.find(a => a.id === id)).filter(Boolean);
    if (items.length === 0) {
      toast.error("Selecciona al menos un artículo");
      return;
    }
    const html = generarHtmlRecap(items, recapFormato);
    setArticuloForm(prev => {
      const { contenido, removido } = removerRecapExistente(prev.contenido_html || "");
      let idx = -1;
      let wrapEnFila = false;
      let donde = "al final del contenido";
      // 1) Posición del cursor en el textarea (solo si no hubo reemplazo, porque
      //    al remover el recap viejo los índices del textarea ya no coinciden)
      const ta = contenidoHtmlRef.current;
      if (!removido && ta && typeof ta.selectionStart === "number" && ta.selectionStart > 0 && ta.selectionStart < contenido.length) {
        idx = ta.selectionStart;
        donde = "en la posición del cursor";
      }
      // 2) Detectar el pie de página: primero la IMAGEN de footer configurada
      //    (editor visual, alt="Footer"), luego el bloque de cancelar suscripción.
      if (idx === -1) {
        let anchor = contenido.search(/<img[^>]*alt=["']Footer["']/i);
        if (anchor === -1) anchor = contenido.search(/cancelar\s+suscripci|unsubscribe|darse\s+de\s+baja/i);
        if (anchor !== -1) {
          // Si el ancla vive dentro de una fila de tabla (estructura del editor
          // visual), insertar el recap como fila propia ANTES de esa fila.
          const trIdx = contenido.lastIndexOf("<tr", anchor);
          if (trIdx !== -1 && !contenido.slice(trIdx, anchor).includes("</tr>")) {
            idx = trIdx;
            wrapEnFila = true;
            donde = "entre el contenido y la imagen de pie de página";
          } else {
            const antes = contenido.slice(0, anchor);
            const candidatos = [antes.lastIndexOf("<table"), antes.lastIndexOf("<div"), antes.lastIndexOf("<p")];
            const inicioFooter = Math.max(...candidatos);
            if (inicioFooter > 0) {
              idx = inicioFooter;
              donde = "entre el contenido y el footer";
            }
          }
        }
      }
      const bloque = wrapEnFila ? `<tr><td style="padding:0;">${html}</td></tr>` : html;
      const nuevo = idx === -1
        ? contenido + bloque
        : contenido.slice(0, idx) + bloque + contenido.slice(idx);
      toast.success(`${items.length} artículo${items.length > 1 ? "s" : ""} insertado${items.length > 1 ? "s" : ""} ${donde}${removido ? " (recap anterior reemplazado)" : ""} (formato ${recapFormato === "profesional" ? "Profesional" : "Columna"})`);
      return { ...prev, contenido_html: nuevo };
    });
    setShowRecapModal(false);
  };

  // Recalcular y persistir los contadores almacenados de TODOS los artículos
  // (deja consistentes el listado y los reportes globales con el modal).
  const [recalculandoStats, setRecalculandoStats] = useState(false);
  const handleRecalcularEstadisticas = useCallback(async () => {
    if (recalculandoStats) return;
    setRecalculandoStats(true);
    try {
      const res = await fetch(`${API_URL}/api/articulos/recalcular-estadisticas`, {
        method: "POST",
        ...getAuthHeaders(),
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Error al recalcular");
      toast.success(
        `Estadísticas recalculadas: ${data.articulos_procesados || 0} artículo(s) procesado(s).`,
        { duration: 6000 }
      );
      await fetchArticulos();
    } catch (error) {
      toast.error(error.message || "No se pudieron recalcular las estadísticas");
    } finally {
      setRecalculandoStats(false);
    }
  }, [recalculandoStats, getAuthHeaders, fetchArticulos]);

  // Reconstruye el detalle por destinatario (articulos_envios_detalle) de los
  // envíos que lo hayan perdido, usando la lista de destinatarios guardada y
  // los clics registrados. No destructivo.
  const [reconstruyendoDetalle, setReconstruyendoDetalle] = useState(false);
  const handleReconstruirDetalle = useCallback(async () => {
    if (reconstruyendoDetalle) return;
    const ok = window.confirm(
      "Reconstruir detalle de envíos\n\n" +
      "Se recreará el detalle por destinatario de los envíos que lo hayan perdido, " +
      "usando la lista de destinatarios y los clics que aún existen.\n\n" +
      "• Recupera: a quién se envió + clics (navegador, dispositivo, hora).\n" +
      "• NO recupera: aperturas sin clic ni qué correo puntual falló.\n" +
      "Es una operación segura (no borra nada). ¿Continuar?"
    );
    if (!ok) return;
    setReconstruyendoDetalle(true);
    try {
      const res = await fetch(`${API_URL}/api/articulos/reconstruir-detalle`, {
        method: "POST",
        ...getAuthHeaders(),
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify({ todos_los_faltantes: true }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Error al reconstruir el detalle");
      toast.success(
        `Detalle reconstruido: ${data.total_filas_reconstruidas || 0} fila(s) en ${data.envios_procesados || 0} envío(s).`,
        { duration: 8000 }
      );
      await fetchArticulos();
    } catch (error) {
      toast.error(error.message || "No se pudo reconstruir el detalle");
    } finally {
      setReconstruyendoDetalle(false);
    }
  }, [reconstruyendoDetalle, getAuthHeaders, fetchArticulos]);


  const fetchListas = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/articulos/listas/all`, getAuthHeaders());
      const data = await res.json();
      setListas(data.listas || []);
    } catch (error) {
      console.error("Error fetching listas:", error);
    }
  }, [getAuthHeaders]);

  const fetchContactosLista = useCallback(async (listaId) => {
    try {
      const res = await fetch(`${API_URL}/api/articulos/listas/${listaId}/contactos`, getAuthHeaders());
      const data = await res.json();
      setContactosLista(data.contactos || []);
    } catch (error) {
      console.error("Error fetching contactos:", error);
    }
  }, [getAuthHeaders]);

  const fetchEnvios = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/articulos/envios?limit=50`, getAuthHeaders());
      const data = await res.json();
      setEnvios(data.items || []);
    } catch (error) {
      console.error("Error fetching envios:", error);
    }
  }, [getAuthHeaders]);

  const fetchProgramados = useCallback(async () => {
    try {
      const [progRes, pausRes] = await Promise.all([
        fetch(`${API_URL}/api/articulos/envios?limit=100&estado=programado`, getAuthHeaders()),
        fetch(`${API_URL}/api/articulos/envios?limit=100&estado=pausado_programado`, getAuthHeaders()),
      ]);
      const progData = await progRes.json();
      const pausData = await pausRes.json();
      setProgramados([...(progData.items || []), ...(pausData.items || [])]);
    } catch (error) {
      console.error("Error fetching programados:", error);
    }
  }, [getAuthHeaders]);

  const fetchSchedulerHealth = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/articulos/scheduler/health`, getAuthHeaders());
      if (!res.ok) return;
      const data = await res.json();
      setSchedulerHealth(data);
    } catch (error) {
      console.error("Error fetching scheduler health:", error);
    }
  }, [getAuthHeaders]);

  const fetchDesuscritos = useCallback(async () => {
    setLoadingDesuscritos(true);
    try {
      const res = await fetch(`${API_URL}/api/articulos/unsubscribes`, getAuthHeaders());
      const data = await res.json();
      setDesuscritos(data.items || []);
    } catch (error) {
      console.error("Error fetching desuscritos:", error);
    } finally {
      setLoadingDesuscritos(false);
    }
  }, [getAuthHeaders]);

  const handleDesbloquearDesuscrito = async (email) => {
    if (!window.confirm(`¿Desbloquear a "${email}"? Volverá a recibir correos de marketing.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/articulos/unsubscribes/${encodeURIComponent(email)}`, {
        method: "DELETE",
        ...getAuthHeaders(),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || "Email desbloqueado");
        fetchDesuscritos();
      } else {
        toast.error(data.detail || "Error al desbloquear");
      }
    } catch (e) {
      toast.error("Error al desbloquear el email");
    }
  };

  // ============ BLOCKLIST (No deseados) ============
  const fetchBlocklist = useCallback(async () => {
    setLoadingBlocklist(true);
    try {
      const res = await fetch(`${API_URL}/api/articulos/blocklist`, getAuthHeaders());
      const data = await res.json();
      setBlocklist(data.items || []);
    } catch (e) {
      console.error("Error fetching blocklist:", e);
    } finally {
      setLoadingBlocklist(false);
    }
  }, [getAuthHeaders]);

  const handleAgregarBlocklist = async () => {
    const value = (newBlocklistValue || "").trim();
    if (!value) {
      toast.error("Ingrese un email o dominio");
      return;
    }
    setAddingBlocklist(true);
    try {
      const res = await fetch(`${API_URL}/api/articulos/blocklist`, {
        method: "POST",
        ...getAuthHeaders(),
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify({ value, reason: newBlocklistReason.trim() }),
      });
      const data = await res.json();
      if (res.ok) {
        if (data.created) {
          toast.success(`${data.item.type === "email" ? "Email" : "Dominio"} agregado: ${data.item.value}`);
        } else {
          toast.info(`Ya estaba en la lista: ${data.item.value}`);
        }
        setNewBlocklistValue("");
        setNewBlocklistReason("");
        fetchBlocklist();
      } else {
        toast.error(data.detail || "Error al agregar");
      }
    } catch (e) {
      toast.error("Error al agregar a la lista");
    } finally {
      setAddingBlocklist(false);
    }
  };

  const handleEliminarBlocklist = async (entry) => {
    if (!window.confirm(`¿Eliminar "${entry.value}" de la lista de no deseados? Volverá a poder recibir envíos.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/articulos/blocklist/${entry.id}`, {
        method: "DELETE",
        ...getAuthHeaders(),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Eliminado: ${entry.value}`);
        fetchBlocklist();
      } else {
        toast.error(data.detail || "Error al eliminar");
      }
    } catch (e) {
      toast.error("Error al eliminar de la lista");
    }
  };

  const handleEnviarAhora = async (envio) => {
    if (!window.confirm(`¿Disparar ahora el envío de "${envio.articulo_titulo}" a ${envio.total_destinatarios} destinatarios?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/articulos/envios/${envio.id}/enviar-ahora`, {
        method: "POST",
        ...getAuthHeaders(),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || "Envío iniciado");
        fetchProgramados();
        fetchEnvios();
      } else {
        toast.error(data.detail || "Error al disparar el envío");
      }
    } catch (e) {
      toast.error("Error al disparar el envío");
    }
  };

  const handleCancelarProgramado = async (envio) => {
    if (!window.confirm(`¿Cancelar el envío programado de "${envio.articulo_titulo}"?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/articulos/envios/${envio.id}`, {
        method: "DELETE",
        ...getAuthHeaders(),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || "Envío cancelado");
        fetchProgramados();
        fetchEnvios();
      } else {
        toast.error(data.detail || "Error al cancelar");
      }
    } catch (e) {
      toast.error("Error al cancelar el envío");
    }
  };

  const handleEliminarEnvio = async (envio, contexto) => {
    const label = envio.articulo_titulo || "este envío";
    const msg = contexto === "programado"
      ? `¿Eliminar permanentemente el envío programado de "${label}"? Esta acción no se puede deshacer.`
      : `¿Eliminar permanentemente este envío del historial de "${label}"? Se borrarán también sus estadísticas. Esta acción no se puede deshacer.`;
    if (!window.confirm(msg)) return;
    try {
      const res = await fetch(`${API_URL}/api/articulos/envios/${envio.id}/eliminar`, {
        method: "DELETE",
        ...getAuthHeaders(),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || "Envío eliminado");
        fetchProgramados();
        fetchEnvios();
        fetchEstadisticasGenerales();
      } else {
        toast.error(data.detail || "Error al eliminar");
      }
    } catch (e) {
      toast.error("Error al eliminar el envío");
    }
  };


  const fetchEstadisticasGenerales = useCallback(async (articuloId = null) => {
    try {
      const base = `${API_URL}/api/articulos/estadisticas-generales`;
      const aid = articuloId ? `&articulo_id=${encodeURIComponent(articuloId)}` : "";
      const [resTodos, resExt, resInt] = await Promise.all([
        fetch(`${base}${articuloId ? `?articulo_id=${encodeURIComponent(articuloId)}` : ""}`, getAuthHeaders()),
        fetch(`${base}?audiencia=externo${aid}`, getAuthHeaders()),
        fetch(`${base}?audiencia=interno${aid}`, getAuthHeaders()),
      ]);
      setEstadisticasGenerales(await resTodos.json());
      setStatsExterno(await resExt.json());
      setStatsInterno(await resInt.json());
    } catch (error) {
      console.error("Error fetching estadisticas:", error);
    }
  }, [getAuthHeaders]);

  // Toggle the per-article filter for the top KPI cards. Re-fetches the stats
  // scoped to the selected article, or clears the filter when toggled off.
  // Al seleccionar un artículo, auto-cambia la vista de tarjetas a su audiencia.
  const handleToggleFiltroArticulo = useCallback((articulo) => {
    const isOff = filtroArticuloId === articulo.id;
    const next = isOff ? null : articulo.id;
    setFiltroArticuloId(next);
    fetchEstadisticasGenerales(next);
    if (!isOff) {
      const auds = articulo.audiencias || [];
      if (auds.length === 1 && auds[0] === "interno") setAudienciaView("interno");
      else if (auds.length === 1 && auds[0] === "externo") setAudienciaView("externo");
      else if (auds.length > 1) setAudienciaView("todos");
    }
  }, [filtroArticuloId, fetchEstadisticasGenerales]);

  // Render de una fila de 4 tarjetas KPI para una audiencia dada.
  const renderKpiRow = (stats, etiqueta, badgeClass) => {
    const r = stats?.resumen || {};
    const cards = [
      { label: "Artículos", val: r.total_articulos || 0, icon: <FileText className="w-5 h-5 text-blue-600" />, bg: "bg-blue-100 dark:bg-blue-900" },
      { label: "Total Enviados", val: r.total_enviados || 0, icon: <Send className="w-5 h-5 text-green-600" />, bg: "bg-green-100 dark:bg-green-900" },
      { label: "Tasa Apertura", val: `${r.tasa_apertura_global || 0}%`, icon: <Eye className="w-5 h-5 text-purple-600" />, bg: "bg-purple-100 dark:bg-purple-900" },
      { label: "Tasa Clicks", val: `${r.tasa_clicks_global || 0}%`, icon: <MousePointer className="w-5 h-5 text-amber-600" />, bg: "bg-amber-100 dark:bg-amber-900" },
    ];
    return (
      <div className="space-y-2" data-testid={`kpi-row-${etiqueta.toLowerCase()}`}>
        <div className="flex items-center gap-2">
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${badgeClass}`}>{etiqueta}</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {cards.map((c, i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 ${c.bg} rounded-lg`}>{c.icon}</div>
                  <div>
                    <p className="text-sm text-slate-500">{c.label}</p>
                    <p className="text-2xl font-bold">{c.val}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  };

  // Búsqueda global de contactos en TODAS las listas.
  const buscarContactosGlobal = useCallback(async () => {
    const q = searchContactoGlobal.trim();
    if (!q) { setResultadosGlobal(null); return; }
    setBuscandoGlobal(true);
    try {
      const res = await fetch(`${API_URL}/api/articulos/contactos/buscar?q=${encodeURIComponent(q)}`, getAuthHeaders());
      const data = await res.json();
      setResultadosGlobal(data.contactos || []);
    } catch (e) {
      setResultadosGlobal([]);
    } finally {
      setBuscandoGlobal(false);
    }
  }, [searchContactoGlobal, getAuthHeaders]);

  // Exportar a Excel los correos desuscritos (con su lista de contactos).
  const exportarDesuscritos = useCallback(async () => {
    setExportandoDesuscritos(true);
    try {
      const res = await fetch(`${API_URL}/api/articulos/unsubscribes/export`, getAuthHeaders());
      if (!res.ok) throw new Error("export failed");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `desuscritos_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("No se pudo exportar los desuscritos");
    } finally {
      setExportandoDesuscritos(false);
    }
  }, [getAuthHeaders]);

  const fetchUsuariosSistema = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/articulos/usuarios-sistema`, getAuthHeaders());
      const data = await res.json();
      setUsuariosSistema(data.usuarios || []);
    } catch (error) {
      console.error("Error fetching usuarios:", error);
    }
  }, [getAuthHeaders]);

  const fetchPlantillas = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/articulos/plantillas`, getAuthHeaders());
      const data = await res.json();
      setPlantillas(data.plantillas || []);
    } catch (error) {
      console.error("Error fetching plantillas:", error);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchArticulos(),
        fetchListas(),
        fetchEnvios(),
        fetchProgramados(),
        fetchEstadisticasGenerales(),
        fetchUsuariosSistema(),
        fetchPlantillas(),
        fetchDesuscritos(),
        fetchBlocklist(),
        fetchSchedulerHealth()
      ]);
      setLoading(false);
    };
    loadData();
    // Solo al montar: refetches puntuales (p. ej. búsqueda) se manejan aparte
    // para no re-disparar TODAS las llamadas con cada letra escrita.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch SOLO de artículos cuando cambia la búsqueda (ya debounced).
  const _articulosFetchedOnce = useRef(false);
  useEffect(() => {
    if (!_articulosFetchedOnce.current) {
      _articulosFetchedOnce.current = true;
      return;
    }
    fetchArticulos();
  }, [fetchArticulos]);

  // Poll scheduler health every 30s while on the "programados" tab so the UI
  // reflects each tick without needing a manual refresh.
  useEffect(() => {
    if (activeTab !== "programados") return;
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await fetch(`${API_URL}/api/articulos/scheduler/health`, getAuthHeaders());
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (!cancelled) setSchedulerHealth(data);
      } catch (e) {
        // ignore transient errors; next tick will retry
      }
    };
    tick();
    const id = setInterval(tick, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, [activeTab, getAuthHeaders]);

  useEffect(() => {
    if (!selectedLista) return;
    let cancelled = false;
    const listaId = selectedLista.id;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/articulos/listas/${listaId}/contactos`, getAuthHeaders());
        const data = await res.json();
        if (!cancelled) setContactosLista(data.contactos || []);
      } catch (e) {
        // ignore
      }
    })();
    return () => { cancelled = true; };
  }, [selectedLista, getAuthHeaders]);

  // CRUD handlers
  const handleSaveArticulo = async () => {
    if (!articuloForm.titulo || !articuloForm.asunto) {
      toast.error("Título y asunto son requeridos");
      return;
    }

    try {
      const url = editingArticulo 
        ? `${API_URL}/api/articulos/${editingArticulo.id}`
        : `${API_URL}/api/articulos`;
      
      const res = await fetch(url, {
        method: editingArticulo ? "PUT" : "POST",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify(articuloForm)
      });

      if (res.ok) {
        toast.success(editingArticulo ? "Artículo actualizado" : "Artículo creado");
        setShowArticuloDialog(false);
        setEditingArticulo(null);
        setArticuloForm({ titulo: "", asunto: "", contenido_html: "", contenido_tipo: "editor", imagen_url: "", categoria: "", editor_config: null });
        fetchArticulos();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Error al guardar");
      }
    } catch (error) {
      toast.error("Error al guardar artículo");
    }
  };

  const handleDeleteArticulo = async (articulo) => {
    if (!window.confirm(`¿Eliminar el artículo "${articulo.titulo}"?`)) return;

    try {
      const res = await fetch(`${API_URL}/api/articulos/${articulo.id}`, {
        method: "DELETE",
        ...getAuthHeaders()
      });

      if (res.ok) {
        toast.success("Artículo eliminado");
        fetchArticulos();
      }
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const handleClonarArticulo = async (articulo) => {
    if (!window.confirm(`¿Crear una copia del artículo "${articulo.titulo}"?\n\nLa copia tendrá el mismo contenido pero con estadísticas reseteadas a 0.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/articulos/${articulo.id}/clonar`, {
        method: "POST",
        ...getAuthHeaders(),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Clonado como "${data.titulo}". Abriendo editor…`);
        await fetchArticulos();
        // Llevar al usuario directo a editar el nuevo artículo clonado.
        if (data.articulo) {
          openEditArticulo(data.articulo);
        }
      } else {
        toast.error(data.detail || "Error al clonar");
      }
    } catch (e) {
      toast.error("Error al clonar el artículo");
    }
  };

  const handleResetEstadisticas = async (articulo) => {
    if (!window.confirm(
      `¿BORRAR todas las estadísticas del artículo "${articulo.titulo}"?\n\n` +
      `Se eliminarán:\n- Todos los envíos históricos\n- Aperturas, clicks y cancelaciones registrados\n- Contadores de tracking\n\n` +
      `Esta acción NO se puede deshacer.`
    )) return;
    try {
      const res = await fetch(`${API_URL}/api/articulos/${articulo.id}/estadisticas`, {
        method: "DELETE",
        ...getAuthHeaders(),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(
          `Estadísticas borradas · envíos: ${data.envios_borrados}, detalles: ${data.detalles_borrados}, clicks: ${data.clicks_borrados}`,
          { duration: 8000 }
        );
        fetchArticulos();
        setShowEstadisticasDialog(false);
      } else {
        toast.error(data.detail || "Error al borrar estadísticas");
      }
    } catch (e) {
      toast.error("Error al borrar estadísticas");
    }
  };

  const handleUploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await fetch(`${API_URL}/api/articulos/upload-image`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (res.ok) {
        const data = await res.json();
        setArticuloForm(prev => ({ ...prev, imagen_url: data.url }));
        toast.success("Imagen cargada");
        return data.url;
      }
    } catch (error) {
      toast.error("Error al cargar imagen");
    }
    return null;
  };

  // Handle upload image for plantilla gallery
  const handleUploadImageForGallery = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return null;

    const formData = new FormData();
    formData.append("file", file);

    try {
      toast.loading("Subiendo imagen...", { id: "upload-img" });
      const res = await fetch(`${API_URL}/api/articulos/upload-image`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (res.ok) {
        const data = await res.json();
        toast.success("Imagen cargada correctamente", { id: "upload-img" });
        return data.url;
      } else {
        const errorData = await res.json().catch(() => ({}));
        toast.error(errorData.detail || "Error al cargar imagen", { id: "upload-img" });
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error("Error al cargar imagen", { id: "upload-img" });
    }
    return null;
  };

  // Handle template selection from gallery
  const handleSelectPlantilla = (templateData) => {
    setArticuloForm(prev => ({
      ...prev,
      contenido_tipo: "plantilla",
      plantilla_id: templateData.plantilla_id,
      contenido_html: templateData.contenido_html,
      categoria: prev.categoria || templateData.plantilla_nombre?.split(" ")[0] || ""
    }));
    setShowPlantillasGallery(false);
    toast.success(`Plantilla "${templateData.plantilla_nombre}" aplicada`);
  };

  const handleSaveLista = async () => {
    if (!listaForm.nombre) {
      toast.error("El nombre es requerido");
      return;
    }

    try {
      const url = editingLista 
        ? `${API_URL}/api/articulos/listas/${editingLista.id}`
        : `${API_URL}/api/articulos/listas`;
      
      const res = await fetch(url, {
        method: editingLista ? "PUT" : "POST",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify(listaForm)
      });

      if (res.ok) {
        toast.success(editingLista ? "Lista actualizada" : "Lista creada");
        setShowListaDialog(false);
        setListaForm({ nombre: "", descripcion: "" });
        setEditingLista(null);
        fetchListas();
      }
    } catch (error) {
      toast.error("Error al guardar lista");
    }
  };

  const openEditLista = (lista) => {
    setEditingLista(lista);
    setListaForm({ nombre: lista.nombre, descripcion: lista.descripcion || "" });
    setShowListaDialog(true);
  };

  const handleDeleteLista = async (lista) => {
    if (!window.confirm(`¿Eliminar la lista "${lista.nombre}"?`)) return;

    try {
      const res = await fetch(`${API_URL}/api/articulos/listas/${lista.id}`, {
        method: "DELETE",
        ...getAuthHeaders()
      });

      if (res.ok) {
        toast.success("Lista eliminada");
        setSelectedLista(null);
        fetchListas();
      }
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const resetImportState = () => {
    setImportFileObj(null);
    setImportSheets([]);
    setSelectedSheet("");
    setImportSheetsLoading(false);
    setImporting(false);
    setImportPreview(null);
    setImportPreviewLoading(false);
  };

  const fetchImportPreview = async (file, sheetName) => {
    if (!file) return;
    setImportPreviewLoading(true);
    setImportPreview(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      let url = `${API_URL}/api/articulos/excel/preview`;
      if (sheetName) url += `?sheet_name=${encodeURIComponent(sheetName)}`;
      const res = await fetch(url, { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd });
      const data = await res.json();
      if (res.ok) {
        setImportPreview(data);
        const m = data.mapping || {};
        setManualMapping({
          email: m.email || "",
          nombre: m.nombre || "__none__",
          empresa: m.empresa || "__none__",
          telefono: m.telefono || "__none__",
          clase_cliente: m.clase_cliente || "__none__",
        });
      } else {
        toast.error(data.detail || "No se pudo previsualizar el archivo");
      }
    } catch (err) {
      toast.error("Error al previsualizar el archivo");
    } finally {
      setImportPreviewLoading(false);
    }
  };

  const handleImportContactos = async (e) => {
    const file = e.target.files?.[0];
    // allow re-selecting the same file later
    if (e.target) e.target.value = "";
    if (!file || !selectedLista) return;

    const isCsv = file.name.toLowerCase().endsWith(".csv");
    setImportFileObj(file);
    setImportSheets([]);
    setSelectedSheet("");

    if (isCsv) {
      fetchImportPreview(file, null);
      return;
    }

    // Excel: detect sheets (libros/hojas) first
    setImportSheetsLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API_URL}/api/articulos/excel/sheets`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd
      });
      const data = await res.json();
      if (res.ok && data.is_excel && Array.isArray(data.sheets) && data.sheets.length > 1) {
        // Multiple sheets → let the user choose; preview the first one
        setImportSheets(data.sheets);
        setSelectedSheet(data.sheets[0].name);
        fetchImportPreview(file, data.sheets[0].name);
      } else {
        // Single sheet → preview the default sheet directly
        const only = data.sheets && data.sheets[0] ? data.sheets[0].name : null;
        fetchImportPreview(file, only);
      }
    } catch (err) {
      // Fallback: preview the first sheet
      fetchImportPreview(file, null);
    } finally {
      setImportSheetsLoading(false);
    }
  };

  const handleSheetChange = (value) => {
    setSelectedSheet(value);
    fetchImportPreview(importFileObj, value);
  };

  const doImport = async (file, sheetName) => {
    if (!file || !selectedLista) return;
    setImporting(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      let url = `${API_URL}/api/articulos/listas/${selectedLista.id}/import?update_existing=${importUpdateExisting ? "true" : "false"}`;
      if (sheetName) url += `&sheet_name=${encodeURIComponent(sheetName)}`;
      // Manual column mapping overrides (from the preview re-mapping UI)
      if (manualMapping.email) url += `&email_col=${encodeURIComponent(manualMapping.email)}`;
      const _ov = { nombre_col: manualMapping.nombre, empresa_col: manualMapping.empresa, telefono_col: manualMapping.telefono, clase_col: manualMapping.clase_cliente };
      Object.entries(_ov).forEach(([p, v]) => { if (v && v !== "__none__") url += `&${p}=${encodeURIComponent(v)}`; });
      const res = await fetch(url, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      const data = await res.json();
      if (res.ok) {
        const parts = [`Nuevos: ${data.imported}`];
        if (data.updated) parts.push(`Actualizados: ${data.updated}`);
        parts.push(`Duplicados: ${data.duplicates}`);
        if (data.errors) parts.push(`Errores: ${data.errors}`);
        toast.success((sheetName ? `Hoja "${sheetName}" — ` : "") + parts.join(" · "));
        fetchContactosLista(selectedLista.id);
        fetchListas();
        setShowImportDialog(false);
        resetImportState();
      } else {
        toast.error(data.detail || "Error en importación");
      }
    } catch (error) {
      toast.error("Error al importar");
    } finally {
      setImporting(false);
    }
  };

  const handleAddContacto = async () => {
    if (!contactoForm.email || !selectedLista) {
      toast.error("Email es requerido");
      return;
    }

    try {
      const url = editingContacto
        ? `${API_URL}/api/articulos/listas/${selectedLista.id}/contactos/${editingContacto.id}`
        : `${API_URL}/api/articulos/listas/${selectedLista.id}/contactos`;
      
      const res = await fetch(url, {
        method: editingContacto ? "PUT" : "POST",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify(contactoForm)
      });

      if (res.ok) {
        toast.success(editingContacto ? "Contacto actualizado" : "Contacto agregado");
        setShowContactoDialog(false);
        setContactoForm({ email: "", nombre: "", empresa: "", telefono: "", clase_cliente: "" });
        setEditingContacto(null);
        fetchContactosLista(selectedLista.id);
        fetchListas();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Error al guardar");
      }
    } catch (error) {
      toast.error("Error al guardar contacto");
    }
  };

  const openEditContacto = (contacto) => {
    setEditingContacto(contacto);
    setContactoForm({
      email: contacto.email,
      nombre: contacto.nombre || "",
      empresa: contacto.empresa || "",
      telefono: contacto.telefono || "",
      clase_cliente: contacto.clase_cliente || ""
    });
    setShowContactoDialog(true);
  };

  const handleDeleteContacto = async (contacto) => {
    if (!selectedLista) return;

    try {
      const res = await fetch(`${API_URL}/api/articulos/listas/${selectedLista.id}/contactos/${contacto.id}`, {
        method: "DELETE",
        ...getAuthHeaders()
      });

      if (res.ok) {
        toast.success("Contacto eliminado");
        fetchContactosLista(selectedLista.id);
        fetchListas();
      }
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const handleTestEmail = async () => {
    const emailDestino = prompt("Ingrese el correo donde desea recibir la prueba:");
    if (!emailDestino || !emailDestino.includes("@")) {
      toast.error("Correo inválido");
      return;
    }

    setTestingEmail(true);
    try {
      const res = await fetch(`${API_URL}/api/articulos/test-email`, {
        method: "POST",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify({
          email_destino: emailDestino,
          nombre_remitente: envioForm.nombre_remitente,
          asunto: "Prueba de Nombre del Remitente"
        })
      });

      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(`Correo de prueba enviado a ${emailDestino}. Revisa tu bandeja de entrada para verificar el nombre del remitente.`);
        console.log("Debug info:", data.debug_info);
      } else {
        toast.error(data.detail || data.error || "Error al enviar correo de prueba");
      }
    } catch (error) {
      toast.error("Error al enviar correo de prueba");
    } finally {
      setTestingEmail(false);
    }
  };

  const handleEnviarArticulo = async () => {
    if (!envioForm.articulo_id) {
      toast.error("Seleccione un artículo");
      return;
    }

    if (envioForm.lista_ids.length === 0 && !envioForm.contactos_adicionales && !envioForm.enviar_usuarios_sistema) {
      toast.error("Seleccione al menos un destinatario");
      return;
    }

    if (envioForm.enviar_usuarios_sistema && envioForm.usuarios_ids.length === 0) {
      toast.error("Seleccione al menos un usuario del sistema o desmarque esa opción");
      return;
    }

    // Convert the local datetime to UTC ISO so the backend can compare reliably.
    let fechaProgramadaIso = envioForm.fecha_programada;
    if (envioForm.programado && envioForm.fecha_programada) {
      try {
        fechaProgramadaIso = new Date(envioForm.fecha_programada).toISOString();
      } catch (_) {
        // keep original on parse error — backend will reject invalid values
      }
    }
    const payload = {
      ...envioForm,
      fecha_programada: fechaProgramadaIso,
      contactos_adicionales: envioForm.contactos_adicionales
        ? envioForm.contactos_adicionales.split(/[,;\n]/).map(e => e.trim()).filter(e => e)
        : []
    };

    // Programado → enviar directo (sin previsualización).
    if (envioForm.programado) {
      performEnviarArticulo(payload);
      return;
    }

    // Inmediato → mostrar diálogo de confirmación con el desglose de destinatarios.
    setEnvioPreview(null);
    setEnvioPreviewLoading(true);
    setShowEnvioPreview(true);
    try {
      const res = await fetch(`${API_URL}/api/articulos/enviar/preview`, {
        method: "POST",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (res.ok) {
        setEnvioPreview({ ...data, _payload: payload });
      } else {
        toast.error(data.detail || "No se pudo calcular la previsualización");
        setShowEnvioPreview(false);
      }
    } catch (e) {
      toast.error("Error al calcular la previsualización del envío");
      setShowEnvioPreview(false);
    } finally {
      setEnvioPreviewLoading(false);
    }
  };

  const performEnviarArticulo = async (payload) => {
    if (enviando) return; // anti doble-clic: ignora un segundo disparo mientras hay uno en curso
    setEnviando(true);
    try {
      const res = await fetch(`${API_URL}/api/articulos/enviar`, {
        method: "POST",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      const data = await res.json();
      if (res.ok) {
        // Show success message including the sender name that will be used
        const senderInfo = data.nombre_remitente_usado ? ` (Remitente: ${data.nombre_remitente_usado})` : '';
        if (payload.programado) {
          toast.success(`Envío programado: ${data.total_destinatarios} destinatarios${senderInfo}`);
        } else if (typeof data.enviados === 'number') {
          // Synchronous send — show the real result
          const bloqueados = (data.bloqueados_blocklist || 0) + (data.bloqueados_unsubscribed || 0);
          if (data.estado === 'error') {
            toast.error(`El envío falló: ${data.error || 'error desconocido'}`, { duration: 10000 });
          } else if (data.enviados === 0 && bloqueados > 0) {
            toast.warning(`No se envió a ningún destinatario: ${bloqueados} están bloqueados (lista "No Deseados" / "Desuscritos"). Revisa esas pestañas y elimina el dominio/correo si corresponde.`, { duration: 14000 });
          } else if (data.fallidos > 0 || bloqueados > 0) {
            const partes = [];
            if (data.fallidos > 0) partes.push(`${data.fallidos} fallidos`);
            if (bloqueados > 0) partes.push(`${bloqueados} bloqueados`);
            toast.warning(`Enviados: ${data.enviados} de ${data.total_destinatarios} (${partes.join(', ')})${senderInfo}`, { duration: 9000 });
          } else {
            toast.success(`Enviados: ${data.enviados} de ${data.total_destinatarios}${senderInfo}`);
          }
        } else {
          toast.success(`Envío en cola: ${data.total_destinatarios} destinatarios${senderInfo}`);
        }
        setShowEnvioPreview(false);
        setShowEnvioDialog(false);
        setEnvioForm({
          articulo_id: "",
          lista_ids: [],
          contactos_adicionales: "",
          programado: false,
          fecha_programada: "",
          enviar_usuarios_sistema: false,
          usuarios_ids: [],
          nombre_remitente: "Qasesores Corredores y Asesores de Seguros SRL"
        });
        fetchEnvios();
        fetchProgramados();
        fetchEstadisticasGenerales();
      } else {
        toast.error(data.detail || "Error al enviar");
      }
    } catch (error) {
      toast.error("Error al enviar artículo");
    } finally {
      setEnviando(false);
    }
  };

  const handleViewEstadisticas = async (articulo) => {
    try {
      setCurrentArticuloId(articulo.id);
      setLoadingArticuloDomainStats(true);
      setLoadingEngagement(true);
      setEngagementStats(null);
      
      // Load main statistics
      const res = await fetch(`${API_URL}/api/articulos/estadisticas/${articulo.id}`, getAuthHeaders());
      const data = await res.json();
      setEstadisticasArticulo(data);
      setShowEstadisticasDialog(true);
      
      // Also load domain statistics
      const domainRes = await fetch(`${API_URL}/api/articulos/estadisticas/${articulo.id}/dominio`, getAuthHeaders());
      const domainData = await domainRes.json();
      setArticuloDomainStats(domainData);

      // Load engagement stats (browser, device, geo)
      try {
        const engRes = await fetch(`${API_URL}/api/articulos/estadisticas/${articulo.id}/engagement`, getAuthHeaders());
        const engData = await engRes.json();
        setEngagementStats(engData);
      } catch { setEngagementStats(null); }

      // Load timeline (interacción a lo largo del tiempo)
      setTimelineData(null);
      setLoadingTimeline(true);
      try {
        const tlRes = await fetch(`${API_URL}/api/articulos/estadisticas/${articulo.id}/timeline?rango=${timelineRango}`, getAuthHeaders());
        const tlData = await tlRes.json();
        setTimelineData(tlData);
      } catch { setTimelineData(null); }
      finally { setLoadingTimeline(false); }
    } catch (error) {
      toast.error("Error al cargar estadísticas");
    } finally {
      setLoadingArticuloDomainStats(false);
      setLoadingEngagement(false);
    }
  };

  const handleViewDetalleCorreosArticulo = async (categoria, dominio = null) => {
    setLoadingDetalleCorreos(true);
    setShowDetalleCorreosDialog(true);
    try {
      let url = `${API_URL}/api/articulos/estadisticas/${currentArticuloId}/detalle?categoria=${categoria}`;
      if (dominio) {
        url += `&dominio=${encodeURIComponent(dominio)}`;
      }
      const res = await fetch(url, getAuthHeaders());
      const data = await res.json();
      setDetalleCorreos(data);
    } catch (error) {
      toast.error("Error al cargar detalle de correos");
      setShowDetalleCorreosDialog(false);
    } finally {
      setLoadingDetalleCorreos(false);
    }
  };

  const handleViewDominioStats = async (envioId) => {
    setLoadingDominioStats(true);
    setShowDominioStatsDialog(true);
    setCurrentEnvioId(envioId);
    try {
      const res = await fetch(`${API_URL}/api/articulos/estadisticas-dominio/${envioId}`, getAuthHeaders());
      const data = await res.json();
      setDominioStats(data);
    } catch (error) {
      toast.error("Error al cargar estadísticas por dominio");
      setShowDominioStatsDialog(false);
    } finally {
      setLoadingDominioStats(false);
    }
  };

  const handleViewDominioStatsGlobal = async () => {
    setLoadingDominioStats(true);
    setShowDominioStatsDialog(true);
    try {
      const res = await fetch(`${API_URL}/api/articulos/estadisticas-dominio-global`, getAuthHeaders());
      const data = await res.json();
      setDominioStats(data);
      setCurrentEnvioId(null); // Global, no specific envio
    } catch (error) {
      toast.error("Error al cargar estadísticas por dominio");
      setShowDominioStatsDialog(false);
    } finally {
      setLoadingDominioStats(false);
    }
  };

  const handleViewDetalleCorreos = async (categoria, dominio = null, envioId = null) => {
    setLoadingDetalleCorreos(true);
    setShowDetalleCorreosDialog(true);
    try {
      let url;
      if (envioId || currentEnvioId) {
        const id = envioId || currentEnvioId;
        url = `${API_URL}/api/articulos/estadisticas-dominio/${id}/detalle?categoria=${categoria}`;
      } else {
        url = `${API_URL}/api/articulos/estadisticas-dominio-global/detalle?categoria=${categoria}`;
      }
      if (dominio) {
        url += `&dominio=${encodeURIComponent(dominio)}`;
      }
      const res = await fetch(url, getAuthHeaders());
      const data = await res.json();
      setDetalleCorreos(data);
    } catch (error) {
      toast.error("Error al cargar detalle de correos");
      setShowDetalleCorreosDialog(false);
    } finally {
      setLoadingDetalleCorreos(false);
    }
  };

  const getCategoriaLabel = (categoria) => {
    const labels = {
      'todos': 'Todos',
      'enviados': 'Enviados',
      'fallidos': 'Fallidos',
      'rebotados': 'Rebotados',
      'abiertos': 'Abiertos',
      'clicks': 'Con Clicks',
      'no_abiertos': 'No Abiertos',
      'cancelaron': 'Cancelaron Suscripción',
      'bloqueados': 'Bloqueados (ya cancelados)'
    };
    return labels[categoria] || categoria;
  };

  const handleExportarCorreosFiltrados = async () => {
    if (!detalleCorreos) return;
    
    try {
      let url;
      if (currentEnvioId) {
        url = `${API_URL}/api/articulos/estadisticas-dominio/${currentEnvioId}/exportar?categoria=${detalleCorreos.categoria}`;
      } else {
        url = `${API_URL}/api/articulos/estadisticas-dominio-global/exportar?categoria=${detalleCorreos.categoria}`;
      }
      if (detalleCorreos.dominio) {
        url += `&dominio=${encodeURIComponent(detalleCorreos.dominio)}`;
      }
      
      toast.info("Generando archivo Excel...");
      
      const res = await fetch(url, getAuthHeaders());
      if (!res.ok) throw new Error("Error al generar archivo");
      
      const blob = await res.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = `correos_${detalleCorreos.categoria}${detalleCorreos.dominio ? `_${detalleCorreos.dominio}` : ''}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success("Archivo Excel descargado correctamente");
    } catch (error) {
      toast.error("Error al exportar: " + error.message);
    }
  };

  const handleExportReporte = async (articuloId) => {
    try {
      const res = await fetch(`${API_URL}/api/articulos/reportes/export/${articuloId}`, getAuthHeaders());
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `reporte_articulo.xlsx`;
      a.click();
    } catch (error) {
      toast.error("Error al exportar");
    }
  };

  const handleReenviarNoAbiertos = async () => {
    if (!currentArticuloId) return;
    let fechaProgramadaIso = null;
    if (reenvioProgramar) {
      if (!reenvioFecha) {
        toast.error("Selecciona la fecha y hora para programar el reenvío");
        return;
      }
      const fechaSel = new Date(reenvioFecha);
      if (fechaSel.getTime() <= Date.now()) {
        toast.error("La fecha programada debe ser en el futuro");
        return;
      }
      fechaProgramadaIso = fechaSel.toISOString();
    }
    setReenvioLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/articulos/reenviar-no-abiertos/${currentArticuloId}`, {
        method: "POST",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify({ programado: reenvioProgramar, fecha_programada: fechaProgramadaIso })
      });
      const data = await res.json();
      if (res.ok) {
        if (data.programado) {
          toast.success(`Reenvío programado. Al enviarse, sólo irá a quienes no hayan abierto hasta ese momento (ahora: ${data.total_destinatarios_actual}).`);
        } else {
          toast.success(`Reenvío iniciado: ${data.total_destinatarios} destinatarios que no abrieron`);
        }
        setShowReenvioConfirm(false);
        setReenvioProgramar(false);
        setReenvioFecha("");
        fetchEnvios();
        fetchProgramados();
        fetchEstadisticasGenerales();
      } else {
        toast.error(data.detail || "Error al reenviar");
      }
    } catch (error) {
      toast.error("Error al reenviar artículo");
    } finally {
      setReenvioLoading(false);
    }
  };

  // Reanuda un envío incompleto desde donde quedó. NO reinicia: el backend
  // detecta los destinatarios ya procesados y vuelve a disparar el job sólo
  // con los pendientes.
  const [reanudandoEnvioId, setReanudandoEnvioId] = useState(null);
  const [controlEnvioId, setControlEnvioId] = useState(null);
  const [reconciliandoEnvioId, setReconciliandoEnvioId] = useState(null);
  const refrescarEstadisticasModal = async () => {
    if (!currentArticuloId) return;
    fetchEstadisticasGenerales();
    try {
      const sRes = await fetch(
        `${API_URL}/api/articulos/estadisticas/${currentArticuloId}`,
        getAuthHeaders()
      );
      if (sRes.ok) setEstadisticasArticulo(await sRes.json());
    } catch { /* ignore — polling fallback */ }
  };
  const handleReconciliarEnvio = async (envioId) => {
    if (!envioId) return;
    setReconciliandoEnvioId(envioId);
    try {
      // 1) Vista previa SOLO LECTURA: muestra cómo quedarán las tarjetas con los
      //    registros reales, sin escribir nada, para que el usuario confirme.
      let previewMsg = "¿Reparar los contadores con los registros reales? Esto NO reenvía correos ni consume créditos.";
      try {
        const dRes = await fetch(`${API_URL}/api/articulos/envios/${envioId}/diagnostico`, getAuthHeaders());
        if (dRes.ok) {
          const diag = await dRes.json();
          const p = diag.preview_si_reparo || {};
          const a = diag.contadores_doc_actuales || {};
          previewMsg =
            `VISTA PREVIA (no se ha cambiado nada todavía):\n\n` +
            `• Enviados (entregados):  ${a.enviados ?? "?"}  →  ${p.enviados_entregados ?? "?"}\n` +
            `• Rebotados:  ${a.rebotados ?? "?"}  →  ${p.rebotados ?? "?"}\n` +
            `• No enviados (bloqueados+fallidos+rebotados):  ${p.no_enviados_total ?? "?"}\n` +
            `• Total: ${p.suma_total ?? "?"}\n\n` +
            `¿Aplicar estos valores? (no reenvía correos, no consume créditos)`;
        }
      } catch { /* si falla el preview, igual pedimos confirmación básica */ }

      if (!window.confirm(previewMsg)) {
        setReconciliandoEnvioId(null);
        return;
      }

      // 2) Aplicar la reparación.
      const res = await fetch(`${API_URL}/api/articulos/envios/${envioId}/reconciliar`, {
        method: "POST",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" }
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || "Contadores reparados.", { duration: 12000 });
        await refrescarEstadisticasModal();
      } else {
        toast.error(data.detail || "No se pudieron reparar los contadores");
      }
    } catch (e) {
      toast.error("Error al reparar los contadores");
    } finally {
      setReconciliandoEnvioId(null);
    }
  };
  const handleReanudarEnvio = async (envioId) => {
    if (!envioId) return;
    if (!window.confirm("¿Reanudar este envío desde donde quedó? Sólo se enviará a los destinatarios pendientes.")) return;
    setReanudandoEnvioId(envioId);
    try {
      const res = await fetch(`${API_URL}/api/articulos/envios/${envioId}/reanudar`, {
        method: "POST",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" }
      });
      const data = await res.json();
      if (res.ok) {
        if (data.pendientes === 0) {
          // No hay pendientes reales: el backend reconcilió los contadores.
          toast.success(
            data.message ||
            `Todos los destinatarios (${data.total_original}) ya fueron procesados. No hay correos faltantes por enviar.`,
            { duration: 12000 }
          );
        } else {
          toast.success(
            `Reanudado: enviando ${data.pendientes} pendiente(s) de ${data.total_original}. ` +
            `${data.ya_procesados} ya estaban procesados.`
          );
        }
        if (currentArticuloId) {
          fetchEstadisticasGenerales();
          // Refresca el modal de estadísticas inmediatamente — el polling de 5s
          // se encargará de seguir actualizando mientras el envío avanza.
          try {
            const sRes = await fetch(
              `${API_URL}/api/articulos/estadisticas/${currentArticuloId}`,
              getAuthHeaders()
            );
            if (sRes.ok) setEstadisticasArticulo(await sRes.json());
          } catch { /* ignore — polling fallback */ }
        }
      } else {
        toast.error(data.detail || "No se pudo reanudar el envío");
      }
    } catch (e) {
      toast.error("Error al reanudar el envío");
    } finally {
      setReanudandoEnvioId(null);
    }
  };

  const refreshStatsModal = async () => {
    if (!currentArticuloId) return;
    fetchEstadisticasGenerales();
    try {
      const sRes = await fetch(`${API_URL}/api/articulos/estadisticas/${currentArticuloId}`, getAuthHeaders());
      if (sRes.ok) setEstadisticasArticulo(await sRes.json());
    } catch { /* ignore — polling fallback */ }
  };

  const handlePausarEnvio = async (envioId, refresh) => {
    if (!envioId) return;
    setControlEnvioId(envioId);
    try {
      const res = await fetch(`${API_URL}/api/articulos/envios/${envioId}/pausar`, {
        method: "POST",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" }
      });
      const data = await res.json();
      if (res.ok) { toast.success(data.message || "Envío pausado"); if (refresh) await refresh(); }
      else toast.error(data.detail || "No se pudo pausar el envío");
    } catch (e) {
      toast.error("Error al pausar el envío");
    } finally {
      setControlEnvioId(null);
    }
  };

  const handleDetenerEnvio = async (envioId, refresh) => {
    if (!envioId) return;
    if (!window.confirm("¿Detener este envío? No se enviarán más correos y NO se podrá reanudar.")) return;
    setControlEnvioId(envioId);
    try {
      const res = await fetch(`${API_URL}/api/articulos/envios/${envioId}/detener`, {
        method: "POST",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" }
      });
      const data = await res.json();
      if (res.ok) { toast.success(data.message || "Envío detenido"); if (refresh) await refresh(); }
      else toast.error(data.detail || "No se pudo detener el envío");
    } catch (e) {
      toast.error("Error al detener el envío");
    } finally {
      setControlEnvioId(null);
    }
  };

  const handleReanudarProgramado = async (envioId) => {
    if (!envioId) return;
    setControlEnvioId(envioId);
    try {
      const res = await fetch(`${API_URL}/api/articulos/envios/${envioId}/reanudar`, {
        method: "POST",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" }
      });
      const data = await res.json();
      if (res.ok) { toast.success(data.message || "Envío programado reanudado"); fetchProgramados(); fetchEnvios(); }
      else toast.error(data.detail || "No se pudo reanudar");
    } catch (e) {
      toast.error("Error al reanudar el envío programado");
    } finally {
      setControlEnvioId(null);
    }
  };

  const handleChangeTimelineRango = useCallback(async (newRango) => {
    setTimelineRango(newRango);
    if (!currentArticuloId) return;
    setLoadingTimeline(true);
    try {
      const tlRes = await fetch(`${API_URL}/api/articulos/estadisticas/${currentArticuloId}/timeline?rango=${newRango}`, getAuthHeaders());
      const tlData = await tlRes.json();
      setTimelineData(tlData);
    } catch {
      setTimelineData(null);
    } finally {
      setLoadingTimeline(false);
    }
  }, [currentArticuloId, getAuthHeaders]);

  // ====== Reporte Mensual handlers ======
  const openReporteMensualDialog = async () => {
    setShowReporteMensualDialog(true);
    setReporteMensualLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/articulos/reporte-mensual/config`, getAuthHeaders());
      const data = await res.json();
      if (res.ok) {
        setReporteMensualConfig(data);
      } else {
        toast.error(data.detail || "Error al cargar configuración");
      }
    } catch (e) {
      toast.error("Error al cargar configuración");
    } finally {
      setReporteMensualLoading(false);
    }
  };

  // ===== SMTP Marketing handlers =====
  const openSmtpMarketingDialog = async () => {
    setShowSmtpMarketingDialog(true);
    setSmtpMarketingLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/articulos/smtp-config`, getAuthHeaders());
      const data = await res.json();
      if (res.ok) {
        setSmtpMarketingConfig({
          smtp_server: data.smtp_server || "",
          smtp_port: data.smtp_port ?? 587,
          username: data.username || "",
          password: data.password === "********" ? "" : (data.password || ""),
          from_name: data.from_name || "QAsesores Marketing",
          from_email: data.from_email || "",
          use_tls: data.use_tls ?? true,
          use_ssl: data.use_ssl ?? false,
          enabled: data.enabled ?? true,
          throttle_enabled: data.throttle_enabled ?? true,
          batch_size: data.batch_size ?? 50,
          batch_pause_seconds: data.batch_pause_seconds ?? 60,
        });
      } else {
        toast.error(data.detail || "Error al cargar configuración SMTP");
      }
    } catch (e) {
      toast.error("Error al cargar configuración SMTP");
    } finally {
      setSmtpMarketingLoading(false);
    }
  };

  const [reparandoStats, setReparandoStats] = useState(false);
  const [reparProgreso, setReparProgreso] = useState({ done: 0, total: 0 });
  const handleRepararEstadisticas = async () => {
    if (!window.confirm(
      "REPARAR ESTADÍSTICAS (acción única, segura e idempotente):\n\n" +
      "• Elimina envíos 100% fallidos por Playwright (no entregaron nada)\n" +
      "• Elimina registros duplicados por correo (conserva el más completo)\n" +
      "• Recalcula enviados/abiertos/clicks contando correos ÚNICOS\n" +
      "• Recalcula los totales de cada artículo\n" +
      "• Crea el índice único anti-duplicados\n\n" +
      "Se procesa artículo por artículo (puede tardar un poco). ¿Ejecutar ahora?"
    )) return;
    setReparandoStats(true);
    setReparProgreso({ done: 0, total: 0 });
    try {
      // 1) Lista completa de artículos
      const listRes = await fetch(`${API_URL}/api/articulos?limit=1000`, getAuthHeaders());
      const listData = await listRes.json();
      const ids = (listData.items || []).map((a) => a.id).filter(Boolean);
      setReparProgreso({ done: 0, total: ids.length });
      let totalDup = 0, totalPw = 0, fallos = 0;
      // 2) Reparar artículo por artículo (cada request es rápido → evita timeout del proxy)
      for (let i = 0; i < ids.length; i++) {
        try {
          const r = await fetch(`${API_URL}/api/articulos/admin/reparar-estadisticas?articulo_id=${encodeURIComponent(ids[i])}`, {
            method: "POST",
            headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
          });
          if (r.ok) {
            const d = await r.json();
            (d.reporte || []).forEach((a) => (a.envios || []).forEach((e) => {
              totalDup += e.duplicados_eliminados || 0;
              if (e.accion === "eliminado_playwright_total") totalPw += 1;
            }));
          } else { fallos += 1; }
        } catch { fallos += 1; }
        setReparProgreso({ done: i + 1, total: ids.length });
      }
      // 3) Crear índice único al final (ya sin duplicados)
      let indice = "omitido";
      try {
        const ir = await fetch(`${API_URL}/api/articulos/admin/reparar-estadisticas?articulo_id=__crear_indice__&crear_indice_unico=true`, {
          method: "POST",
          headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        });
        if (ir.ok) { const idj = await ir.json(); indice = idj.indice_unico || "omitido"; }
      } catch { /* noop */ }
      toast.success(
        `Reparación completada · ${ids.length} artículos · ${totalDup} duplicados eliminados · ${totalPw} envíos Playwright eliminados · Índice: ${indice}${fallos ? ` · ${fallos} con error` : ""}`,
        { duration: 15000 }
      );
      try { fetchArticulos(); fetchEstadisticasGenerales(); } catch { /* noop */ }
    } catch (e) {
      toast.error("Error al reparar estadísticas");
    } finally {
      setReparandoStats(false);
      setReparProgreso({ done: 0, total: 0 });
    }
  };

  const saveSmtpMarketingConfig = async () => {
    setSmtpMarketingSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/articulos/smtp-config`, {
        method: "POST",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify(smtpMarketingConfig),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || "Configuración guardada");
      } else {
        toast.error(data.detail || "Error al guardar");
      }
    } catch (e) {
      toast.error("Error al guardar configuración SMTP");
    } finally {
      setSmtpMarketingSaving(false);
    }
  };

  const testSmtpMarketing = async () => {
    const email = (smtpTestEmail || "").trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Introduce un email válido para la prueba");
      return;
    }
    setSmtpTesting(true);
    try {
      const res = await fetch(`${API_URL}/api/articulos/smtp-config/test`, {
        method: "POST",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify({ to_email: email }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || "Correo de prueba enviado");
      } else {
        const raw = data?.detail || "Fallo el envío de prueba";
        // Hint especial para el error más común con Office 365 / Microsoft 365
        if (/SmtpClientAuthentication is disabled/i.test(raw) || /5\.7\.139/.test(raw)) {
          toast.error(
            "Microsoft 365 bloquea SMTP AUTH por defecto.\nPide al admin de O365 habilitar SMTP AUTH en el buzón (aka.ms/smtp_auth_disabled) o usar un buzón dedicado con SMTP AUTH activo.",
            { duration: 12000 }
          );
        } else {
          toast.error(String(raw).slice(0, 400), { duration: 10000 });
        }
      }
    } catch (e) {
      toast.error(`Error de red al enviar prueba SMTP: ${e?.message || e}`, { duration: 8000 });
    } finally {
      setSmtpTesting(false);
    }
  };

  const checkMarketingDns = async () => {
    setDnsChecking(true);
    setDnsCheck(null);
    try {
      const res = await fetch(`${API_URL}/api/articulos/smtp-config/dns-check`, getAuthHeaders());
      const data = await res.json();
      if (res.ok) {
        setDnsCheck(data);
        if (data.all_ok) {
          toast.success("Entregabilidad correcta: SPF, DKIM y DMARC configurados");
        } else {
          toast.warning("Se encontraron problemas de entregabilidad (revisa el detalle)");
        }
      } else {
        toast.error(data.detail || "No se pudo verificar el DNS");
      }
    } catch (e) {
      toast.error(`Error de red al verificar DNS: ${e?.message || e}`);
    } finally {
      setDnsChecking(false);
    }
  };


  const handleAddEmailReporte = () => {
    const email = (nuevoEmailReporte || "").trim();
    if (!email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Correo inválido");
      return;
    }
    const currentEmails = reporteMensualConfig?.emails_adicionales || [];
    if (currentEmails.includes(email)) {
      toast.warning("Ese correo ya está en la lista");
      return;
    }
    setReporteMensualConfig({
      ...reporteMensualConfig,
      emails_adicionales: [...currentEmails, email],
    });
    setNuevoEmailReporte("");
  };

  const handleRemoveEmailReporte = (email) => {
    setReporteMensualConfig({
      ...reporteMensualConfig,
      emails_adicionales: (reporteMensualConfig?.emails_adicionales || []).filter((e) => e !== email),
    });
  };

  const handleSaveReporteMensual = async () => {
    if (!reporteMensualConfig) return;
    setReporteMensualSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/articulos/reporte-mensual/config`, {
        method: "PUT",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify({
          activo: !!reporteMensualConfig.activo,
          dia_envio: Number(reporteMensualConfig.dia_envio) || 1,
          hora_envio: reporteMensualConfig.hora_envio || "09:00",
          emails_adicionales: reporteMensualConfig.emails_adicionales || [],
          incluir_admin_original: reporteMensualConfig.incluir_admin_original !== false,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setReporteMensualConfig(data);
        toast.success("Configuración guardada");
      } else {
        toast.error(data.detail || "Error al guardar");
      }
    } catch (e) {
      toast.error("Error al guardar configuración");
    } finally {
      setReporteMensualSaving(false);
    }
  };

  const handleEnviarReporteAhora = async () => {
    setReporteMensualSending(true);
    try {
      const res = await fetch(`${API_URL}/api/articulos/reporte-mensual/enviar-ahora`, {
        method: "POST",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
      });
      const data = await res.json();
      if (res.ok) {
        if (data.enviado) {
          const total = data.envio?.results?.filter((r) => r.ok).length || 0;
          toast.success(`Reporte enviado a ${total} destinatario${total === 1 ? '' : 's'}`);
          // Refresh config to show ultimo_envio
          openReporteMensualDialog();
        } else {
          toast.warning(data.razon || "No se envió");
        }
      } else {
        toast.error(data.detail || "Error al enviar");
      }
    } catch (e) {
      toast.error("Error al enviar reporte");
    } finally {
      setReporteMensualSending(false);
    }
  };

  const handlePreviewReporte = () => {
    const url = `${API_URL}/api/articulos/reporte-mensual/previsualizar-pdf`;
    // Open in new tab using fetch blob (to attach auth header)
    fetch(url, getAuthHeaders())
      .then((r) => {
        if (!r.ok) throw new Error("preview failed");
        return r.blob();
      })
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, "_blank");
      })
      .catch(() => toast.error("No se pudo previsualizar el PDF"));
  };


  const openEnvioDialog = (articulo = null) => {
    // Read send-as-image preference saved in editor_config (set in Visual Email Editor)
    const cfg = articulo?.editor_config || {};
    const anchoIn = parseFloat(cfg.imagenAnchoPulgadas) || 5.5;
    const altoIn = parseFloat(cfg.imagenAltoPulgadas) || 11;
    setEnvioForm({
      articulo_id: articulo?.id || "",
      lista_ids: [],
      contactos_adicionales: "",
      programado: false,
      fecha_programada: "",
      enviar_usuarios_sistema: false,
      usuarios_ids: [],
      nombre_remitente: "Qasesores Corredores y Asesores de Seguros SRL",
      enviar_como_imagen: !!cfg.enviarComoImagen,
      imagen_ancho: cfg.imagenAncho || Math.round(anchoIn * 96),
      imagen_alto: Math.round(altoIn * 96),
      imagen_autoajuste: cfg.imagenAutoajuste !== undefined ? !!cfg.imagenAutoajuste : true,
      imagen_link_url: cfg.imagenLinkUrl || "",
      incluir_link_ver_online: true,
    });
    setShowEnvioDialog(true);
  };

  const openEditArticulo = (articulo) => {
    setEditingArticulo(articulo);
    setArticuloForm({
      titulo: articulo.titulo || "",
      asunto: articulo.asunto || "",
      contenido_html: articulo.contenido_html || "",
      contenido_tipo: articulo.contenido_tipo || "editor",
      imagen_url: articulo.imagen_url || "",
      categoria: articulo.categoria || "",
      editor_config: articulo.editor_config || null
    });
    setShowArticuloDialog(true);
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
        <Sidebar user={user} />
        <main className="ml-0 md:ml-64 flex-1 p-6 flex items-center justify-center">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar user={user} />
      <main className="ml-0 md:ml-64 flex-1 p-6 overflow-x-clip">
        <div className="max-w-7xl mx-auto space-y-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
          {/* STICKY HEADER - se mantiene fijo al hacer scroll */}
          <div className="sticky top-0 z-20 bg-slate-50 dark:bg-slate-900 pt-2 pb-4 border-b border-slate-200 dark:border-slate-700 space-y-6" data-testid="articulos-sticky-header">
          {/* Header */}
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Artículos</h1>
              <p className="text-slate-500">Gestión de envíos masivos de correo electrónico</p>
            </div>
            <div className="flex gap-2">
              {["admin", "administrador", "superadmin"].includes((user?.role || "").toLowerCase()) && (
                <Button
                  variant="outline"
                  className="text-purple-600 border-purple-200 hover:bg-purple-50"
                  data-testid="smtp-marketing-btn"
                  onClick={() => openSmtpMarketingDialog()}
                >
                  <Server className="w-4 h-4 mr-2" />
                  SMTP Marketing
                </Button>
              )}
              {["admin", "administrador", "superadmin"].includes((user?.role || "").toLowerCase()) && (
                <Button
                  variant="outline"
                  className="text-amber-700 border-amber-300 hover:bg-amber-50 dark:text-amber-300 dark:border-amber-700 dark:hover:bg-amber-900/30"
                  data-testid="migrate-spacing-btn"
                  onClick={() => { setMigrationResult(null); setShowMigrationDialog(true); }}
                >
                  <Eraser className="w-4 h-4 mr-2" />
                  Limpiar Espacios
                </Button>
              )}
              {["admin", "administrador", "superadmin"].includes((user?.role || "").toLowerCase()) && (
                <Button
                  variant="outline"
                  className="text-emerald-700 border-emerald-300 hover:bg-emerald-50 dark:text-emerald-300 dark:border-emerald-700 dark:hover:bg-emerald-900/30"
                  data-testid="reparar-estadisticas-btn"
                  onClick={handleRepararEstadisticas}
                  disabled={reparandoStats}
                  title="Repara contadores, elimina duplicados y envíos fallidos por Playwright"
                >
                  <Wrench className="w-4 h-4 mr-2" />
                  {reparandoStats ? `Reparando ${reparProgreso.done}/${reparProgreso.total}...` : "Reparar Estadísticas"}
                </Button>
              )}
              {!soloEstadisticas && (
                <>
                  <Button variant="outline" onClick={() => openReporteMensualDialog()} data-testid="reporte-mensual-btn">
                    <Mail className="w-4 h-4 mr-2" />
                    Reporte Mensual
                  </Button>
                  <Button variant="outline" onClick={() => { setShowListaDialog(true); setListaForm({ nombre: "", descripcion: "" }); }}>
                    <List className="w-4 h-4 mr-2" />
                    Nueva Lista
                  </Button>
                  <Button onClick={() => { setShowArticuloDialog(true); setEditingArticulo(null); setArticuloForm({ titulo: "", asunto: "", contenido_html: "", contenido_tipo: "editor", imagen_url: "", categoria: "", editor_config: null }); }}>
                    <Plus className="w-4 h-4 mr-2" />
                    Nuevo Artículo
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Stats Cards */}
          {estadisticasGenerales && (
            <div className="space-y-2">
              {filtroArticuloId && (
                <div className="flex items-center justify-between gap-3 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg" data-testid="filtro-articulo-banner">
                  <div className="flex items-center gap-2 text-sm">
                    <Filter className="w-4 h-4 text-blue-600" />
                    <span className="text-blue-900">
                      Métricas filtradas por: <span className="font-semibold">
                        {articulos.find(a => a.id === filtroArticuloId)?.titulo || "artículo seleccionado"}
                      </span>
                    </span>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => { setFiltroArticuloId(null); fetchEstadisticasGenerales(null); }} data-testid="clear-filtro-btn" className="h-7 text-blue-700 hover:bg-blue-100">
                    <X className="w-3.5 h-3.5 mr-1" /> Ver métricas globales
                  </Button>
                </div>
              )}
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="inline-flex rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden" data-testid="audiencia-selector">
                  {[
                    { key: "todos", label: "Todos" },
                    { key: "interno", label: "Interno" },
                    { key: "externo", label: "Externo" },
                  ].map(opt => (
                    <button
                      key={opt.key}
                      onClick={() => setAudienciaView(opt.key)}
                      data-testid={`audiencia-opt-${opt.key}`}
                      className={`px-4 py-1.5 text-sm font-medium transition-colors ${audienciaView === opt.key ? "bg-blue-600 text-white" : "bg-white dark:bg-slate-800 text-slate-600 hover:bg-slate-50"}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-4">
                {(audienciaView === "externo" || audienciaView === "todos") &&
                  renderKpiRow(statsExterno, "Externo", "bg-emerald-100 text-emerald-700")}
                {(audienciaView === "interno" || audienciaView === "todos") &&
                  renderKpiRow(statsInterno, "Interno", "bg-indigo-100 text-indigo-700")}
              </div>
            </div>
          )}

          {/* Tabs */}
            <TabsList>
              <TabsTrigger value="articulos">
                <FileText className="w-4 h-4 mr-2" />
                Artículos
              </TabsTrigger>
              <TabsTrigger value="listas">
                <Users className="w-4 h-4 mr-2" />
                Listas de Contactos
              </TabsTrigger>
              <TabsTrigger value="envios">
                <Send className="w-4 h-4 mr-2" />
                Historial de Envíos
              </TabsTrigger>
              <TabsTrigger value="programados" data-testid="tab-programados">
                <Clock className="w-4 h-4 mr-2" />
                Programados
              </TabsTrigger>
              <TabsTrigger value="desuscritos" data-testid="tab-desuscritos">
                <UserX className="w-4 h-4 mr-2" />
                Desuscritos
                {desuscritos.length > 0 && (
                  <Badge variant="secondary" className="ml-2 bg-red-100 text-red-700 border-red-200">
                    {desuscritos.length}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="no-deseados" data-testid="tab-no-deseados">
                <Ban className="w-4 h-4 mr-2" />
                No Deseados
                {blocklist.length > 0 && (
                  <Badge variant="secondary" className="ml-2 bg-amber-100 text-amber-700 border-amber-200">
                    {blocklist.length}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>
          </div>
          {/* FIN STICKY HEADER */}

            {/* Artículos Tab */}
            <TabsContent value="articulos" className="mt-4">
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle>Mis Artículos</CardTitle>
                    <div className="flex items-center gap-2">
                      {!soloEstadisticas && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleRecalcularEstadisticas}
                        disabled={recalculandoStats}
                        title="Recalcula y guarda los contadores (Enviados/Abiertos/Clicks únicos) de todos los artículos para que coincidan con el detalle"
                        data-testid="recalcular-estadisticas-btn"
                      >
                        {recalculandoStats ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <RotateCw className="w-4 h-4 mr-2" />
                        )}
                        {recalculandoStats ? "Recalculando…" : "Recalcular estadísticas"}
                      </Button>
                      )}
                      {!soloEstadisticas && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleReconstruirDetalle}
                        disabled={reconstruyendoDetalle}
                        title="Reconstruye el detalle por destinatario (a quién se envió + clics) de los envíos que lo hayan perdido. No destructivo."
                        data-testid="reconstruir-detalle-btn"
                      >
                        {reconstruyendoDetalle ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Wrench className="w-4 h-4 mr-2" />
                        )}
                        {reconstruyendoDetalle ? "Reconstruyendo…" : "Reconstruir detalle"}
                      </Button>
                      )}
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          placeholder="Buscar..."
                          value={searchArticulo}
                          onChange={(e) => setSearchArticulo(e.target.value)}
                          className="pl-9 w-64"
                          data-testid="search-articulo-input"
                        />
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="overflow-auto max-h-[65vh]">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100 dark:bg-slate-800 sticky top-0 z-10">
                        <tr>
                          {[
                            { key: "titulo", label: "Título", align: "left" },
                            { key: "asunto", label: "Asunto", align: "left" },
                            { key: "contenido_tipo", label: "Tipo", align: "left" },
                            { key: "total_enviados", label: "Enviados", align: "center" },
                            { key: "total_rebotados", label: "Rebotes", align: "center" },
                            { key: "total_abiertos", label: "Abiertos", align: "center" },
                            { key: "total_clicks", label: "Clicks", align: "center" },
                            { key: "created_at", label: "Fecha", align: "left" },
                            { key: "fecha_envio", label: "Fecha Envío", align: "left" },
                          ].map((col) => (
                            <th
                              key={col.key}
                              className={`p-3 text-${col.align} cursor-pointer select-none hover:bg-slate-200 dark:hover:bg-slate-700`}
                              onClick={() => handleArticuloSort(col.key)}
                              title="Click para ordenar; click de nuevo invierte el orden"
                              data-testid={`th-articulo-${col.key}`}
                            >
                              <span className={`inline-flex items-center gap-1 ${col.align === "center" ? "justify-center" : ""}`}>
                                {col.label}
                                {articuloSort.key === col.key
                                  ? (articuloSort.dir === "asc" ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />)
                                  : <ArrowUpDown className="w-3 h-3 opacity-30" />}
                              </span>
                            </th>
                          ))}
                          <th className="p-3 text-center">Acciones</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {sortedArticulos.map((articulo) => (
                          <tr
                            key={articulo.id}
                            className={`cursor-pointer transition-colors ${filtroArticuloId === articulo.id ? "bg-blue-50 dark:bg-blue-950 ring-1 ring-blue-300" : (articulo.audiencias || []).includes("interno") ? "bg-sky-50 dark:bg-sky-950/30 hover:bg-sky-100 dark:hover:bg-sky-900/40" : "hover:bg-slate-50 dark:hover:bg-slate-800"}`}
                            onClick={() => handleToggleFiltroArticulo(articulo)}
                            data-testid={`articulo-row-${articulo.id}`}
                            data-audiencias={(articulo.audiencias || []).join(",")}
                            title="Click para filtrar las métricas por este artículo"
                          >
                            <td className="p-3 font-medium">
                              <div className="flex items-center gap-2">
                                <span>{articulo.titulo}</span>
                                {(articulo.audiencias || []).includes("interno") && (
                                  <Badge
                                    variant="outline"
                                    className="border-sky-300 bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300"
                                    data-testid="badge-articulo-interno"
                                  >
                                    Usuarios del sistema
                                  </Badge>
                                )}
                              </div>
                            </td>
                            <td className="p-3 text-slate-600">{articulo.asunto}</td>
                            <td className="p-3">
                              <Badge variant="outline">
                                {articulo.contenido_tipo === "imagen" ? "Imagen" : articulo.contenido_tipo === "plantilla" ? "Plantilla" : "Editor"}
                              </Badge>
                            </td>
                            <td className="p-3 text-center">{articulo.total_enviados || 0}</td>
                            <td className="p-3 text-center">
                              <span className={articulo.total_rebotados > 0 ? "text-red-500 font-medium" : ""}>
                                {articulo.total_rebotados || 0}
                              </span>
                            </td>
                            <td className="p-3 text-center">{articulo.total_abiertos || 0}</td>
                            <td className="p-3 text-center">{articulo.total_clicks || 0}</td>
                            <td className="p-3 text-slate-500">{articulo.created_at?.slice(0, 10) || "—"}</td>
                            <td className="p-3 text-slate-500" data-testid={`fecha-envio-${articulo.id}`}>
                              {articulo.proxima_programada ? (
                                <span className="inline-flex items-center gap-1 text-amber-600" title="Envío programado, aún no realizado">
                                  <CalendarClock className="w-3.5 h-3.5" />
                                  {articulo.proxima_programada.slice(0, 10)}
                                </span>
                              ) : articulo.ultima_fecha_envio ? (
                                articulo.ultima_fecha_envio.slice(0, 10)
                              ) : (
                                "—"
                              )}
                            </td>
                            <td className="p-3" onClick={(e) => e.stopPropagation()}>
                              <div className="flex justify-center gap-1">
                                {!soloEstadisticas && (
                                  <Button size="icon" variant="ghost" onClick={() => openEnvioDialog(articulo)} title="Enviar">
                                    <Send className="w-4 h-4 text-green-600" />
                                  </Button>
                                )}
                                <Button size="icon" variant="ghost" onClick={() => handleViewEstadisticas(articulo)} title="Estadísticas" data-testid={`estadisticas-articulo-${articulo.id}`}>
                                  <BarChart3 className="w-4 h-4 text-blue-600" />
                                </Button>
                                {!soloEstadisticas && (
                                  <>
                                    <Button size="icon" variant="ghost" onClick={() => handleResetEstadisticas(articulo)} title="Borrar estadísticas (dejar como recién creado)" data-testid={`reset-estadisticas-${articulo.id}`}>
                                      <RotateCw className="w-4 h-4 text-amber-600" />
                                    </Button>
                                    <Button size="icon" variant="ghost" onClick={() => handleClonarArticulo(articulo)} title="Clonar artículo" data-testid={`clone-articulo-${articulo.id}`}>
                                      <Copy className="w-4 h-4 text-purple-600" />
                                    </Button>
                                    <Button size="icon" variant="ghost" onClick={() => openEditArticulo(articulo)} title="Editar">
                                      <Edit className="w-4 h-4" />
                                    </Button>
                                    <Button size="icon" variant="ghost" onClick={() => handleDeleteArticulo(articulo)} title="Eliminar" className="text-red-500">
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                        {sortedArticulos.length === 0 && (
                          <tr>
                            <td colSpan={10} className="p-8 text-center text-slate-500">
                              {audienciaView === "interno"
                                ? "No hay artículos enviados a usuarios del sistema."
                                : audienciaView === "externo"
                                  ? "No hay artículos enviados a contactos externos."
                                  : "No hay artículos. Crea tu primer artículo para comenzar."}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Listas de Contactos Tab */}
            <TabsContent value="listas" className="mt-4">
              <Card className="mb-4">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="relative flex-1 min-w-[260px]">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Búsqueda general en TODAS las listas de contactos..."
                        value={searchContactoGlobal}
                        onChange={(e) => setSearchContactoGlobal(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") buscarContactosGlobal(); }}
                        className="pl-9"
                        data-testid="buscar-global-input"
                      />
                    </div>
                    <Button onClick={buscarContactosGlobal} disabled={buscandoGlobal} data-testid="buscar-global-btn">
                      <Search className="w-4 h-4 mr-2" />
                      {buscandoGlobal ? "Buscando..." : "Buscar"}
                    </Button>
                    {resultadosGlobal !== null && (
                      <Button variant="ghost" onClick={() => { setResultadosGlobal(null); setSearchContactoGlobal(""); }} data-testid="limpiar-global-btn">
                        <X className="w-4 h-4 mr-1" /> Limpiar
                      </Button>
                    )}
                  </div>
                  {resultadosGlobal !== null && (
                    <div className="mt-3">
                      <p className="text-sm text-slate-500 mb-2">{resultadosGlobal.length} resultado(s) en todas las listas</p>
                      <div className="overflow-x-auto max-h-80 overflow-y-auto border rounded-lg" data-testid="resultados-global-tabla">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-100 dark:bg-slate-800 sticky top-0">
                            <tr>
                              <th className="p-2 text-left">Email</th>
                              <th className="p-2 text-left">Nombre</th>
                              <th className="p-2 text-left">Empresa</th>
                              <th className="p-2 text-left">Teléfono</th>
                              <th className="p-2 text-left">Lista</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {resultadosGlobal.map((c, i) => (
                              <tr key={i} className="hover:bg-slate-50 dark:hover:bg-slate-800">
                                <td className="p-2">{c.email}</td>
                                <td className="p-2">{c.nombre || "-"}</td>
                                <td className="p-2">{c.empresa || "-"}</td>
                                <td className="p-2">{c.telefono || "-"}</td>
                                <td className="p-2"><span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">{c.lista_nombre || "—"}</span></td>
                              </tr>
                            ))}
                            {resultadosGlobal.length === 0 && (
                              <tr><td colSpan={5} className="p-4 text-center text-slate-500">Sin resultados</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Listas</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {listas.map((lista) => (
                      <div
                        key={lista.id}
                        className={`p-3 rounded-lg cursor-pointer border transition-colors ${
                          selectedLista?.id === lista.id
                            ? "bg-blue-50 dark:bg-blue-900/20 border-blue-300"
                            : "hover:bg-slate-50 dark:hover:bg-slate-800 border-transparent"
                        }`}
                        onClick={() => setSelectedLista(lista)}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium">{lista.nombre}</p>
                            <p className="text-sm text-slate-500">{lista.contactos_count || 0} contactos</p>
                          </div>
                          <div className="flex gap-1">
                            {!soloEstadisticas && (
                            <>
                            <Button size="icon" variant="ghost" onClick={(e) => { e.stopPropagation(); openEditLista(lista); }} title="Editar">
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button size="icon" variant="ghost" onClick={(e) => { e.stopPropagation(); handleDeleteLista(lista); }} className="text-red-500" title="Eliminar">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                            </>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    {listas.length === 0 && (
                      <p className="text-center text-slate-500 py-4">No hay listas</p>
                    )}
                  </CardContent>
                </Card>

                <Card className="lg:col-span-2">
                  <CardHeader>
                    <div className="flex justify-between items-center">
                      <CardTitle className="text-lg">
                        {selectedLista ? `Contactos: ${selectedLista.nombre}` : "Seleccione una lista"}
                      </CardTitle>
                      {selectedLista && !soloEstadisticas && (
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => setShowImportDialog(true)}>
                            <Upload className="w-4 h-4 mr-2" />
                            Importar
                          </Button>
                          <Button size="sm" onClick={() => setShowContactoDialog(true)}>
                            <UserPlus className="w-4 h-4 mr-2" />
                            Agregar
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {selectedLista ? (
                      <div className="space-y-3">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <Input
                            placeholder="Buscar en esta lista (email, nombre, empresa, teléfono...)"
                            value={searchContactoLista}
                            onChange={(e) => setSearchContactoLista(e.target.value)}
                            className="pl-9"
                            data-testid="buscar-en-lista-input"
                          />
                        </div>
                        <div className="overflow-x-auto max-h-96 overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-100 dark:bg-slate-800 sticky top-0">
                            <tr>
                              <th className="p-2 text-left">Email</th>
                              <th className="p-2 text-left">Nombre</th>
                              <th className="p-2 text-left">Empresa</th>
                              <th className="p-2 text-left">Teléfono</th>
                              <th className="p-2 text-left">Clase Cliente</th>
                              <th className="p-2 text-center">Acciones</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {contactosLista.filter((c) => {
                              const q = searchContactoLista.trim().toLowerCase();
                              if (!q) return true;
                              return [c.email, c.nombre, c.empresa, c.telefono, c.clase_cliente]
                                .some((v) => (v || "").toString().toLowerCase().includes(q));
                            }).map((contacto) => (
                              <tr key={contacto.id} className="hover:bg-slate-50 dark:hover:bg-slate-800">
                                <td className="p-2">{contacto.email}</td>
                                <td className="p-2">{contacto.nombre || "-"}</td>
                                <td className="p-2">{contacto.empresa || "-"}</td>
                                <td className="p-2">{contacto.telefono || "-"}</td>
                                <td className="p-2">{contacto.clase_cliente || "-"}</td>
                                <td className="p-2 text-center">
                                  <div className="flex justify-center gap-1">
                                    {!soloEstadisticas && (
                                    <>
                                    <Button size="icon" variant="ghost" onClick={() => openEditContacto(contacto)} title="Editar">
                                      <Edit className="w-4 h-4" />
                                    </Button>
                                    <Button size="icon" variant="ghost" onClick={() => handleDeleteContacto(contacto)} className="text-red-500" title="Eliminar">
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                    </>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                            {contactosLista.length === 0 && (
                              <tr>
                                <td colSpan={6} className="p-4 text-center text-slate-500">
                                  No hay contactos en esta lista
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                        </div>
                      </div>
                    ) : (
                      <p className="text-center text-slate-500 py-8">
                        Seleccione una lista para ver sus contactos
                      </p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Historial de Envíos Tab */}
            <TabsContent value="envios" className="mt-4">
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle>Historial de Envíos</CardTitle>
                    <Button variant="outline" onClick={handleViewDominioStatsGlobal}>
                      <PieChart className="w-4 h-4 mr-2" />
                      Estadísticas por Dominio (Global)
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100 dark:bg-slate-800">
                        <tr>
                          <th className="p-3 text-left">Artículo</th>
                          <th className="p-3 text-center">Estado</th>
                          <th className="p-3 text-center">Destinatarios</th>
                          <th className="p-3 text-center">Enviados</th>
                          <th className="p-3 text-center">Abiertos</th>
                          <th className="p-3 text-center">Clicks</th>
                          <th className="p-3 text-center">Rebotados</th>
                          <th className="p-3 text-left">Fecha</th>
                          <th className="p-3 text-center">Dominios</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {envios.map((envio) => (
                          <tr
                            key={envio.id}
                            data-testid={`envio-row-${envio.id}`}
                            data-audiencia={envio.audiencia || "externo"}
                            className={
                              envio.audiencia === "interno"
                                ? "bg-sky-50 dark:bg-sky-950/30 hover:bg-sky-100 dark:hover:bg-sky-900/40"
                                : "hover:bg-slate-50 dark:hover:bg-slate-800"
                            }
                          >
                            <td className="p-3 font-medium">
                              <div className="flex items-center gap-2">
                                <span>{envio.articulo_titulo}</span>
                                {envio.audiencia === "interno" && (
                                  <Badge
                                    variant="outline"
                                    className="border-sky-300 bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300"
                                    data-testid="badge-usuarios-sistema"
                                  >
                                    Usuarios del sistema
                                  </Badge>
                                )}
                              </div>
                            </td>
                            <td className="p-3 text-center">
                              <Badge className={
                                envio.estado === "completado" ? "bg-green-500" :
                                envio.estado === "enviando" ? "bg-blue-500" :
                                envio.estado === "en_cola" ? "bg-indigo-500" :
                                envio.estado === "programado" ? "bg-amber-500" :
                                "bg-red-500"
                              }>
                                {envio.estado === "en_cola" ? "en cola" : envio.estado}
                              </Badge>
                            </td>
                            <td className="p-3 text-center">{envio.total_destinatarios}</td>
                            <td className="p-3 text-center text-green-600">{envio.enviados}</td>
                            <td className="p-3 text-center text-purple-600">{envio.abiertos}</td>
                            <td className="p-3 text-center text-blue-600">{envio.clicks}</td>
                            <td className="p-3 text-center text-red-600">{envio.rebotados}</td>
                            <td className="p-3 text-slate-500">{envio.created_at?.slice(0, 16).replace("T", " ")}</td>
                            <td className="p-3 text-center">
                              <Button 
                                size="icon" 
                                variant="ghost" 
                                onClick={() => openDestinatariosEnvio(envio.id, envio.articulo_titulo)}
                                title="Ver destinatarios de este envío (por lista)"
                                data-testid={`ver-destinatarios-envio-${envio.id}`}
                              >
                                <Users className="w-4 h-4 text-emerald-600" />
                              </Button>
                              <Button 
                                size="icon" 
                                variant="ghost" 
                                onClick={() => handleViewDominioStats(envio.id)}
                                title="Ver estadísticas por dominio"
                              >
                                <Globe className="w-4 h-4 text-blue-600" />
                              </Button>
                              {!soloEstadisticas && (
                                <Button 
                                  size="icon" 
                                  variant="ghost" 
                                  onClick={() => handleEliminarEnvio(envio, "historial")}
                                  title="Eliminar este envío del historial (borra sus estadísticas)"
                                  data-testid={`eliminar-envio-${envio.id}`}
                                >
                                  <Trash2 className="w-4 h-4 text-red-600" />
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                        {envios.length === 0 && (
                          <tr>
                            <td colSpan={9} className="p-8 text-center text-slate-500">
                              No hay envíos registrados
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Envíos Programados Tab */}
            <TabsContent value="programados" className="mt-4">
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Clock className="w-5 h-5 text-amber-600" />
                        Envíos Programados
                      </CardTitle>
                      <p className="text-xs text-slate-500 mt-1">
                        Lista de artículos programados para envío automático. Se disparan cada minuto al llegar la hora.
                      </p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => { fetchProgramados(); fetchSchedulerHealth(); }} data-testid="refresh-programados-btn">
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Actualizar
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <SchedulerHealthBanner health={schedulerHealth} />
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100 dark:bg-slate-800">
                        <tr>
                          <th className="p-3 text-left">Artículo</th>
                          <th className="p-3 text-left">Fecha Programada</th>
                          <th className="p-3 text-center">Destinatarios</th>
                          <th className="p-3 text-left">Remitente</th>
                          <th className="p-3 text-left">Creado por</th>
                          <th className="p-3 text-center">Acciones</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {programados.map((envio) => {
                          const fecha = envio.fecha_programada ? new Date(envio.fecha_programada) : null;
                          const isPast = fecha && fecha < new Date();
                          return (
                            <tr key={envio.id} className="hover:bg-slate-50 dark:hover:bg-slate-800" data-testid={`programado-row-${envio.id}`}>
                              <td className="p-3 font-medium">
                                <button
                                  type="button"
                                  className="text-left hover:text-blue-600 hover:underline"
                                  onClick={() => irAlArticulo(envio.articulo_id)}
                                  data-testid={`ir-articulo-${envio.id}`}
                                  title="Ver este artículo en la pestaña Artículos"
                                >
                                  {envio.articulo_titulo}
                                </button>
                              </td>
                              <td className="p-3">
                                {fecha ? (
                                  <span className={isPast ? "text-amber-600 font-medium" : ""}>
                                    {fecha.toLocaleString("es-DO", { dateStyle: "medium", timeStyle: "short" })}
                                    {isPast && <span className="ml-2 text-[10px] text-amber-600">(esperando dispatch)</span>}
                                  </span>
                                ) : <span className="text-slate-400">—</span>}
                              </td>
                              <td className="p-3 text-center">
                                <Badge variant="outline">{envio.total_destinatarios}</Badge>
                              </td>
                              <td className="p-3 text-slate-600">{envio.nombre_remitente || "—"}</td>
                              <td className="p-3 text-slate-500">{envio.created_by_name || "—"}</td>
                              <td className="p-3 text-center">
                                <div className="flex gap-1 justify-center items-center">
                                  {envio.estado === "pausado_programado" && (
                                    <Badge variant="outline" className="text-amber-700 border-amber-300 mr-1" data-testid={`estado-pausado-${envio.id}`}>En pausa</Badge>
                                  )}
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                                    onClick={() => openDestinatariosEnvio(envio.id, envio.articulo_titulo)}
                                    data-testid={`ver-destinatarios-prog-${envio.id}`}
                                    title="Ver destinatarios programados (por lista)"
                                  >
                                    <Users className="w-3.5 h-3.5 mr-1" /> Destinatarios
                                  </Button>
                                  {soloEstadisticas ? (
                                    <span className="text-slate-400 text-xs">—</span>
                                  ) : (
                                  <>
                                  {envio.estado === "pausado_programado" ? (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="text-blue-700 border-blue-200 hover:bg-blue-50"
                                      onClick={() => handleReanudarProgramado(envio.id)}
                                      disabled={controlEnvioId === envio.id}
                                      data-testid={`reanudar-programado-${envio.id}`}
                                      title="Reanudar: se volverá a disparar a su hora programada"
                                    >
                                      <RotateCw className="w-3.5 h-3.5 mr-1" /> Reanudar
                                    </Button>
                                  ) : (
                                  <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-green-700 border-green-200 hover:bg-green-50"
                                    onClick={() => handleEnviarAhora(envio)}
                                    data-testid={`enviar-ahora-${envio.id}`}
                                    title="Disparar este envío inmediatamente"
                                  >
                                    <Send className="w-3.5 h-3.5 mr-1" /> Enviar ahora
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-amber-700 border-amber-200 hover:bg-amber-50"
                                    onClick={() => handlePausarEnvio(envio.id, () => { fetchProgramados(); fetchEnvios(); })}
                                    disabled={controlEnvioId === envio.id}
                                    data-testid={`pausar-programado-${envio.id}`}
                                    title="Pausar: no se disparará hasta que lo reanudes"
                                  >
                                    <Pause className="w-3.5 h-3.5 mr-1" /> Pausar
                                  </Button>
                                  </>
                                  )}
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-red-700 border-red-200 hover:bg-red-50"
                                    onClick={() => handleCancelarProgramado(envio)}
                                    data-testid={`cancelar-programado-${envio.id}`}
                                    title="Detener y cancelar el envío programado"
                                  >
                                    <X className="w-3.5 h-3.5 mr-1" /> Detener
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-red-700 border-red-300 hover:bg-red-100"
                                    onClick={() => handleEliminarEnvio(envio, "programado")}
                                    data-testid={`eliminar-programado-${envio.id}`}
                                    title="Eliminar permanentemente este envío programado"
                                  >
                                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Eliminar
                                  </Button>
                                  </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                        {programados.length === 0 && (
                          <tr>
                            <td colSpan={6} className="p-8 text-center text-slate-500">
                              <Clock className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                              No hay envíos programados pendientes.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Desuscritos Tab */}
            <TabsContent value="desuscritos" className="mt-4">
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center flex-wrap gap-3">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <UserX className="w-5 h-5 text-red-600" />
                        Contactos Desuscritos
                      </CardTitle>
                      <p className="text-xs text-slate-500 mt-1">
                        Lista global de correos que cancelaron su suscripción. Estos contactos no reciben envíos hasta que se desbloqueen manualmente.
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          placeholder="Buscar email..."
                          value={searchDesuscrito}
                          onChange={(e) => setSearchDesuscrito(e.target.value)}
                          className="pl-10 w-64"
                          data-testid="search-desuscritos-input"
                        />
                      </div>
                      <Button variant="outline" size="sm" onClick={fetchDesuscritos} data-testid="refresh-desuscritos-btn">
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Actualizar
                      </Button>
                      <Button size="sm" onClick={exportarDesuscritos} disabled={exportandoDesuscritos} className="bg-emerald-600 hover:bg-emerald-700" data-testid="export-desuscritos-btn">
                        <Download className="w-4 h-4 mr-2" />
                        {exportandoDesuscritos ? "Exportando..." : "Exportar a Excel"}
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="desuscritos-table">
                      <thead className="bg-slate-100 dark:bg-slate-800">
                        <tr>
                          <th className="p-3 text-left">Email</th>
                          <th className="p-3 text-left">Primera desuscripción</th>
                          <th className="p-3 text-left">Última desuscripción</th>
                          <th className="p-3 text-left">Último artículo</th>
                          <th className="p-3 text-center">Veces</th>
                          <th className="p-3 text-center">Acción</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {desuscritos
                          .filter((d) =>
                            !searchDesuscrito ||
                            (d.email || "").toLowerCase().includes(searchDesuscrito.toLowerCase())
                          )
                          .map((d) => {
                            const fmt = (iso) => {
                              if (!iso) return "—";
                              try {
                                return new Date(iso).toLocaleString("es-DO", { dateStyle: "medium", timeStyle: "short" });
                              } catch {
                                return iso;
                              }
                            };
                            return (
                              <tr
                                key={d.id || d.email}
                                className="hover:bg-slate-50 dark:hover:bg-slate-800"
                                data-testid={`desuscrito-row-${d.email}`}
                              >
                                <td className="p-3 font-medium break-all">{d.email}</td>
                                <td className="p-3 text-slate-600">{fmt(d.first_unsubscribed_at)}</td>
                                <td className="p-3 text-slate-600">{fmt(d.last_unsubscribed_at)}</td>
                                <td className="p-3 text-slate-600">
                                  {d.last_articulo_titulo || <span className="text-slate-400">—</span>}
                                </td>
                                <td className="p-3 text-center">
                                  <Badge variant="outline">{d.unsubscribe_count || 1}</Badge>
                                </td>
                                <td className="p-3 text-center">
                                  {!soloEstadisticas ? (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-green-700 border-green-200 hover:bg-green-50"
                                    onClick={() => handleDesbloquearDesuscrito(d.email)}
                                    data-testid={`desbloquear-btn-${d.email}`}
                                    title="Eliminar de la lista de desuscritos"
                                  >
                                    <Check className="w-3.5 h-3.5 mr-1" /> Desbloquear
                                  </Button>
                                  ) : (
                                    <span className="text-slate-400 text-xs">—</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        {!loadingDesuscritos && desuscritos.length === 0 && (
                          <tr>
                            <td colSpan={6} className="p-8 text-center text-slate-500">
                              <UserX className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                              No hay contactos desuscritos.
                            </td>
                          </tr>
                        )}
                        {loadingDesuscritos && (
                          <tr>
                            <td colSpan={6} className="p-8 text-center text-slate-500">
                              <Loader2 className="w-6 h-6 mx-auto animate-spin text-slate-400" />
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* No Deseados (Blocklist) Tab */}
            <TabsContent value="no-deseados" className="mt-4">
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center flex-wrap gap-3">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Ban className="w-5 h-5 text-amber-600" />
                        Lista de No Deseados
                      </CardTitle>
                      <p className="text-xs text-slate-500 mt-1">
                        Emails y dominios que NUNCA recibirán envíos. Se valida automáticamente antes de cada envío, igual que la lista de desuscritos.
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          placeholder="Buscar..."
                          value={searchBlocklist}
                          onChange={(e) => setSearchBlocklist(e.target.value)}
                          className="pl-10 w-64"
                          data-testid="search-blocklist-input"
                        />
                      </div>
                      <Button variant="outline" size="sm" onClick={fetchBlocklist} data-testid="refresh-blocklist-btn">
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Actualizar
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Formulario de alta */}
                  {!soloEstadisticas && (
                  <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg border">
                    <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                      <Plus className="w-4 h-4" /> Agregar a la lista
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                      <div className="md:col-span-5">
                        <Label className="text-xs text-slate-600">Email o dominio</Label>
                        <Input
                          placeholder="ej: spam@correo.com  ó  @dominio.com"
                          value={newBlocklistValue}
                          onChange={(e) => setNewBlocklistValue(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") handleAgregarBlocklist(); }}
                          data-testid="blocklist-value-input"
                        />
                        <p className="text-[11px] text-slate-500 mt-1">
                          Si escribe un dominio sin <code>@</code> al inicio (ej. <code>spam.com</code>), se guardará como dominio automáticamente.
                        </p>
                      </div>
                      <div className="md:col-span-5">
                        <Label className="text-xs text-slate-600">Motivo (opcional)</Label>
                        <Input
                          placeholder="ej: solicitó dejar de recibir, competidor, dominio descartable…"
                          value={newBlocklistReason}
                          onChange={(e) => setNewBlocklistReason(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") handleAgregarBlocklist(); }}
                          data-testid="blocklist-reason-input"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Button
                          className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                          onClick={handleAgregarBlocklist}
                          disabled={addingBlocklist || !newBlocklistValue.trim()}
                          data-testid="add-blocklist-btn"
                        >
                          {addingBlocklist ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <Plus className="w-4 h-4 mr-2" />
                          )}
                          Agregar
                        </Button>
                      </div>
                    </div>
                  </div>
                  )}

                  {/* Tabla */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="blocklist-table">
                      <thead className="bg-slate-100 dark:bg-slate-800">
                        <tr>
                          <th className="p-3 text-left">Valor</th>
                          <th className="p-3 text-left">Tipo</th>
                          <th className="p-3 text-left">Motivo</th>
                          <th className="p-3 text-left">Agregado</th>
                          <th className="p-3 text-left">Por</th>
                          <th className="p-3 text-center">Veces bloqueado</th>
                          <th className="p-3 text-center">Acción</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {blocklist
                          .filter((b) =>
                            !searchBlocklist ||
                            (b.value || "").toLowerCase().includes(searchBlocklist.toLowerCase()) ||
                            (b.reason || "").toLowerCase().includes(searchBlocklist.toLowerCase())
                          )
                          .map((b) => {
                            const fmt = (iso) => {
                              if (!iso) return "—";
                              try {
                                return new Date(iso).toLocaleString("es-DO", { dateStyle: "medium", timeStyle: "short" });
                              } catch {
                                return iso;
                              }
                            };
                            return (
                              <tr
                                key={b.id}
                                className="hover:bg-slate-50 dark:hover:bg-slate-800"
                                data-testid={`blocklist-row-${b.value}`}
                              >
                                <td className="p-3 font-mono text-xs break-all">{b.value}</td>
                                <td className="p-3">
                                  {b.type === "domain" ? (
                                    <Badge className="bg-purple-100 text-purple-700 border-purple-200">Dominio</Badge>
                                  ) : (
                                    <Badge className="bg-blue-100 text-blue-700 border-blue-200">Email</Badge>
                                  )}
                                </td>
                                <td className="p-3 text-slate-600 max-w-xs truncate" title={b.reason}>
                                  {b.reason || <span className="text-slate-400">—</span>}
                                </td>
                                <td className="p-3 text-slate-600 text-xs">{fmt(b.created_at)}</td>
                                <td className="p-3 text-slate-600 text-xs break-all">{b.created_by || "—"}</td>
                                <td
                                  className="p-3 text-center"
                                  title={b.last_blocked_at ? `Último bloqueo: ${fmt(b.last_blocked_at)}` : "Aún no se ha detectado en ningún envío"}
                                  data-testid={`blocklist-block-count-${b.value}`}
                                >
                                  {b.block_count > 0 ? (
                                    <Badge className="bg-amber-100 text-amber-800 border-amber-200 font-mono">
                                      {b.block_count}
                                    </Badge>
                                  ) : (
                                    <span className="text-slate-400 text-xs">0</span>
                                  )}
                                </td>
                                <td className="p-3 text-center">
                                  {!soloEstadisticas ? (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-red-700 border-red-200 hover:bg-red-50"
                                    onClick={() => handleEliminarBlocklist(b)}
                                    data-testid={`delete-blocklist-btn-${b.value}`}
                                    title="Eliminar de la lista"
                                  >
                                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Eliminar
                                  </Button>
                                  ) : (
                                    <span className="text-slate-400 text-xs">—</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        {!loadingBlocklist && blocklist.length === 0 && (
                          <tr>
                            <td colSpan={7} className="p-8 text-center text-slate-500">
                              <Ban className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                              No hay emails ni dominios bloqueados.
                            </td>
                          </tr>
                        )}
                        {loadingBlocklist && (
                          <tr>
                            <td colSpan={7} className="p-8 text-center text-slate-500">
                              <Loader2 className="w-6 h-6 mx-auto animate-spin text-slate-400" />
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Dialogs */}
        <Dialog open={showArticuloDialog} onOpenChange={setShowArticuloDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingArticulo ? "Editar Artículo" : "Nuevo Artículo"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* Quick Template Button */}
              {!editingArticulo && (
                <div className="p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-gray-800">¿Quieres empezar con una plantilla?</h4>
                      <p className="text-sm text-gray-500">Elige entre {plantillas.length || 15} plantillas profesionales prediseñadas</p>
                    </div>
                    <Button onClick={() => setShowPlantillasGallery(true)} className="gap-2">
                      <LayoutTemplate className="w-4 h-4" />
                      Ver Galería de Plantillas
                    </Button>
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Título *</Label>
                  <Input value={articuloForm.titulo} onChange={(e) => setArticuloForm({ ...articuloForm, titulo: e.target.value })} placeholder="Título del artículo" />
                </div>
                <div>
                  <Label>Asunto del Correo *</Label>
                  <Input value={articuloForm.asunto} onChange={(e) => setArticuloForm({ ...articuloForm, asunto: e.target.value })} placeholder="Asunto que verán los destinatarios" />
                </div>
              </div>
              <div>
                <Label>Tipo de Contenido</Label>
                <Select value={articuloForm.contenido_tipo} onValueChange={(v) => setArticuloForm({ ...articuloForm, contenido_tipo: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="editor">Editor Visual</SelectItem>
                    <SelectItem value="plantilla">Usar Plantilla</SelectItem>
                    <SelectItem value="imagen">Solo Imagen</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {articuloForm.contenido_tipo === "editor" && (
                <div className="space-y-4">
                  {/* Visual Editor Banner */}
                  <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-gray-800">Editor Visual de Email</h4>
                        <p className="text-sm text-gray-500">Crea tu plantilla con múltiples secciones, imágenes y texto</p>
                        {articuloForm.editor_config && (
                          <p className="text-xs text-green-600 mt-1">✓ Diseño guardado - Click para editar</p>
                        )}
                      </div>
                      <Button onClick={() => setShowVisualEditor(true)} className="gap-2">
                        <Paintbrush className="w-4 h-4" />
                        {articuloForm.editor_config ? "Editar Diseño" : "Abrir Editor Visual"}
                      </Button>
                    </div>
                  </div>
                  
                  {/* Textarea for manual HTML editing */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Contenido HTML</Label>
                      <Button type="button" variant="outline" size="sm" onClick={abrirRecapModal} className="gap-1.5" data-testid="articulos-mes-btn">
                        <List className="w-4 h-4" />
                        Artículos del Mes
                      </Button>
                    </div>
                    <Textarea
                      ref={contenidoHtmlRef}
                      value={articuloForm.contenido_html}
                      onChange={(e) => setArticuloForm({ ...articuloForm, contenido_html: e.target.value })}
                      placeholder="<div>Tu contenido aquí...</div>"
                      className="min-h-[150px] font-mono text-sm"
                    />
                    {articuloForm.contenido_html && (
                      <button
                        type="button"
                        onClick={() => setShowEmailPreview(true)}
                        className="text-sm text-blue-600 hover:underline"
                        data-testid="ver-vista-previa-btn"
                      >
                        Ver vista previa (correo completo)
                      </button>
                    )}
                  </div>
                </div>
              )}
              {articuloForm.contenido_tipo === "imagen" && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Imagen del Artículo</Label>
                    <div className="flex gap-2">
                      <Input value={articuloForm.imagen_url} onChange={(e) => setArticuloForm({ ...articuloForm, imagen_url: e.target.value })} placeholder="URL de la imagen" className="flex-1" />
                      <Button variant="outline" asChild>
                        <label className="cursor-pointer">
                          <Upload className="w-4 h-4 mr-2" />Subir
                          <input type="file" accept="image/*" className="hidden" onChange={handleUploadImage} />
                        </label>
                      </Button>
                    </div>
                    {articuloForm.imagen_url && <img src={articuloForm.imagen_url} alt="Preview" className="max-h-48 rounded border" />}
                  </div>
                </div>
              )}
              {articuloForm.contenido_tipo === "plantilla" && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label>Plantilla Seleccionada</Label>
                    <Button variant="outline" size="sm" onClick={() => setShowPlantillasGallery(true)}>
                      <LayoutTemplate className="w-4 h-4 mr-1" /> Cambiar Plantilla
                    </Button>
                  </div>
                  {articuloForm.plantilla_id && (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-green-700">
                        Plantilla aplicada: <strong>{plantillas.find(p => p.id === articuloForm.plantilla_id)?.nombre || articuloForm.plantilla_id}</strong>
                      </span>
                    </div>
                  )}
                </div>
              )}
              <div>
                <Label>Categoría (opcional)</Label>
                <Input value={articuloForm.categoria} onChange={(e) => setArticuloForm({ ...articuloForm, categoria: e.target.value })} placeholder="Ej: Newsletter, Promoción" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowArticuloDialog(false)}>Cancelar</Button>
              <Button onClick={handleSaveArticulo}>{editingArticulo ? "Actualizar" : "Guardar"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Galería de Plantillas */}
        <Dialog open={showPlantillasGallery} onOpenChange={setShowPlantillasGallery}>
          <DialogContent className="max-w-5xl h-[85vh]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <LayoutTemplate className="w-5 h-5" />
                Galería de Plantillas de Email
              </DialogTitle>
            </DialogHeader>
            <div className="flex-1 min-h-0">
              <PlantillasGallery 
                plantillas={plantillas}
                onSelect={handleSelectPlantilla}
                onClose={() => setShowPlantillasGallery(false)}
                onImageUpload={handleUploadImageForGallery}
              />
            </div>
          </DialogContent>
        </Dialog>

        {/* Editor Visual de Email */}
        <Dialog open={showVisualEditor} onOpenChange={setShowVisualEditor}>
          <DialogContent className="max-w-6xl h-[90vh]">
            <VisualEmailEditor
              token={token}
              articulos={articulos}
              initialConfig={articuloForm.editor_config}
              onSave={(html, editorConfig) => {
                setArticuloForm(prev => ({ 
                  ...prev, 
                  contenido_html: html,
                  editor_config: editorConfig 
                }));
                setShowVisualEditor(false);
                toast.success("Diseño guardado");
              }}
              onCancel={() => setShowVisualEditor(false)}
            />
          </DialogContent>
        </Dialog>

        <Dialog open={showListaDialog} onOpenChange={(open) => { setShowListaDialog(open); if (!open) { setEditingLista(null); setListaForm({ nombre: "", descripcion: "" }); } }}>
          <DialogContent>
            <DialogHeader><DialogTitle>{editingLista ? "Editar Lista" : "Nueva Lista de Contactos"}</DialogTitle></DialogHeader>
            <div className="space-y-4 py-4">
              <div><Label>Nombre *</Label><Input value={listaForm.nombre} onChange={(e) => setListaForm({ ...listaForm, nombre: e.target.value })} placeholder="Nombre de la lista" /></div>
              <div><Label>Descripción</Label><Textarea value={listaForm.descripcion} onChange={(e) => setListaForm({ ...listaForm, descripcion: e.target.value })} placeholder="Descripción opcional" /></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setShowListaDialog(false)}>Cancelar</Button><Button onClick={handleSaveLista}>{editingLista ? "Guardar Cambios" : "Crear Lista"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={showImportDialog} onOpenChange={(open) => { setShowImportDialog(open); if (!open) resetImportState(); }}>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Importar Contactos</DialogTitle><DialogDescription>Suba un archivo Excel o CSV con una columna de emails</DialogDescription></DialogHeader>
            <div className="py-4 space-y-3">
              <label className="flex items-start gap-3 p-3 border rounded-lg bg-amber-50 border-amber-200 cursor-pointer hover:bg-amber-100 transition-colors">
                <Checkbox
                  checked={importUpdateExisting}
                  onCheckedChange={(c) => setImportUpdateExisting(!!c)}
                  data-testid="import-update-existing-checkbox"
                  className="mt-0.5"
                />
                <div className="flex-1 text-sm">
                  <div className="font-medium text-amber-900">Actualizar datos de duplicados</div>
                  <p className="text-xs text-amber-700 mt-0.5">
                    Si un email ya existe en la lista, sus datos (nombre, empresa, teléfono, etc.) se actualizarán con los valores del Excel. Por defecto los duplicados se ignoran y los contactos existentes quedan sin cambios.
                  </p>
                </div>
              </label>

              {importSheetsLoading ? (
                <div className="border-2 border-dashed rounded-lg p-8 text-center flex flex-col items-center gap-2 text-slate-500" data-testid="import-detecting-sheets">
                  <Loader2 className="w-7 h-7 animate-spin" />
                  <p className="text-sm">Detectando hojas del Excel…</p>
                </div>
              ) : importFileObj ? (
                <div className="space-y-3" data-testid="import-preview-step">
                  {importFileObj && <p className="text-xs text-slate-500 truncate">Archivo: <b>{importFileObj.name}</b></p>}

                  {importSheets.length > 1 && (
                    <div className="rounded-lg p-3 space-y-2 bg-indigo-50 border border-indigo-200" data-testid="import-sheet-selector">
                      <div className="flex items-center gap-2 text-indigo-900">
                        <FileText className="w-4 h-4" />
                        <p className="text-sm font-medium">El Excel tiene {importSheets.length} hojas. ¿Cuál deseas importar?</p>
                      </div>
                      <Select value={selectedSheet} onValueChange={handleSheetChange}>
                        <SelectTrigger data-testid="import-sheet-select" className="bg-white">
                          <SelectValue placeholder="Selecciona una hoja" />
                        </SelectTrigger>
                        <SelectContent>
                          {importSheets.map((s) => (
                            <SelectItem key={s.name} value={s.name} data-testid={`import-sheet-option-${s.name}`}>
                              {s.name}{typeof s.rows === "number" ? ` (${s.rows} filas)` : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {importPreviewLoading ? (
                    <div className="border rounded-lg p-6 text-center flex flex-col items-center gap-2 text-slate-500" data-testid="import-preview-loading">
                      <Loader2 className="w-6 h-6 animate-spin" />
                      <p className="text-sm">Generando vista previa…</p>
                    </div>
                  ) : importPreview ? (
                    <div className="space-y-3" data-testid="import-preview-content">
                      {/* Column mapping (editable — re-map manually if auto-detection misses) */}
                      <div className="rounded-lg border p-3 bg-slate-50">
                        <p className="text-xs font-semibold text-slate-700 mb-2">Mapeo de columnas <span className="font-normal text-slate-400">(ajústalo si la detección automática falla)</span></p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {[
                            { k: "email", label: "Email", req: true },
                            { k: "nombre", label: "Nombre" },
                            { k: "empresa", label: "Empresa" },
                            { k: "telefono", label: "Teléfono" },
                            { k: "clase_cliente", label: "Clase Cliente" },
                          ].map(({ k, label, req }) => (
                            <div key={k} className="flex items-center gap-2" data-testid={`map-row-${k}`}>
                              <span className={`text-xs w-24 shrink-0 ${req ? "font-semibold text-slate-700" : "text-slate-500"}`}>{label}{req ? " *" : ""}</span>
                              <Select
                                value={manualMapping[k] || (req ? "" : "__none__")}
                                onValueChange={(v) => setManualMapping((prev) => ({ ...prev, [k]: v }))}
                              >
                                <SelectTrigger className="h-8 text-xs bg-white" data-testid={`map-select-${k}`}>
                                  <SelectValue placeholder={req ? "Selecciona columna…" : "(ninguna)"} />
                                </SelectTrigger>
                                <SelectContent>
                                  {!req && <SelectItem value="__none__" className="text-xs">(ninguna)</SelectItem>}
                                  {importPreview.columns?.map((c) => (
                                    <SelectItem key={c} value={c} className="text-xs" data-testid={`map-${k}-option-${c}`}>
                                      {c}{k === "email" && importPreview.column_email_counts ? ` (${importPreview.column_email_counts[c] || 0} correos)` : ""}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Sample rows table */}
                      <div className="rounded-lg border overflow-hidden">
                        <div className="overflow-x-auto max-h-56">
                          <table className="w-full text-xs" data-testid="import-preview-table">
                            <thead className="bg-slate-100 sticky top-0">
                              <tr>
                                {importPreview.columns?.map((c) => (
                                  <th key={c} className={`px-2 py-1.5 text-left font-semibold whitespace-nowrap ${c === manualMapping.email ? "text-emerald-700" : "text-slate-600"}`}>
                                    {c}{c === manualMapping.email ? " ✉" : ""}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {importPreview.sample_rows?.map((row, ri) => (
                                <tr key={ri} className="border-t">
                                  {importPreview.columns?.map((c) => (
                                    <td key={c} className="px-2 py-1.5 whitespace-nowrap text-slate-700 max-w-[180px] truncate">{row[c]}</td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <p className="text-xs text-slate-600" data-testid="import-preview-summary">
                        <b className="text-emerald-700">{importPreview.column_email_counts?.[manualMapping.email] ?? importPreview.valid_emails}</b> correos válidos en la columna seleccionada, de <b>{importPreview.total_rows}</b> filas
                        {!(importPreview.column_email_counts?.[manualMapping.email] ?? importPreview.valid_emails) && <span className="text-red-600"> — selecciona una columna que contenga correos.</span>}
                      </p>
                    </div>
                  ) : null}

                  <div className="flex justify-end gap-2 pt-1">
                    <Button variant="outline" size="sm" onClick={resetImportState} disabled={importing} data-testid="import-change-file-btn">Cambiar archivo</Button>
                    <Button
                      size="sm"
                      onClick={() => doImport(importFileObj, importSheets.length > 1 ? selectedSheet : null)}
                      disabled={importing || importPreviewLoading || !importPreview || !manualMapping.email || !(importPreview.column_email_counts?.[manualMapping.email] ?? importPreview.valid_emails)}
                      data-testid="import-confirm-btn"
                    >
                      {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                      {importing ? "Importando…" : `Confirmar e importar${importPreview ? ` (${importPreview.column_email_counts?.[manualMapping.email] ?? importPreview.valid_emails})` : ""}`}
                    </Button>
                  </div>
                </div>
              ) : (
                <Label className="cursor-pointer block">
                  <div className="border-2 border-dashed rounded-lg p-8 text-center hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                    <Upload className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                    <p className="text-sm text-slate-600">Click para seleccionar archivo</p>
                    <p className="text-xs text-slate-400 mt-1">Excel (.xlsx) o CSV</p>
                  </div>
                  <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleImportContactos} data-testid="import-file-input" />
                </Label>
              )}
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={showContactoDialog} onOpenChange={(open) => { setShowContactoDialog(open); if (!open) { setEditingContacto(null); setContactoForm({ email: "", nombre: "", empresa: "", telefono: "", clase_cliente: "" }); } }}>
          <DialogContent>
            <DialogHeader><DialogTitle>{editingContacto ? "Editar Contacto" : "Agregar Contacto"}</DialogTitle></DialogHeader>
            <div className="space-y-4 py-4">
              <div><Label>Email *</Label><Input type="email" value={contactoForm.email} onChange={(e) => setContactoForm({ ...contactoForm, email: e.target.value })} placeholder="correo@ejemplo.com" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Nombre</Label><Input value={contactoForm.nombre} onChange={(e) => setContactoForm({ ...contactoForm, nombre: e.target.value })} placeholder="Nombre del contacto" /></div>
                <div><Label>Empresa</Label><Input value={contactoForm.empresa} onChange={(e) => setContactoForm({ ...contactoForm, empresa: e.target.value })} placeholder="Empresa u organización" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Teléfono</Label><Input value={contactoForm.telefono} onChange={(e) => setContactoForm({ ...contactoForm, telefono: e.target.value })} placeholder="809-000-0000" /></div>
                <div><Label>Clase Cliente</Label><Input value={contactoForm.clase_cliente} onChange={(e) => setContactoForm({ ...contactoForm, clase_cliente: e.target.value })} placeholder="Ej: VIP, Regular, Corporativo" /></div>
              </div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setShowContactoDialog(false)}>Cancelar</Button><Button onClick={handleAddContacto}>{editingContacto ? "Guardar Cambios" : "Agregar"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ===== Modal: Limpiar Espacios (admin only) ===== */}
        <Dialog open={showMigrationDialog} onOpenChange={(v) => { if (!migratingSpacing) setShowMigrationDialog(v); }}>
          <DialogContent className="max-w-lg" data-testid="migrate-spacing-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Eraser className="w-5 h-5 text-amber-600" />
                Limpiar espacios entre secciones
              </DialogTitle>
              <DialogDescription className="text-sm pt-2">
                Esta acción recorre TODOS los artículos guardados y elimina el padding por defecto entre secciones para que se vean iguales que los nuevos artículos.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 p-3 text-xs text-amber-900 dark:text-amber-200">
                <p className="font-semibold mb-1">¿Qué hace exactamente?</p>
                <ul className="list-disc pl-4 space-y-0.5">
                  <li>Pone <code>padding: 0</code> en todas las secciones (Texto, Imagen, Imagen+Texto, Botón, Divisor).</li>
                  <li><b>Conserva</b> las secciones <i>Espacio</i> (su padding es su propósito).</li>
                  <li><b>No toca</b> el padding interno de botones ni de header/footer.</li>
                  <li>Es <b>idempotente</b>: re-ejecutar no hace daño.</li>
                </ul>
              </div>
              {migrationResult && (
                <div className="rounded-md border border-emerald-200 bg-emerald-50 dark:bg-emerald-900/20 dark:border-emerald-800 p-3 text-xs text-emerald-900 dark:text-emerald-200 space-y-1">
                  <p className="font-semibold">✓ Migración completada</p>
                  <p>Artículos revisados: <b>{migrationResult.total_articulos}</b></p>
                  <p>Editor config actualizado: <b>{migrationResult.actualizados_editor_config}</b></p>
                  <p>HTML limpiado: <b>{migrationResult.actualizados_contenido_html}</b></p>
                  {migrationResult.errors?.length > 0 && (
                    <p className="text-red-700 dark:text-red-300">Errores: {migrationResult.errors.length}</p>
                  )}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowMigrationDialog(false)}
                disabled={migratingSpacing}
                data-testid="migrate-spacing-cancel-btn"
              >
                {migrationResult ? "Cerrar" : "Cancelar"}
              </Button>
              {!migrationResult && (
                <Button
                  onClick={handleMigrateRemoveSpacing}
                  disabled={migratingSpacing}
                  className="bg-amber-600 hover:bg-amber-500 text-white"
                  data-testid="migrate-spacing-confirm-btn"
                >
                  {migratingSpacing ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Ejecutando…</>
                  ) : (
                    <><Eraser className="w-4 h-4 mr-2" /> Ejecutar Migración</>
                  )}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={showEnvioDialog} onOpenChange={setShowEnvioDialog}>
          <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Enviar Artículo</DialogTitle></DialogHeader>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-3 py-1">
              <div className="space-y-3">
              <div><Label>Artículo *</Label><Select value={envioForm.articulo_id} onValueChange={(v) => setEnvioForm({ ...envioForm, articulo_id: v })}><SelectTrigger><SelectValue placeholder="Seleccionar artículo..." /></SelectTrigger><SelectContent>{articulos.map((a) => <SelectItem key={a.id} value={a.id}>{a.titulo}</SelectItem>)}</SelectContent></Select></div>

              {/* Enviar como imagen (render HTML → PNG inline) - PROMINENT POSITION */}
              <div className="border-2 border-purple-300 rounded-lg p-3 bg-purple-50 space-y-2">
                <div className="flex items-start gap-2">
                  <Checkbox
                    checked={envioForm.enviar_como_imagen}
                    onCheckedChange={(c) => setEnvioForm({ ...envioForm, enviar_como_imagen: !!c })}
                    id="enviar-como-imagen"
                    data-testid="enviar-como-imagen-checkbox"
                    className="mt-0.5"
                  />
                  <div className="flex-1">
                    <Label htmlFor="enviar-como-imagen" className="cursor-pointer flex items-center gap-1.5 font-semibold text-purple-900">
                      <ImageIcon className="w-4 h-4 text-purple-600" />
                      Enviar como imagen
                    </Label>
                    <p className="text-xs text-slate-700 mt-0.5">
                      Renderiza el contenido HTML como PNG y lo embebe en el correo.
                      Útil cuando los destinatarios bloquean HTML o el diseño se rompe en clientes antiguos.
                      Las aperturas y clicks se siguen rastreando.
                    </p>
                  </div>
                </div>
                {envioForm.enviar_como_imagen && (
                  <div className="grid grid-cols-2 gap-3 pt-2 pl-6">
                    <div>
                      <Label className="text-xs">Ancho de imagen (px)</Label>
                      <Input
                        type="number"
                        min={320}
                        max={1200}
                        value={envioForm.imagen_ancho}
                        onChange={(e) => setEnvioForm({ ...envioForm, imagen_ancho: parseInt(e.target.value || "600", 10) })}
                        data-testid="imagen-ancho-input"
                      />
                    </div>
                    <div className="flex items-center gap-2 pt-5">
                      <Checkbox
                        checked={envioForm.incluir_link_ver_online}
                        onCheckedChange={(c) => setEnvioForm({ ...envioForm, incluir_link_ver_online: !!c })}
                        id="incluir-link-online"
                      />
                      <Label htmlFor="incluir-link-online" className="cursor-pointer text-sm">
                        Incluir link "Ver en el navegador"
                      </Label>
                    </div>
                  </div>
                )}
              </div>

              <div><Label>Nombre del Remitente</Label><Input value={envioForm.nombre_remitente} onChange={(e) => setEnvioForm({ ...envioForm, nombre_remitente: e.target.value })} /><p className="text-xs text-slate-500 mt-1">Este nombre aparecerá como remitente en el correo del destinatario</p><p className="text-xs text-amber-600 mt-1"><strong>Nota:</strong> Si usa Microsoft 365/Office 365, el servidor puede mostrar el nombre de la cuenta de correo. Para cambiar esto, debe configurar un "alias de envío" en el Centro de Administración de Exchange.</p><Button variant="outline" size="sm" className="mt-2" onClick={handleTestEmail} disabled={testingEmail}>{testingEmail ? "Enviando..." : "Probar Nombre del Remitente"}</Button></div>
              </div>
              <div className="space-y-3">
              <div><div className="flex items-center justify-between mb-1"><Label>Listas de Contactos</Label><div className="flex gap-2"><Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEnvioForm({ ...envioForm, lista_ids: listas.map(l => l.id) })} data-testid="seleccionar-todas-listas-btn">Seleccionar todas</Button><Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEnvioForm({ ...envioForm, lista_ids: [] })} data-testid="deseleccionar-listas-btn">Deseleccionar</Button></div></div><div className="space-y-2 max-h-44 overflow-y-auto border rounded p-2">{listas.map((lista) => (<div key={lista.id} className="flex items-center gap-2"><Checkbox checked={envioForm.lista_ids.includes(lista.id)} onCheckedChange={(checked) => { if (checked) { setEnvioForm({ ...envioForm, lista_ids: [...envioForm.lista_ids, lista.id] }); } else { setEnvioForm({ ...envioForm, lista_ids: envioForm.lista_ids.filter(id => id !== lista.id) }); } }} /><span>{lista.nombre} ({lista.contactos_count || 0})</span></div>))}{listas.length === 0 && <p className="text-sm text-slate-500">No hay listas disponibles</p>}</div></div>
              <div className="space-y-2 border rounded p-2" data-testid="usuarios-sistema-section">
                <div className="flex items-center gap-2">
                  <Checkbox
                    data-testid="enviar-usuarios-sistema-checkbox"
                    checked={envioForm.enviar_usuarios_sistema}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        // Al activar, abrir el modal y preseleccionar todos por defecto
                        setEnvioForm({ ...envioForm, enviar_usuarios_sistema: true, usuarios_ids: usuariosSistema.map(u => u.id) });
                        setShowUsuariosModal(true);
                      } else {
                        setEnvioForm({ ...envioForm, enviar_usuarios_sistema: false, usuarios_ids: [] });
                      }
                    }}
                  />
                  <Label>Enviar a usuarios del sistema</Label>
                </div>
                {envioForm.enviar_usuarios_sistema && (
                  <div className="flex items-center justify-between pl-6">
                    <span className="text-xs text-slate-600" data-testid="usuarios-seleccionados-count">
                      {envioForm.usuarios_ids.length} de {usuariosSistema.length} usuarios seleccionados
                    </span>
                    <Button type="button" size="sm" variant="outline" onClick={() => setShowUsuariosModal(true)} data-testid="seleccionar-usuarios-btn">
                      <UserPlus className="w-4 h-4 mr-1" /> Seleccionar usuarios
                    </Button>
                  </div>
                )}
              </div>
              <div><Label>Emails adicionales (separados por coma o línea)</Label><Textarea value={envioForm.contactos_adicionales} onChange={(e) => setEnvioForm({ ...envioForm, contactos_adicionales: e.target.value })} placeholder="correo1@ejemplo.com, correo2@ejemplo.com" rows={2} /></div>
              <div className="flex items-center gap-2"><Checkbox checked={envioForm.programado} onCheckedChange={(checked) => setEnvioForm({ ...envioForm, programado: !!checked })} /><Label>Programar envío</Label></div>
              {envioForm.programado && <div><Label>Fecha y Hora Programada</Label><Input type="datetime-local" value={envioForm.fecha_programada} onChange={(e) => setEnvioForm({ ...envioForm, fecha_programada: e.target.value })} /></div>}
              <div className="pt-1">
                <Button type="button" variant="outline" size="sm" className="w-full border-blue-200 text-blue-700 hover:bg-blue-50" onClick={openDestinatariosPreview} data-testid="ver-destinatarios-preview-btn">
                  <Users className="w-4 h-4 mr-2" />
                  Ver destinatarios{(() => {
                    const listasSel = (envioForm.lista_ids || []).reduce((acc, id) => { const l = listas.find(x => x.id === id); return acc + (l?.contactos_count || 0); }, 0);
                    const adic = (envioForm.contactos_adicionales || "").split(/[,;\n]/).map(e => e.trim()).filter(e => e).length;
                    const sis = envioForm.enviar_usuarios_sistema ? (envioForm.usuarios_ids?.length || 0) : 0;
                    const tot = listasSel + adic + sis;
                    return tot > 0 ? ` (~${tot})` : "";
                  })()}
                </Button>
                <p className="text-xs text-slate-500 mt-1">Revisa a quiénes se enviará{envioForm.programado ? " (programado)" : ""}, agrupados por lista. El total real elimina correos duplicados.</p>
              </div>
              </div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setShowEnvioDialog(false)} data-testid="envio-cancel-btn">Cancelar</Button><Button onClick={handleEnviarArticulo} data-testid="envio-enviar-btn"><Send className="w-4 h-4 mr-2" />{envioForm.programado ? "Programar Envío" : "Enviar Ahora"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal de selección de usuarios del sistema */}
        <Dialog open={showUsuariosModal} onOpenChange={setShowUsuariosModal}>
          <DialogContent className="max-w-2xl" data-testid="usuarios-sistema-modal">
            <DialogHeader>
              <DialogTitle>Seleccionar usuarios del sistema</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Buscar usuario por nombre o email..."
                    value={searchUsuarioModal}
                    onChange={(e) => setSearchUsuarioModal(e.target.value)}
                    className="pl-9"
                    data-testid="usuarios-modal-search"
                  />
                </div>
                <Button type="button" size="sm" variant="outline" onClick={() => setEnvioForm({ ...envioForm, usuarios_ids: usuariosSistema.map(u => u.id) })} data-testid="seleccionar-todos-btn">
                  Seleccionar todos
                </Button>
                <Button type="button" size="sm" variant="outline" onClick={() => setEnvioForm({ ...envioForm, usuarios_ids: [] })} data-testid="deseleccionar-todos-btn">
                  Deseleccionar todos
                </Button>
              </div>
              <p className="text-xs text-slate-500" data-testid="usuarios-modal-count">
                {envioForm.usuarios_ids.length} de {usuariosSistema.length} seleccionados
              </p>
              <div className="max-h-80 overflow-y-auto border rounded-lg divide-y">
                {usuariosSistema
                  .filter((u) => {
                    const q = searchUsuarioModal.trim().toLowerCase();
                    if (!q) return true;
                    return (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q);
                  })
                  .map((u) => {
                    const checked = envioForm.usuarios_ids.includes(u.id);
                    return (
                      <label key={u.id} className="flex items-center gap-3 p-2 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800" data-testid={`usuario-row-${u.id}`}>
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(c) => {
                            if (c) setEnvioForm({ ...envioForm, usuarios_ids: [...envioForm.usuarios_ids, u.id] });
                            else setEnvioForm({ ...envioForm, usuarios_ids: envioForm.usuarios_ids.filter(id => id !== u.id) });
                          }}
                        />
                        <div className="flex flex-col leading-tight">
                          <span className="text-sm font-medium">{u.name || u.email}</span>
                          <span className="text-xs text-slate-500">{u.email}{u.departamento_nombre ? ` · ${u.departamento_nombre}` : ""}</span>
                        </div>
                      </label>
                    );
                  })}
                {usuariosSistema.length === 0 && (
                  <p className="text-center text-slate-500 py-4">No hay usuarios disponibles</p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button onClick={() => setShowUsuariosModal(false)} data-testid="usuarios-modal-confirmar-btn">
                Confirmar ({envioForm.usuarios_ids.length})
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal compartido: destinatarios agrupados (preview + segmentación de enviados) */}
        <Dialog open={destinatariosModal.open} onOpenChange={(o) => setDestinatariosModal((m) => ({ ...m, open: o }))}>
          <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="destinatarios-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-600" />
                {destinatariosModal.titulo}
              </DialogTitle>
              <DialogDescription>
                {destinatariosModal.loading ? "Cargando..." : `${destinatariosModal.total} destinatario(s) único(s) · ${destinatariosModal.grupos.length} grupo(s)`}
              </DialogDescription>
            </DialogHeader>
            {destinatariosModal.loading ? (
              <div className="py-10 text-center text-slate-500">Cargando destinatarios...</div>
            ) : destinatariosModal.sinEnvios ? (
              <div className="py-10 text-center text-slate-500">No se encontró la lista de destinatarios para este envío (envío antiguo sin registro de destinatarios).</div>
            ) : destinatariosModal.grupos.length === 0 ? (
              <div className="py-10 text-center text-slate-500">No hay destinatarios para mostrar.</div>
            ) : (
              <div className="space-y-3">
                <Input
                  placeholder="Filtrar por nombre o correo..."
                  value={destinatariosSearch}
                  onChange={(e) => setDestinatariosSearch(e.target.value)}
                  data-testid="destinatarios-search-input"
                />
                {destinatariosModal.grupos.map((g) => {
                  const q = destinatariosSearch.trim().toLowerCase();
                  const filtered = q
                    ? g.contactos.filter(c => (c.nombre || "").toLowerCase().includes(q) || (c.email || "").toLowerCase().includes(q))
                    : g.contactos;
                  if (q && filtered.length === 0) return null;
                  const color = g.tipo === "sistema" ? "text-indigo-700 bg-indigo-50 border-indigo-200"
                    : g.tipo === "adicionales" ? "text-amber-700 bg-amber-50 border-amber-200"
                    : "text-blue-700 bg-blue-50 border-blue-200";
                  return (
                    <details key={g.id} open className="border rounded-lg" data-testid={`destinatarios-grupo-${g.id}`}>
                      <summary className={`cursor-pointer select-none px-3 py-2 flex items-center justify-between rounded-t-lg border-b ${color}`}>
                        <span className="font-semibold text-sm">{g.nombre}</span>
                        <Badge variant="outline" className="ml-2">{filtered.length}{q ? ` / ${g.total}` : ""}</Badge>
                      </summary>
                      <div className="max-h-56 overflow-y-auto divide-y">
                        {filtered.length === 0 ? (
                          <p className="px-3 py-2 text-sm text-slate-400">Sin contactos</p>
                        ) : filtered.map((c, i) => (
                          <div key={`${c.email}-${i}`} className="px-3 py-1.5 text-sm flex items-center justify-between gap-3">
                            <span className="truncate">{c.nombre || <span className="text-slate-400">—</span>}{c.empresa ? <span className="text-slate-400"> · {c.empresa}</span> : null}</span>
                            <span className="text-slate-600 truncate">{c.email}</span>
                          </div>
                        ))}
                      </div>
                    </details>
                  );
                })}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setDestinatariosModal((m) => ({ ...m, open: false }))} data-testid="destinatarios-close-btn">Cerrar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>


        {/* Confirmation preview before sending */}
        <Dialog open={showEnvioPreview} onOpenChange={setShowEnvioPreview}>
          <DialogContent className="max-w-md" data-testid="envio-preview-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Send className="w-5 h-5 text-blue-600" />
                Confirmar envío
              </DialogTitle>
              <DialogDescription>Revisa el desglose antes de enviar.</DialogDescription>
            </DialogHeader>

            {envioPreviewLoading || !envioPreview ? (
              <div className="py-10 flex flex-col items-center justify-center gap-3 text-gray-500" data-testid="envio-preview-loading">
                <Loader2 className="w-7 h-7 animate-spin" />
                <span className="text-sm">Calculando destinatarios…</span>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg border border-gray-200 p-3 text-center">
                    <p className="text-xs text-gray-500">Total destinatarios</p>
                    <p className="text-2xl font-bold text-gray-800" data-testid="preview-total">{envioPreview.total_destinatarios}</p>
                  </div>
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-center">
                    <p className="text-xs text-emerald-700">Se enviarán</p>
                    <p className="text-2xl font-bold text-emerald-700" data-testid="preview-a-enviar">{envioPreview.a_enviar}</p>
                  </div>
                </div>

                {envioPreview.excluidos_total > 0 && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-1.5" data-testid="preview-excluidos">
                    <p className="text-sm font-semibold text-amber-800">
                      {envioPreview.excluidos_total} NO se enviarán (excluidos)
                    </p>
                    {envioPreview.bloqueados_unsubscribed > 0 && (
                      <p className="text-xs text-amber-700 flex justify-between">
                        <span>• Desuscritos</span><b>{envioPreview.bloqueados_unsubscribed}</b>
                      </p>
                    )}
                    {envioPreview.bloqueados_blocklist > 0 && (
                      <p className="text-xs text-amber-700 flex justify-between">
                        <span>• No Deseados (lista negra)</span><b>{envioPreview.bloqueados_blocklist}</b>
                      </p>
                    )}
                  </div>
                )}

                <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800" data-testid="preview-bloques">
                  {envioPreview.a_enviar > 0 ? (
                    envioPreview.throttle_enabled && envioPreview.bloques > 1 ? (
                      <span>
                        Se enviará en <b>{envioPreview.bloques} bloques</b> de hasta {envioPreview.batch_size} correos
                        {envioPreview.batch_pause_seconds > 0 && <>, con pausas de {envioPreview.batch_pause_seconds}s entre bloques (anti-spam).</>}
                      </span>
                    ) : (
                      <span>Se enviará en <b>1 bloque</b> de {envioPreview.a_enviar} correo(s).</span>
                    )
                  ) : (
                    <span className="text-red-700 font-medium">No hay destinatarios válidos para enviar. Revisa las listas "No Deseados" y "Desuscritos".</span>
                  )}
                </div>

                {!envioPreview.smtp_configurado && (
                  <p className="text-xs text-red-600" data-testid="preview-smtp-warning">⚠️ El SMTP de Marketing no está configurado/habilitado.</p>
                )}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEnvioPreview(false)} disabled={enviando} data-testid="preview-cancel-btn">Cancelar</Button>
              <Button
                onClick={() => performEnviarArticulo(envioPreview._payload)}
                disabled={enviando || envioPreviewLoading || !envioPreview || envioPreview.a_enviar <= 0}
                data-testid="preview-confirm-btn"
              >
                {enviando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                {enviando ? "Enviando…" : `Confirmar y enviar (${envioPreview ? envioPreview.a_enviar : 0})`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={showEstadisticasDialog} onOpenChange={setShowEstadisticasDialog}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 justify-between">
                <span className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-blue-600" />
                  Estadísticas: {estadisticasArticulo?.articulo?.titulo}
                </span>
                {estadisticasArticulo?.articulo && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600 border-red-200 hover:bg-red-50 mr-10"
                    onClick={() => handleResetEstadisticas(estadisticasArticulo.articulo)}
                    data-testid="reset-estadisticas-btn"
                    title="Borrar todas las estadísticas de este artículo"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Borrar estadísticas
                  </Button>
                )}
              </DialogTitle>
              <DialogDescription>
                Haz clic en los números para ver el detalle de correos
              </DialogDescription>
            </DialogHeader>
            {estadisticasArticulo && (
              <div className="space-y-6 py-4">
                {/* Tarjetas de resumen - CLICKEABLES */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <Card 
                    className="cursor-pointer hover:ring-2 hover:ring-green-400 transition-all"
                    onClick={() => handleViewDetalleCorreosArticulo('enviados')}
                  >
                    <CardContent className="p-4 text-center">
                      <Send className="w-6 h-6 mx-auto mb-2 text-green-600" />
                      <p className="text-2xl font-bold">{estadisticasArticulo.resumen?.total_enviados || 0}</p>
                      <p className="text-sm text-slate-500">Enviados</p>
                      <p className="text-xs text-green-500 mt-1">Click para ver lista</p>
                    </CardContent>
                  </Card>
                  <Card 
                    className="cursor-pointer hover:ring-2 hover:ring-purple-400 transition-all"
                    onClick={() => handleViewDetalleCorreosArticulo('abiertos')}
                  >
                    <CardContent className="p-4 text-center">
                      <Eye className="w-6 h-6 mx-auto mb-2 text-purple-600" />
                      <p className="text-2xl font-bold">{estadisticasArticulo.resumen?.total_abiertos || 0}</p>
                      <p className="text-sm text-slate-500">Abiertos ({estadisticasArticulo.resumen?.tasa_apertura || 0}%)</p>
                      <p className="text-xs text-purple-500 mt-1">Click para ver lista</p>
                    </CardContent>
                  </Card>
                  <Card 
                    className="cursor-pointer hover:ring-2 hover:ring-amber-400 transition-all"
                    onClick={() => handleViewDetalleCorreosArticulo('no_abiertos')}
                    data-testid="card-no-abiertos"
                  >
                    <CardContent className="p-4 text-center">
                      <Mail className="w-6 h-6 mx-auto mb-2 text-amber-600" />
                      <p className="text-2xl font-bold">
                        {Math.max(0, (estadisticasArticulo.resumen?.total_enviados || 0) - (estadisticasArticulo.resumen?.total_abiertos || 0))}
                      </p>
                      <p className="text-sm text-slate-500">No Abiertos</p>
                      <p className="text-xs text-amber-500 mt-1">Click para ver lista</p>
                    </CardContent>
                  </Card>
                  <Card 
                    className="cursor-pointer hover:ring-2 hover:ring-blue-400 transition-all"
                    onClick={() => handleViewDetalleCorreosArticulo('clicks')}
                  >
                    <CardContent className="p-4 text-center">
                      <MousePointer className="w-6 h-6 mx-auto mb-2 text-blue-600" />
                      <p className="text-2xl font-bold">{estadisticasArticulo.resumen?.total_clicks || 0}</p>
                      <p className="text-sm text-slate-500">Clicks ({estadisticasArticulo.resumen?.tasa_clicks || 0}%)</p>
                      <p className="text-xs text-blue-500 mt-1">Click para ver lista</p>
                    </CardContent>
                  </Card>
                  <Card 
                    className="cursor-pointer hover:ring-2 hover:ring-red-400 transition-all"
                    onClick={() => handleViewDetalleCorreosArticulo('rebotados')}
                  >
                    <CardContent className="p-4 text-center">
                      <AlertTriangle className="w-6 h-6 mx-auto mb-2 text-red-600" />
                      <p className="text-2xl font-bold">{estadisticasArticulo.resumen?.total_rebotados || 0}</p>
                      <p className="text-sm text-slate-500">Rebotados ({estadisticasArticulo.resumen?.tasa_rebote || 0}%)</p>
                      <p className="text-xs text-red-500 mt-1">Click para ver lista</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Banner informativo: envíos PROGRAMADOS pendientes (no cuentan como enviados) */}
                {(estadisticasArticulo.resumen?.total_programados || 0) > 0 && (
                  <div
                    className="flex items-center gap-3 rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3 dark:border-indigo-800 dark:bg-indigo-950/40"
                    data-testid="programados-pendientes-banner"
                  >
                    <Clock className="w-5 h-5 text-indigo-600 shrink-0" />
                    <div className="text-sm">
                      <span className="font-semibold text-indigo-700 dark:text-indigo-300">
                        {estadisticasArticulo.resumen.total_programados} envío{estadisticasArticulo.resumen.total_programados > 1 ? "s" : ""} programado{estadisticasArticulo.resumen.total_programados > 1 ? "s" : ""} pendiente{estadisticasArticulo.resumen.total_programados > 1 ? "s" : ""}
                      </span>
                      <span className="text-indigo-600 dark:text-indigo-400">
                        {" "}· {estadisticasArticulo.resumen.total_destinatarios_programados || 0} destinatario{(estadisticasArticulo.resumen.total_destinatarios_programados || 0) > 1 ? "s" : ""} agendado{(estadisticasArticulo.resumen.total_destinatarios_programados || 0) > 1 ? "s" : ""}
                        {estadisticasArticulo.resumen.proxima_fecha_programada && (
                          <> · se enviará el {new Date(estadisticasArticulo.resumen.proxima_fecha_programada).toLocaleString("es-DO", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}</>
                        )}
                      </span>
                      <p className="text-xs text-indigo-500 mt-0.5">Aún no se ha enviado; no se cuenta en las estadísticas de arriba.</p>
                    </div>
                  </div>
                )}

                {/* Destinatarios por lista (segmentación de lo enviado) */}
                <div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-blue-200 text-blue-700 hover:bg-blue-50"
                    onClick={() => openDestinatariosEnviados(estadisticasArticulo.articulo?.id, estadisticasArticulo.articulo?.titulo)}
                    data-testid="ver-destinatarios-enviados-btn"
                  >
                    <Users className="w-4 h-4 mr-2" />
                    Ver destinatarios por lista
                  </Button>
                  <span className="text-xs text-slate-500 ml-2">A quiénes se les envió, agrupados por la lista de origen.</span>
                </div>


                {/* ===== Barra de progreso de envío ===== */}
                {/* Visible siempre que haya destinatarios objetivo conocidos.
                    Muestra el % de avance del envío inicial. Si hay envío
                    activo (estado enviando / pausa_lote), muestra estado
                    en vivo (lote actual, tiempo restante de pausa). */}
                {((estadisticasArticulo.resumen?.total_destinatarios || 0) > 0) && (() => {
                  const r = estadisticasArticulo.resumen || {};
                  const total = r.envio_activo?.total_destinatarios || r.total_destinatarios || 0;
                  // Conteos PROPIOS del envío inicial (no del agregado de campaña),
                  // para que esta barra y su detalle muestren lo que pasó en ESTE envío.
                  const enviados = (r.envio_activo?.enviados) ?? (r.total_enviados || 0);
                  const fallidos = (r.envio_activo?.fallidos) || 0;
                  const bloqueados = (r.envio_activo?.bloqueados) || 0;
                  const rebotadosProg = (r.envio_activo?.rebotados) || 0;
                  // No enviados = TODO lo que no se entregó en este envío: fallidos
                  // (errores SMTP) + bloqueados (desuscritos / no deseados) +
                  // rebotados (bounced). Coincide con el detalle de ESTE envío.
                  const noEnviados = fallidos + bloqueados + rebotadosProg;
                  const procesados = enviados + fallidos + bloqueados + rebotadosProg;
                  const pct = total > 0 ? Math.min(100, Math.round((procesados / total) * 100)) : 0;
                  const estado = r.envio_activo?.estado || "completado";
                  const enProgreso = ["enviando", "pausa_lote", "programado", "preparando", "en_cola"].includes(estado);
                  const interrumpido = estado === "interrumpido";
                  const completo = procesados >= total;
                  // Se puede reanudar si quedan pendientes (procesados < total) y
                  // el envío no fue cancelado. NO dependemos de estado==="completado"
                  // porque un envío cortado pudo quedar mal marcado como completado;
                  // si aún faltan destinatarios, debe poder reanudarse.
                  const puedeReanudar = !completo && estado !== "cancelado";
                  const envioActivoId = r.envio_activo?.id;
                  // Color según estado
                  const barColor = completo
                    ? "bg-emerald-500"
                    : interrumpido
                      ? "bg-red-500"
                      : estado === "pausa_lote"
                        ? "bg-amber-500"
                        : "bg-blue-500";
                  const labelColor = completo
                    ? "text-emerald-700"
                    : interrumpido
                      ? "text-red-700"
                      : estado === "pausa_lote"
                        ? "text-amber-700"
                        : "text-blue-700";
                  // Pausa countdown
                  let pausaInfo = null;
                  if (estado === "pausa_lote" && r.envio_activo?.lote_pausa_hasta) {
                    const ms = new Date(r.envio_activo.lote_pausa_hasta).getTime() - readNowMs();
                    if (ms > 0) {
                      const sec = Math.ceil(ms / 1000);
                      pausaInfo = `Esperando lote · reanuda en ${sec}s`;
                    }
                  }
                  // Velocidad y tiempo estimado (ETA). Promedio real incluyendo
                  // pausas: procesados / tiempo transcurrido desde el inicio.
                  let etaInfo = null;
                  if (
                    enProgreso && estado !== "programado" &&
                    r.envio_activo?.started_at && procesados > 0 && procesados < total
                  ) {
                    const startMs = new Date(r.envio_activo.started_at).getTime();
                    const elapsedSec = Math.max(1, (readNowMs() - startMs) / 1000);
                    const ratePerSec = procesados / elapsedSec;
                    if (ratePerSec > 0) {
                      const restantes = Math.max(0, total - procesados);
                      const etaSec = Math.round(restantes / ratePerSec);
                      const ratePerMin = Math.max(1, Math.round(ratePerSec * 60));
                      const fmtDur = (s) => {
                        if (s < 60) return `${s}s`;
                        const m = Math.floor(s / 60);
                        const sec = s % 60;
                        if (m < 60) return `${m}m ${sec}s`;
                        const h = Math.floor(m / 60);
                        const mm = m % 60;
                        return `${h}h ${mm}m`;
                      };
                      etaInfo = { ratePerMin, etaText: fmtDur(etaSec) };
                    }
                  }
                  return (
                    <Card className="border-slate-200" data-testid="envio-progress-card">
                      <CardContent className="p-4 space-y-2">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-2">
                            <Activity className={`w-4 h-4 ${labelColor}`} />
                            <span className={`text-sm font-medium ${labelColor}`}>
                              {completo
                                ? "Envío completado"
                                : interrumpido
                                  ? "Envío interrumpido"
                                  : enProgreso
                                    ? "Envío en progreso"
                                    : "Progreso de envío"}
                            </span>
                            {r.envio_activo?.lote_actual && estado === "pausa_lote" && (
                              <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700">
                                Lote {r.envio_activo.lote_actual}
                              </Badge>
                            )}
                          </div>
                          <span className="text-sm font-bold text-slate-700" data-testid="envio-progress-percent">
                            {pct}%
                          </span>
                        </div>
                        <div className="w-full h-3 rounded-full bg-slate-100 overflow-hidden">
                          <div
                            className={`h-full ${barColor} transition-all duration-500 rounded-full`}
                            style={{ width: `${pct}%` }}
                            data-testid="envio-progress-bar"
                          />
                        </div>
                        <div className="flex items-center justify-between text-xs text-slate-500">
                          <span data-testid="envio-progress-count">
                            <span className="font-semibold text-slate-700">{Math.min(procesados, total).toLocaleString()}</span>
                            {" / "}
                            <span className="font-semibold text-slate-700">{total.toLocaleString()}</span>
                            {" correos procesados"}
                            {fallidos > 0 && (
                              <span className="text-red-600 ml-1">({fallidos.toLocaleString()} fallidos)</span>
                            )}
                            {bloqueados > 0 && (
                              <span className="text-amber-600 ml-1">({bloqueados.toLocaleString()} bloqueados)</span>
                            )}
                          </span>
                          <span className="text-slate-400">
                            {pausaInfo ? pausaInfo : `Restan ${Math.max(0, total - procesados).toLocaleString()}`}
                          </span>
                        </div>
                        {etaInfo && (
                          <div
                            className="flex items-center gap-4 text-xs bg-blue-50 border border-blue-100 rounded-md px-2.5 py-1.5"
                            data-testid="envio-eta-info"
                          >
                            <span className="flex items-center gap-1.5 text-blue-700 font-medium" data-testid="envio-speed">
                              <Gauge className="w-3.5 h-3.5" />
                              {etaInfo.ratePerMin.toLocaleString()} correos/min
                            </span>
                            <span className="flex items-center gap-1.5 text-slate-600" data-testid="envio-eta">
                              <Clock className="w-3.5 h-3.5" />
                              Tiempo restante aprox.: <span className="font-semibold text-slate-800">{etaInfo.etaText}</span>
                            </span>
                          </div>
                        )}
                        {noEnviados > 0 && (
                          <button
                            type="button"
                            onClick={() => { setCurrentEnvioId(r.envio_activo?.id || null); handleViewDetalleCorreos('fallidos', null, r.envio_activo?.id); }}
                            className="text-xs text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                            data-testid="ver-no-enviados-btn"
                          >
                            <AlertCircle className="w-3.5 h-3.5" />
                            Ver los {noEnviados.toLocaleString()} que no se enviaron y el motivo
                          </button>
                        )}
                        {enProgreso && envioActivoId && estado !== "programado" && (
                          <div className="pt-2 border-t border-slate-100 flex items-center justify-between gap-3" data-testid="envio-control-activo">
                            <p className="text-xs text-slate-600">
                              Puedes <span className="font-medium text-amber-700">pausar</span> el envío (se reanuda desde donde quedó) o <span className="font-medium text-red-700">detenerlo</span> por completo.
                            </p>
                            <div className="flex gap-2 shrink-0">
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-amber-300 text-amber-700 hover:bg-amber-50"
                                onClick={() => handlePausarEnvio(envioActivoId, refreshStatsModal)}
                                disabled={controlEnvioId === envioActivoId}
                                data-testid="pausar-envio-btn"
                              >
                                <Pause className="w-3.5 h-3.5 mr-1.5" /> Pausar
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-red-300 text-red-700 hover:bg-red-50"
                                onClick={() => handleDetenerEnvio(envioActivoId, refreshStatsModal)}
                                disabled={controlEnvioId === envioActivoId}
                                data-testid="detener-envio-btn"
                              >
                                <Ban className="w-3.5 h-3.5 mr-1.5" /> Detener
                              </Button>
                            </div>
                          </div>
                        )}
                        {puedeReanudar && envioActivoId && (
                          <div className="pt-2 border-t border-slate-100 flex items-center justify-between gap-3">
                            <p className={`text-xs ${interrumpido ? "text-red-700 font-medium" : "text-slate-600"}`}>
                              {interrumpido
                                ? "⚠ El envío se interrumpió (sin actividad por más de 15 min). Reanudalo desde donde quedó."
                                : "¿El envío se detuvo o cayó por reinicio? Reanudalo desde donde quedó."}
                            </p>
                            <div className="flex gap-2 shrink-0">
                            <Button
                              size="sm"
                              variant="outline"
                              className={interrumpido
                                ? "border-red-300 text-red-700 hover:bg-red-50"
                                : "border-blue-300 text-blue-700 hover:bg-blue-50"}
                              onClick={() => handleReanudarEnvio(envioActivoId)}
                              disabled={reanudandoEnvioId === envioActivoId}
                              data-testid="reanudar-envio-btn"
                            >
                              {reanudandoEnvioId === envioActivoId ? (
                                <>
                                  <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                                  Reanudando…
                                </>
                              ) : (
                                <>
                                  <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                                  {estado === "pausado" ? "Reanudar" : "Volver a enviar"}
                                </>
                              )}
                            </Button>
                            {(estado === "pausado" || interrumpido) && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-red-300 text-red-700 hover:bg-red-50"
                                onClick={() => handleDetenerEnvio(envioActivoId, refreshStatsModal)}
                                disabled={controlEnvioId === envioActivoId}
                                data-testid="detener-envio-btn-2"
                              >
                                <Ban className="w-3.5 h-3.5 mr-1.5" /> Detener
                              </Button>
                            )}
                            </div>
                          </div>
                        )}
                        {envioActivoId && !enProgreso && (
                          <div className="pt-2 border-t border-slate-100 flex items-center justify-between gap-3">
                            <p className="text-xs text-slate-600">
                              ¿Los números no cuadran (p. ej. "Enviados" incluye rebotados/bloqueados, o "Rebotados" en 0)? Repara los contadores con los registros reales. No reenvía nada.
                            </p>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 shrink-0"
                              onClick={() => handleReconciliarEnvio(envioActivoId)}
                              disabled={reconciliandoEnvioId === envioActivoId}
                              data-testid="reparar-contadores-btn"
                            >
                              {reconciliandoEnvioId === envioActivoId ? (
                                <>
                                  <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                                  Reparando…
                                </>
                              ) : (
                                <>
                                  <Activity className="w-3.5 h-3.5 mr-1.5" />
                                  Reparar contadores
                                </>
                              )}
                            </Button>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })()}
                {(estadisticasArticulo.resumen?.reenvios_progreso || []).map((rv) => {
                  const total = rv.total_destinatarios || 0;
                  const enviados = rv.enviados || 0;
                  const fallidos = rv.fallidos || 0;
                  const bloqueados = rv.bloqueados || 0;
                  const noEnviados = fallidos + bloqueados;
                  const procesados = enviados + fallidos + bloqueados + (rv.rebotados || 0);
                  const estado = rv.estado || "completado";
                  const pct = total > 0 ? Math.min(100, Math.round((procesados / total) * 100)) : 0;
                  const enProgreso = ["enviando", "pausa_lote", "preparando", "en_cola"].includes(estado);
                  const programado = estado === "programado";
                  const interrumpido = estado === "interrumpido";
                  const completo = estado === "completado";
                  const barColor = completo ? "bg-emerald-500" : interrumpido ? "bg-red-500" : programado ? "bg-purple-400" : enProgreso ? "bg-blue-500" : "bg-slate-400";
                  const labelColor = completo ? "text-emerald-700" : interrumpido ? "text-red-700" : programado ? "text-purple-700" : "text-blue-700";
                  let fechaProgTxt = "";
                  if (programado && rv.fecha_programada) {
                    try { fechaProgTxt = new Date(rv.fecha_programada).toLocaleString("es-DO", { dateStyle: "medium", timeStyle: "short" }); } catch (e) {}
                  }
                  return (
                    <Card key={rv.id} className="border-amber-200" data-testid={`reenvio-progress-card-${rv.id}`}>
                      <CardContent className="p-4 space-y-2">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-2">
                            <RefreshCw className={`w-4 h-4 ${labelColor}`} />
                            <span className={`text-sm font-medium ${labelColor}`}>
                              {programado ? "Reenvío programado" : completo ? "Reenvío completado" : interrumpido ? "Reenvío interrumpido" : enProgreso ? "Reenvío en progreso" : "Progreso de reenvío"}
                            </span>
                            <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700">No abiertos</Badge>
                          </div>
                          {!programado && (
                            <span className="text-sm font-bold text-slate-700">{pct}%</span>
                          )}
                        </div>
                        {programado ? (
                          <p className="text-xs text-purple-700 flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            Se enviará el {fechaProgTxt || "(fecha programada)"} sólo a quienes no hayan abierto hasta ese momento.
                          </p>
                        ) : (
                          <>
                            <div className="w-full h-3 rounded-full bg-slate-100 overflow-hidden">
                              <div className={`h-full ${barColor} transition-all duration-500 rounded-full`} style={{ width: `${pct}%` }} />
                            </div>
                            <div className="flex items-center justify-between text-xs text-slate-500">
                              <span>
                                <span className="font-semibold text-slate-700">{procesados.toLocaleString()}</span>
                                {" / "}
                                <span className="font-semibold text-slate-700">{total.toLocaleString()}</span>
                                {" correos procesados"}
                                {fallidos > 0 && (<span className="text-red-600 ml-1">({fallidos.toLocaleString()} fallidos)</span>)}
                                {bloqueados > 0 && (<span className="text-amber-600 ml-1">({bloqueados.toLocaleString()} bloqueados)</span>)}
                              </span>
                              <span className="text-slate-400">{`Restan ${(total - procesados).toLocaleString()}`}</span>
                            </div>
                            {noEnviados > 0 && (
                              <button
                                type="button"
                                onClick={() => { setCurrentEnvioId(rv.id); handleViewDetalleCorreos('fallidos', null, rv.id); }}
                                className="text-xs text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                                data-testid={`reenvio-ver-no-enviados-${rv.id}`}
                              >
                                <AlertCircle className="w-3.5 h-3.5" />
                                Ver los {noEnviados.toLocaleString()} que no se enviaron y el motivo
                              </button>
                            )}
                          </>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
                {((estadisticasArticulo.resumen?.total_enviados || 0) - (estadisticasArticulo.resumen?.total_abiertos || 0)) > 0 && (
                  <Card className="border-amber-200 bg-amber-50/50">
                    <CardContent className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                          <RefreshCw className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-700">
                            {(estadisticasArticulo.resumen?.total_enviados || 0) - (estadisticasArticulo.resumen?.total_abiertos || 0)} contactos no abrieron este articulo
                          </p>
                          <p className="text-xs text-slate-500">Reenvía a quienes no abrieron y a los fallidos/rebotados (con el correo corregido en la lista)</p>
                          {(estadisticasArticulo.resumen?.total_reenvios || 0) > 0 && (
                            <p className="text-xs font-semibold text-amber-700 mt-1" data-testid="total-reenvios-info">
                              <RefreshCw className="w-3 h-3 inline mr-1" />
                              Este artículo ya tuvo {estadisticasArticulo.resumen.total_reenvios} reenvío{estadisticasArticulo.resumen.total_reenvios !== 1 ? "s" : ""} previo{estadisticasArticulo.resumen.total_reenvios !== 1 ? "s" : ""} (no afectan la tasa de apertura)
                            </p>
                          )}
                        </div>
                      </div>
                      {!soloEstadisticas && (
                      <Button
                        variant="default"
                        className="bg-amber-600 hover:bg-amber-700"
                        onClick={() => setShowReenvioConfirm(true)}
                        data-testid="resend-no-abiertos-btn"
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Reenviar a No Abiertos
                      </Button>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Gráfico de Distribución por Dominio */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Globe className="w-5 h-5 text-blue-600" />
                      Segmentación por Dominio de Correo
                      {loadingArticuloDomainStats && <RefreshCw className="w-4 h-4 animate-spin" />}
                    </CardTitle>
                    <p className="text-sm text-slate-500">Distribución de destinatarios por proveedor de correo</p>
                  </CardHeader>
                  <CardContent>
                    {articuloDomainStats?.dominios_grafico?.length > 0 ? (
                      <div className="space-y-4">
                        {/* Barras horizontales */}
                        <div className="space-y-3">
                          {articuloDomainStats.dominios_grafico.map((item, idx) => {
                            const colors = [
                              "bg-blue-500", "bg-green-500", "bg-purple-500", "bg-amber-500",
                              "bg-red-500", "bg-cyan-500", "bg-pink-500", "bg-indigo-500",
                              "bg-teal-500", "bg-orange-500", "bg-slate-400"
                            ];
                            const color = colors[idx % colors.length];
                            return (
                              <div 
                                key={item.dominio}
                                className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 p-2 rounded-lg -mx-2 transition-colors"
                                onClick={() => item.dominio !== 'otros' && handleViewDetalleCorreosArticulo('enviados', item.dominio)}
                              >
                                <div className="flex justify-between text-sm mb-1">
                                  <span className="font-medium flex items-center gap-1">
                                    <Globe className="w-3 h-3" />
                                    @{item.dominio}
                                  </span>
                                  <span className="text-slate-500">{item.total} ({item.porcentaje}%)</span>
                                </div>
                                <div className="w-full bg-slate-100 dark:bg-slate-800 rounded-full h-4">
                                  <div 
                                    className={`${color} h-4 rounded-full transition-all duration-500 flex items-center justify-end pr-2`}
                                    style={{ width: `${Math.max(item.porcentaje, 3)}%` }}
                                  >
                                    {item.porcentaje > 10 && <span className="text-white text-xs font-medium">{item.porcentaje}%</span>}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        
                        {/* Resumen de dominios */}
                        <div className="flex items-center justify-between pt-3 border-t text-sm">
                          <span className="text-slate-500">
                            <strong>{articuloDomainStats.resumen?.total_dominios_unicos || 0}</strong> dominios únicos
                          </span>
                          <span className="text-slate-500">
                            Dominio principal: <strong>@{articuloDomainStats.resumen?.dominio_principal}</strong> ({articuloDomainStats.resumen?.porcentaje_principal}%)
                          </span>
                        </div>
                      </div>
                    ) : (
                      <p className="text-center text-slate-500 py-4">No hay datos de dominios disponibles</p>
                    )}
                  </CardContent>
                </Card>

                {/* Tabla detallada por dominio */}
                {articuloDomainStats?.dominios?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Métricas por Dominio</CardTitle>
                      <p className="text-xs text-slate-500">Haz clic en los números para ver el detalle</p>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-100 dark:bg-slate-800">
                            <tr>
                              <th className="p-2 text-left">Dominio</th>
                              <th className="p-2 text-center">Total</th>
                              <th className="p-2 text-center">Enviados</th>
                              <th className="p-2 text-center">Abiertos</th>
                              <th className="p-2 text-center">Tasa Apertura</th>
                              <th className="p-2 text-center">Rebotados</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {articuloDomainStats.dominios.slice(0, 10).map((d) => (
                              <tr key={d.dominio} className="hover:bg-slate-50">
                                <td className="p-2">
                                  <button 
                                    className="font-medium hover:text-blue-600"
                                    onClick={() => handleViewDetalleCorreosArticulo('enviados', d.dominio)}
                                  >
                                    @{d.dominio}
                                  </button>
                                </td>
                                <td className="p-2 text-center">{d.total}</td>
                                <td className="p-2 text-center">
                                  <button 
                                    className="text-green-600 hover:underline"
                                    onClick={() => handleViewDetalleCorreosArticulo('enviados', d.dominio)}
                                  >
                                    {d.enviados}
                                  </button>
                                </td>
                                <td className="p-2 text-center">
                                  <button 
                                    className="text-purple-600 hover:underline"
                                    onClick={() => handleViewDetalleCorreosArticulo('abiertos', d.dominio)}
                                  >
                                    {d.abiertos}
                                  </button>
                                </td>
                                <td className="p-2 text-center">
                                  <Badge className={
                                    d.tasa_apertura >= 50 ? "bg-green-500" :
                                    d.tasa_apertura >= 20 ? "bg-amber-500" : "bg-red-500"
                                  }>
                                    {d.tasa_apertura}%
                                  </Badge>
                                </td>
                                <td className="p-2 text-center">
                                  <button 
                                    className={d.rebotados > 0 ? "text-red-600 hover:underline font-medium" : "text-slate-400"}
                                    onClick={() => d.rebotados > 0 && handleViewDetalleCorreosArticulo('rebotados', d.dominio)}
                                  >
                                    {d.rebotados}
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Clicks por URL */}
                {Object.keys(estadisticasArticulo.clicks_por_url || {}).length > 0 && (
                  <Card>
                    <CardHeader><CardTitle className="text-lg">Clicks por URL</CardTitle></CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {Object.entries(estadisticasArticulo.clicks_por_url).map(([url, count]) => (
                          <div key={url} className="flex justify-between items-center p-2 bg-slate-50 dark:bg-slate-800 rounded">
                            <span className="text-sm truncate max-w-md">{url}</span>
                            <Badge>{count} clicks</Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* ===== INTERACCIÓN A LO LARGO DEL TIEMPO ===== */}
                <InteractionTimelineCard
                  data={timelineData}
                  loading={loadingTimeline}
                  rango={timelineRango}
                  onRangoChange={handleChangeTimelineRango}
                />

                {/* ===== ENGAGEMENT STATS: Browser, Device, Geo (Aperturas / Clicks) ===== */}
                {loadingEngagement ? (
                  <Card>
                    <CardContent className="p-8 text-center">
                      <RefreshCw className="w-6 h-6 animate-spin mx-auto text-slate-400 mb-2" />
                      <p className="text-sm text-slate-500">Cargando estadisticas de engagement...</p>
                    </CardContent>
                  </Card>
                ) : engagementStats && (engagementStats.aperturas?.total > 0 || engagementStats.clicks?.total > 0 || engagementStats.total_abiertos_con_datos > 0) ? (
                  <>
                    {/* Toggle Aperturas / Clicks */}
                    <div className="flex items-center justify-between gap-4 flex-wrap" data-testid="engagement-toggle-row">
                      <div>
                        <h3 className="text-lg font-semibold flex items-center gap-2">
                          <BarChart3 className="w-5 h-5 text-blue-600" />
                          Segmentación por Navegador / Cliente / Dispositivo
                        </h3>
                        <p className="text-sm text-slate-500">
                          Ve cómo se distribuyen las {engagementMode === "aperturas" ? "aperturas" : "clicks"} por plataforma
                        </p>
                      </div>
                      <div className="inline-flex rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 p-1 gap-1" role="tablist">
                        <button
                          type="button"
                          role="tab"
                          aria-selected={engagementMode === "aperturas"}
                          onClick={() => setEngagementMode("aperturas")}
                          data-testid="engagement-mode-aperturas"
                          className={`px-4 py-1.5 text-sm font-medium rounded-md transition ${
                            engagementMode === "aperturas"
                              ? "bg-white dark:bg-slate-900 text-blue-700 dark:text-blue-300 shadow-sm"
                              : "text-slate-600 dark:text-slate-300 hover:text-slate-900"
                          }`}
                        >
                          <Eye className="w-3.5 h-3.5 inline mr-1.5" />
                          Aperturas
                          {engagementStats.aperturas?.total > 0 && (
                            <span className="ml-1.5 text-xs opacity-70">({engagementStats.aperturas.total})</span>
                          )}
                        </button>
                        <button
                          type="button"
                          role="tab"
                          aria-selected={engagementMode === "clicks"}
                          onClick={() => setEngagementMode("clicks")}
                          data-testid="engagement-mode-clicks"
                          className={`px-4 py-1.5 text-sm font-medium rounded-md transition ${
                            engagementMode === "clicks"
                              ? "bg-white dark:bg-slate-900 text-blue-700 dark:text-blue-300 shadow-sm"
                              : "text-slate-600 dark:text-slate-300 hover:text-slate-900"
                          }`}
                        >
                          <MousePointer className="w-3.5 h-3.5 inline mr-1.5" />
                          Clicks
                          {engagementStats.clicks?.total > 0 && (
                            <span className="ml-1.5 text-xs opacity-70">({engagementStats.clicks.total})</span>
                          )}
                        </button>
                      </div>
                    </div>

                    {(() => {
                      const src = engagementStats[engagementMode] || {
                        navegadores: engagementStats.navegadores || [],
                        dispositivos: engagementStats.dispositivos || [],
                        sistemas: engagementStats.sistemas || [],
                        geografia: engagementStats.geografia || [],
                        ciudades: engagementStats.ciudades || [],
                        total: engagementStats.total_abiertos_con_datos || 0,
                      };
                      if (src.total === 0) {
                        return (
                          <Card data-testid="engagement-empty-mode">
                            <CardContent className="p-6 text-center">
                              {engagementMode === "clicks" ? (
                                <MousePointer className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                              ) : (
                                <Eye className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                              )}
                              <p className="text-sm text-slate-500">
                                Aún no hay {engagementMode === "clicks" ? "clicks registrados" : "aperturas registradas"} para este artículo.
                              </p>
                            </CardContent>
                          </Card>
                        );
                      }
                      const modeLabel = engagementMode === "clicks" ? "clicks" : "aperturas";
                      return (
                        <>
                          <EngagementBarSection
                            title={`${engagementMode === "clicks" ? "Clicks" : "Aperturas"} por cliente de correo / navegador`}
                            subtitle={`Desglose de ${modeLabel} agrupadas por el programa o navegador usado`}
                            icon={<Chrome className="w-5 h-5 text-blue-600" />}
                            data={src.navegadores}
                            testId="engagement-browsers"
                            onItemClick={(nombre) => handleViewEngagementDetalle("navegador", nombre)}
                          />
                          <EngagementSection
                            title="Dispositivos"
                            subtitle={`Tipo de equipo desde donde se registraron las ${modeLabel}`}
                            icon={<Monitor className="w-5 h-5 text-emerald-600" />}
                            data={src.dispositivos}
                            colors={["#10b981","#6366f1","#f59e0b","#ef4444","#8b5cf6"]}
                            testId="engagement-devices"
                            iconMap={{ "Desktop": Monitor, "Movil": Smartphone, "Tablet": Tablet }}
                            onItemClick={(nombre) => handleViewEngagementDetalle("dispositivo", nombre)}
                          />
                          <EngagementSection
                            title="Sistemas Operativos"
                            subtitle={`Distribucion por sistema operativo de los ${modeLabel}`}
                            icon={<Monitor className="w-5 h-5 text-purple-600" />}
                            data={src.sistemas}
                            colors={["#8b5cf6","#6366f1","#10b981","#f59e0b","#ef4444","#ec4899","#14b8a6"]}
                            testId="engagement-os"
                            onItemClick={(nombre) => handleViewEngagementDetalle("sistema", nombre)}
                          />
                          <EngagementSection
                            title="Zona Geografica"
                            subtitle={`Paises desde donde se registraron las ${modeLabel}`}
                            icon={<MapPin className="w-5 h-5 text-red-500" />}
                            data={src.geografia}
                            colors={["#ef4444","#6366f1","#10b981","#f59e0b","#8b5cf6","#ec4899","#14b8a6","#f97316"]}
                            testId="engagement-geo"
                            onItemClick={(nombre) => handleViewEngagementDetalle("pais", nombre)}
                          />
                          {src.ciudades?.length > 0 && (
                            <Card data-testid="engagement-cities">
                              <CardHeader>
                                <CardTitle className="text-lg flex items-center gap-2">
                                  <Globe className="w-5 h-5 text-indigo-500" />
                                  Detalle por Ciudad
                                </CardTitle>
                                <p className="text-sm text-slate-500">Top 20 ciudades con más {modeLabel}</p>
                              </CardHeader>
                              <CardContent>
                                <div className="overflow-x-auto">
                                  <table className="w-full text-sm">
                                    <thead className="bg-slate-100">
                                      <tr>
                                        <th className="p-2 text-left">#</th>
                                        <th className="p-2 text-left">Ciudad</th>
                                        <th className="p-2 text-center">{engagementMode === "clicks" ? "Clicks" : "Aperturas"}</th>
                                        <th className="p-2 text-center">%</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y">
                                      {src.ciudades.map((c, i) => (
                                        <tr
                                          key={c.nombre}
                                          className="hover:bg-indigo-50 cursor-pointer"
                                          onClick={() => handleViewEngagementDetalle("ciudad", c.nombre)}
                                          data-testid={`engagement-city-row-${i}`}
                                          title="Ver quiénes son"
                                        >
                                          <td className="p-2 text-slate-400 text-xs">{i + 1}</td>
                                          <td className="p-2 font-medium flex items-center gap-1.5 text-indigo-700">
                                            <MapPin className="w-3 h-3 text-red-400" />
                                            {c.nombre}
                                          </td>
                                          <td className="p-2 text-center font-semibold">{c.cantidad}</td>
                                          <td className="p-2 text-center">
                                            <Badge variant="secondary">{c.porcentaje}%</Badge>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </CardContent>
                            </Card>
                          )}
                        </>
                      );
                    })()}
                  </>
                ) : engagementStats ? (
                  <Card>
                    <CardContent className="p-6 text-center">
                      <Monitor className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                      <p className="text-sm text-slate-500">Las estadisticas de navegador, dispositivo y ubicacion estaran disponibles cuando los destinatarios abran el email</p>
                      <p className="text-xs text-slate-400 mt-1">Esta informacion se captura automaticamente via el pixel de tracking</p>
                    </CardContent>
                  </Card>
                ) : null}

                <div className="flex justify-end">
                  <Button onClick={() => handleExportReporte(estadisticasArticulo.articulo?.id)}>
                    <Download className="w-4 h-4 mr-2" />Exportar Reporte
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Modal: detalle de un segmento de engagement (quiénes lo componen) */}
        <Dialog open={engDetalle.open} onOpenChange={(o) => setEngDetalle(prev => ({ ...prev, open: o }))}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="engagement-detalle-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-600" />
                {{ navegador: "Navegador / Cliente", dispositivo: "Dispositivo", sistema: "Sistema Operativo", pais: "País", ciudad: "Ciudad" }[engDetalle.tipo] || "Segmento"}: <span className="text-blue-700">{engDetalle.valor}</span>
              </DialogTitle>
              <DialogDescription>
                Contactos que registraron {engDetalle.modo === "clicks" ? "clicks" : "aperturas"} en este segmento
                {!engDetalle.loading && ` · ${engDetalle.correos.length} contacto(s)`}
              </DialogDescription>
            </DialogHeader>

            {engDetalle.loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-7 h-7 animate-spin text-blue-600" />
              </div>
            ) : engDetalle.correos.length === 0 ? (
              <p className="text-sm text-slate-500 py-8 text-center">No se encontraron contactos para este segmento.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 sticky top-0">
                    <tr>
                      <th className="p-2 text-left">#</th>
                      <th className="p-2 text-left">Email</th>
                      <th className="p-2 text-left">Ubicación</th>
                      <th className="p-2 text-left">Navegador</th>
                      <th className="p-2 text-left">Dispositivo</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {engDetalle.correos.map((c, i) => (
                      <tr key={c.email} className="hover:bg-slate-50" data-testid={`engagement-detalle-row-${i}`}>
                        <td className="p-2 text-slate-400 text-xs">{i + 1}</td>
                        <td className="p-2 font-medium text-slate-700">{c.email}</td>
                        <td className="p-2 text-slate-600">{c.ciudad && c.ciudad !== "Desconocida" ? `${c.ciudad}, ${c.pais}` : c.pais}</td>
                        <td className="p-2 text-slate-600">{c.navegador}</td>
                        <td className="p-2 text-slate-600">{c.dispositivo}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Diálogo de Estadísticas por Dominio */}
        <Dialog open={showDominioStatsDialog} onOpenChange={setShowDominioStatsDialog}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-blue-600" />
                Estadísticas por Dominio de Correo
                {dominioStats?.envio && <span className="text-slate-500 font-normal text-sm ml-2">({dominioStats.envio.articulo_titulo})</span>}
              </DialogTitle>
              <DialogDescription>
                Distribución de destinatarios por dominio de correo electrónico. <span className="text-blue-600">Haz clic en los números para ver el detalle.</span>
              </DialogDescription>
            </DialogHeader>
            
            {loadingDominioStats ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : dominioStats ? (
              <div className="space-y-6 py-4">
                {/* Resumen - Tarjetas clickeables */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card 
                    className="cursor-pointer hover:ring-2 hover:ring-blue-400 transition-all"
                    onClick={() => handleViewDetalleCorreos('todos')}
                  >
                    <CardContent className="p-4 text-center">
                      <Mail className="w-6 h-6 mx-auto mb-2 text-blue-600" />
                      <p className="text-2xl font-bold">{dominioStats.total_destinatarios || 0}</p>
                      <p className="text-sm text-slate-500">Total Destinatarios</p>
                      <p className="text-xs text-blue-500 mt-1">Click para ver lista</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 text-center">
                      <Globe className="w-6 h-6 mx-auto mb-2 text-purple-600" />
                      <p className="text-2xl font-bold">{dominioStats.resumen?.total_dominios_unicos || 0}</p>
                      <p className="text-sm text-slate-500">Dominios Únicos</p>
                    </CardContent>
                  </Card>
                  <Card 
                    className="cursor-pointer hover:ring-2 hover:ring-green-400 transition-all"
                    onClick={() => dominioStats.resumen?.dominio_principal && handleViewDetalleCorreos('todos', dominioStats.resumen.dominio_principal)}
                  >
                    <CardContent className="p-4 text-center">
                      <PieChart className="w-6 h-6 mx-auto mb-2 text-green-600" />
                      <p className="text-2xl font-bold">{dominioStats.resumen?.dominio_principal || "-"}</p>
                      <p className="text-sm text-slate-500">Dominio Principal ({dominioStats.resumen?.porcentaje_principal || 0}%)</p>
                      {dominioStats.resumen?.dominio_principal && <p className="text-xs text-green-500 mt-1">Click para ver correos</p>}
                    </CardContent>
                  </Card>
                </div>

                {/* Gráfico visual de barras horizontales - clickeable */}
                {dominioStats.dominios_grafico?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <PieChart className="w-5 h-5" />
                        Distribución por Dominio
                        <span className="text-xs text-slate-400 font-normal">(click en las barras para ver detalle)</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {dominioStats.dominios_grafico.map((item, idx) => {
                          const colors = [
                            "bg-blue-500", "bg-green-500", "bg-purple-500", "bg-amber-500",
                            "bg-red-500", "bg-cyan-500", "bg-pink-500", "bg-indigo-500",
                            "bg-teal-500", "bg-orange-500", "bg-slate-400"
                          ];
                          const color = colors[idx % colors.length];
                          return (
                            <div 
                              key={item.dominio} 
                              className="space-y-1 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 p-2 rounded-lg -mx-2 transition-colors"
                              onClick={() => item.dominio !== 'otros' && handleViewDetalleCorreos('todos', item.dominio)}
                            >
                              <div className="flex justify-between text-sm">
                                <span className="font-medium">{item.dominio}</span>
                                <span className="text-slate-500">{item.total} ({item.porcentaje}%)</span>
                              </div>
                              <div className="w-full bg-slate-100 dark:bg-slate-800 rounded-full h-3">
                                <div 
                                  className={`${color} h-3 rounded-full transition-all duration-500`}
                                  style={{ width: `${Math.max(item.porcentaje, 1)}%` }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Tabla detallada - celdas clickeables */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Detalle por Dominio</CardTitle>
                    <p className="text-xs text-slate-500">Haz clic en los números para ver la lista de correos</p>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-100 dark:bg-slate-800">
                          <tr>
                            <th className="p-3 text-left">Dominio</th>
                            <th className="p-3 text-center">Total</th>
                            <th className="p-3 text-center">%</th>
                            <th className="p-3 text-center">Enviados</th>
                            <th className="p-3 text-center">Fallidos</th>
                            <th className="p-3 text-center">Rebotados</th>
                            <th className="p-3 text-center">Abiertos</th>
                            <th className="p-3 text-center">Tasa Apertura</th>
                            <th className="p-3 text-center">Clicks</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {dominioStats.dominios?.map((dominio) => (
                            <tr key={dominio.dominio} className="hover:bg-slate-50 dark:hover:bg-slate-800">
                              <td className="p-3 font-medium">
                                <button 
                                  className="flex items-center gap-2 hover:text-blue-600 transition-colors"
                                  onClick={() => handleViewDetalleCorreos('todos', dominio.dominio)}
                                >
                                  <Globe className="w-4 h-4 text-slate-400" />
                                  @{dominio.dominio}
                                </button>
                              </td>
                              <td className="p-3 text-center">
                                <button 
                                  className="font-medium hover:text-blue-600 hover:underline transition-colors"
                                  onClick={() => handleViewDetalleCorreos('todos', dominio.dominio)}
                                >
                                  {dominio.total}
                                </button>
                              </td>
                              <td className="p-3 text-center">
                                <Badge variant="outline">{dominio.porcentaje}%</Badge>
                              </td>
                              <td className="p-3 text-center">
                                <button 
                                  className="text-green-600 hover:underline hover:text-green-800 transition-colors"
                                  onClick={() => dominio.enviados > 0 && handleViewDetalleCorreos('enviados', dominio.dominio)}
                                  disabled={dominio.enviados === 0}
                                >
                                  {dominio.enviados}
                                </button>
                              </td>
                              <td className="p-3 text-center">
                                <button 
                                  className="text-amber-600 hover:underline hover:text-amber-800 transition-colors"
                                  onClick={() => dominio.fallidos > 0 && handleViewDetalleCorreos('fallidos', dominio.dominio)}
                                  disabled={dominio.fallidos === 0}
                                >
                                  {dominio.fallidos}
                                </button>
                              </td>
                              <td className="p-3 text-center">
                                <button 
                                  className={`${dominio.rebotados > 0 ? "text-red-500 font-medium hover:underline hover:text-red-700" : ""} transition-colors`}
                                  onClick={() => dominio.rebotados > 0 && handleViewDetalleCorreos('rebotados', dominio.dominio)}
                                  disabled={dominio.rebotados === 0}
                                >
                                  {dominio.rebotados}
                                </button>
                              </td>
                              <td className="p-3 text-center">
                                <button 
                                  className="text-purple-600 hover:underline hover:text-purple-800 transition-colors"
                                  onClick={() => dominio.abiertos > 0 && handleViewDetalleCorreos('abiertos', dominio.dominio)}
                                  disabled={dominio.abiertos === 0}
                                >
                                  {dominio.abiertos}
                                </button>
                              </td>
                              <td className="p-3 text-center">
                                <Badge className={
                                  dominio.tasa_apertura >= 50 ? "bg-green-500" :
                                  dominio.tasa_apertura >= 20 ? "bg-amber-500" :
                                  "bg-red-500"
                                }>
                                  {dominio.tasa_apertura}%
                                </Badge>
                              </td>
                              <td className="p-3 text-center">
                                <button 
                                  className="text-blue-600 hover:underline hover:text-blue-800 transition-colors"
                                  onClick={() => dominio.clicks > 0 && handleViewDetalleCorreos('clicks', dominio.dominio)}
                                  disabled={dominio.clicks === 0}
                                >
                                  {dominio.clicks}
                                </button>
                              </td>
                            </tr>
                          ))}
                          {(!dominioStats.dominios || dominioStats.dominios.length === 0) && (
                            <tr>
                              <td colSpan={9} className="p-8 text-center text-slate-500">
                                No hay datos de dominios disponibles
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>

                {/* Insights */}
                <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200">
                  <CardContent className="p-4">
                    <h4 className="font-medium text-blue-700 dark:text-blue-300 mb-2">Insights</h4>
                    <ul className="text-sm text-slate-600 dark:text-slate-400 space-y-1">
                      <li>• El dominio principal <strong>@{dominioStats.resumen?.dominio_principal}</strong> representa el <strong>{dominioStats.resumen?.porcentaje_principal}%</strong> de tus destinatarios.</li>
                      {dominioStats.dominios?.find(d => d.dominio.includes("gmail")) && (
                        <li>• Los correos de Gmail tienen buena entregabilidad, pero revisa la carpeta de promociones.</li>
                      )}
                      {dominioStats.dominios?.find(d => d.rebotados > 0) && (
                        <li>• Hay dominios con rebotes. Considera limpiar tu lista de contactos.</li>
                      )}
                      {dominioStats.dominios?.some(d => d.tasa_apertura < 10 && d.enviados > 10) && (
                        <li>• Algunos dominios tienen tasas de apertura muy bajas. Revisa si están llegando a spam.</li>
                      )}
                    </ul>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                No se pudieron cargar las estadísticas
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Diálogo de Detalle de Correos */}
        <Dialog open={showDetalleCorreosDialog} onOpenChange={setShowDetalleCorreosDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-blue-600" />
                Detalle de Correos - {getCategoriaLabel(detalleCorreos?.categoria || '')}
                {detalleCorreos?.dominio && <Badge variant="outline" className="ml-2">@{detalleCorreos.dominio}</Badge>}
              </DialogTitle>
              <DialogDescription>
                {detalleCorreos?.total || 0} correos encontrados
              </DialogDescription>
            </DialogHeader>
            
            {loadingDetalleCorreos ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : detalleCorreos ? (
              <div className="space-y-4">
                {/* Resumen rápido */}
                <div className="flex items-center gap-4 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <Badge className="bg-blue-500">{detalleCorreos.total} correos</Badge>
                  <span className="text-sm text-slate-500">Categoría: <strong>{getCategoriaLabel(detalleCorreos.categoria)}</strong></span>
                  {detalleCorreos.dominio && <span className="text-sm text-slate-500">Dominio: <strong>@{detalleCorreos.dominio}</strong></span>}
                </div>

                {/* Lista de correos */}
                <div className="border rounded-lg overflow-hidden">
                  <div className="max-h-[400px] overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100 dark:bg-slate-800 sticky top-0">
                        <tr>
                          <th className="p-3 text-left">#</th>
                          <th className="p-3 text-left">Email</th>
                          <th className="p-3 text-center">Enviado</th>
                          <th className="p-3 text-center">Rebotó</th>
                          <th className="p-3 text-center" data-testid="th-reenvios">Reenvíos</th>
                          <th className="p-3 text-left">Abierto el</th>
                          <th className="p-3 text-left">Clickeado el</th>
                          <th className="p-3 text-left">Fecha de envío</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {detalleCorreos.correos?.map((correo, idx) => {
                          const fmt = (iso) => {
                            if (!iso) return null;
                            try {
                              const d = new Date(iso);
                              return d.toLocaleString('es-DO', { dateStyle: 'short', timeStyle: 'short' });
                            } catch {
                              return iso.slice(0, 16).replace('T', ' ');
                            }
                          };
                          const openedFmt = fmt(correo.opened_at);
                          const clickedFmt = fmt(correo.clicked_at);
                          const sentFmt = fmt(correo.sent_at);
                          const reenvioCount = correo.reenvio_count || 0;
                          const reenvioFechas = (correo.reenvio_fechas || []).map(fmt).filter(Boolean);
                          return (
                          <tr key={`${correo.email}-${idx}`} className="hover:bg-slate-50 dark:hover:bg-slate-800" data-testid={`correo-row-${idx}`}>
                            <td className="p-3 text-slate-400">{idx + 1}</td>
                            <td className="p-3 font-medium">{correo.email}</td>
                            <td className="p-3 text-center">
                              {correo.success ? (
                                <Check className="w-4 h-4 text-green-500 mx-auto" />
                              ) : (
                                <AlertTriangle className="w-4 h-4 text-red-500 mx-auto" />
                              )}
                            </td>
                            <td className="p-3 text-center">
                              {correo.bounced ? (
                                <AlertTriangle className="w-4 h-4 text-red-500 mx-auto" />
                              ) : (
                                <span className="text-slate-300">-</span>
                              )}
                            </td>
                            <td className="p-3 text-center" data-testid={`reenvio-cell-${idx}`}>
                              {reenvioCount > 0 ? (
                                <span
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-300 text-xs font-semibold cursor-help"
                                  title={reenvioFechas.length ? `Reenviado ${reenvioCount}x\n${reenvioFechas.join('\n')}` : `Reenviado ${reenvioCount}x`}
                                  data-testid={`reenvio-badge-${idx}`}
                                >
                                  <RefreshCw className="w-3 h-3" />
                                  {reenvioCount}x
                                </span>
                              ) : (
                                <span className="text-slate-300 text-xs">—</span>
                              )}
                            </td>
                            <td className="p-3">
                              {correo.opened && openedFmt ? (
                                <span className="inline-flex items-center gap-1.5 text-purple-700 dark:text-purple-300" data-testid={`correo-opened-${idx}`}>
                                  <Eye className="w-3.5 h-3.5" />
                                  <span className="text-xs font-medium">{openedFmt}</span>
                                </span>
                              ) : correo.opened ? (
                                <Eye className="w-4 h-4 text-purple-500" />
                              ) : (
                                <span className="text-slate-300 text-xs">No abrió</span>
                              )}
                            </td>
                            <td className="p-3">
                              {correo.clicked && clickedFmt ? (
                                <span className="inline-flex items-center gap-1.5 text-blue-700 dark:text-blue-300" data-testid={`correo-clicked-${idx}`}>
                                  <MousePointer className="w-3.5 h-3.5" />
                                  <span className="text-xs font-medium">{clickedFmt}</span>
                                </span>
                              ) : correo.clicked ? (
                                <MousePointer className="w-4 h-4 text-blue-500" />
                              ) : (
                                <span className="text-slate-300 text-xs">Sin click</span>
                              )}
                            </td>
                            <td className="p-3 text-xs text-slate-500">
                              {sentFmt || "-"}
                            </td>
                          </tr>
                          );
                        })}
                        {(!detalleCorreos.correos || detalleCorreos.correos.length === 0) && (
                          <tr>
                            <td colSpan={8} className="p-8 text-center text-slate-500">
                              No se encontraron correos con este criterio
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Errores si hay */}
                {detalleCorreos.correos?.some(c => c.error) && (
                  <Card className="bg-red-50 dark:bg-red-900/20 border-red-200">
                    <CardHeader>
                      <CardTitle className="text-sm text-red-700">Errores Detectados</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-1 text-sm">
                        {detalleCorreos.correos.filter(c => c.error).slice(0, 10).map((c, idx) => (
                          <div key={idx} className="flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                            <span><strong>{c.email}:</strong> {c.error}</span>
                          </div>
                        ))}
                        {detalleCorreos.correos.filter(c => c.error).length > 10 && (
                          <p className="text-slate-500 mt-2">...y {detalleCorreos.correos.filter(c => c.error).length - 10} errores más</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                No se pudieron cargar los datos
              </div>
            )}

            <DialogFooter className="flex gap-2">
              <Button 
                variant="default" 
                onClick={handleExportarCorreosFiltrados}
                disabled={!detalleCorreos?.correos?.length}
                className="bg-green-600 hover:bg-green-700"
              >
                <Download className="w-4 h-4 mr-2" />
                Exportar a Excel
              </Button>
              <Button variant="outline" onClick={() => setShowDetalleCorreosDialog(false)}>Cerrar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Confirmación de Reenvío a No Abiertos */}
        <Dialog open={showReenvioConfirm} onOpenChange={setShowReenvioConfirm}>
          <DialogContent className="max-w-md" data-testid="resend-confirm-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <RefreshCw className="w-5 h-5 text-amber-600" />
                Confirmar Reenvío
              </DialogTitle>
              <DialogDescription>
                Se reenviará el artículo <strong>{estadisticasArticulo?.articulo?.titulo}</strong> a los contactos que no abrieron el envío anterior y a los que fallaron/rebotaron (reconsultando su correo actual en la lista, por si fue corregido).
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 space-y-3">
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  Incluye <strong>no leídos</strong> + <strong>fallidos/rebotados</strong> (con el correo corregido de la lista)
                </p>
                <p className="text-xs text-amber-600 mt-1">Solo se excluyen los que ya abrieron el artículo y los que cancelaron suscripción. El total exacto se calcula al confirmar.</p>
              </div>

              {/* Programar reenvío a futuro */}
              <div className="rounded-lg border p-3 space-y-2">
                <label className="flex items-center gap-2 cursor-pointer" data-testid="resend-schedule-toggle">
                  <input
                    type="checkbox"
                    checked={reenvioProgramar}
                    onChange={(e) => setReenvioProgramar(e.target.checked)}
                    className="h-4 w-4 rounded border-slate-400 text-amber-600"
                  />
                  <span className="text-sm font-medium flex items-center gap-1">
                    <Clock className="w-4 h-4" /> Programar para más tarde
                  </span>
                </label>
                {reenvioProgramar && (
                  <div className="space-y-1">
                    <Input
                      type="datetime-local"
                      value={reenvioFecha}
                      min={new Date(Date.now() + 60000).toISOString().slice(0, 16)}
                      onChange={(e) => setReenvioFecha(e.target.value)}
                      data-testid="resend-schedule-datetime"
                    />
                    <p className="text-xs text-slate-500">
                      Al llegar la hora, el sistema reenviará <strong>sólo</strong> a quienes no hayan abierto hasta ese momento (la cantidad puede ser menor a la actual).
                    </p>
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowReenvioConfirm(false); setReenvioProgramar(false); setReenvioFecha(""); }} disabled={reenvioLoading}>Cancelar</Button>
              <Button
                className="bg-amber-600 hover:bg-amber-700"
                onClick={handleReenviarNoAbiertos}
                disabled={reenvioLoading}
                data-testid="confirm-resend-btn"
              >
                {reenvioLoading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : (reenvioProgramar ? <Clock className="w-4 h-4 mr-2" /> : <Send className="w-4 h-4 mr-2" />)}
                {reenvioLoading ? "Procesando..." : (reenvioProgramar ? "Programar Reenvío" : "Confirmar Reenvío")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ===== Dialog: Configuración de Reporte Mensual ===== */}
        {/* ===== Modal: SMTP Marketing Configuration (admin only) ===== */}
        <Dialog open={showSmtpMarketingDialog} onOpenChange={setShowSmtpMarketingDialog}>
          <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="smtp-marketing-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Server className="w-5 h-5 text-purple-600" />
                Configuración SMTP — Módulo Marketing
              </DialogTitle>
              <DialogDescription className="text-xs">
                Servidor SMTP <b>dedicado exclusivamente</b> al módulo de Artículos de Marketing.
                Es independiente del SMTP transaccional de la plataforma. Todos los envíos
                (artículos, pruebas, reporte mensual, notificaciones de cancelación) usarán esta configuración.
              </DialogDescription>
            </DialogHeader>

            {smtpMarketingLoading ? (
              <div className="flex items-center justify-center py-10 text-slate-500">
                <Loader2 className="w-6 h-6 animate-spin mr-2" /> Cargando…
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 rounded border bg-slate-50">
                  <div>
                    <p className="text-sm font-medium">Habilitado</p>
                    <p className="text-xs text-slate-500">Si está desactivado, los envíos de marketing fallarán con un error claro.</p>
                  </div>
                  <Switch
                    checked={smtpMarketingConfig.enabled}
                    onCheckedChange={(v) => setSmtpMarketingConfig(c => ({ ...c, enabled: v }))}
                    data-testid="smtp-enabled-switch"
                  />
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2">
                    <Label className="text-xs">Servidor SMTP</Label>
                    <Input
                      value={smtpMarketingConfig.smtp_server}
                      onChange={(e) => setSmtpMarketingConfig(c => ({ ...c, smtp_server: e.target.value }))}
                      placeholder="smtp.office365.com"
                      data-testid="smtp-server-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Puerto</Label>
                    <Input
                      type="number"
                      value={smtpMarketingConfig.smtp_port}
                      onChange={(e) => setSmtpMarketingConfig(c => ({ ...c, smtp_port: parseInt(e.target.value || "587", 10) }))}
                      data-testid="smtp-port-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Usuario</Label>
                    <Input
                      value={smtpMarketingConfig.username}
                      onChange={(e) => setSmtpMarketingConfig(c => ({ ...c, username: e.target.value }))}
                      placeholder="marketing@empresa.com"
                      data-testid="smtp-username-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Contraseña</Label>
                    <Input
                      type="password"
                      value={smtpMarketingConfig.password}
                      onChange={(e) => setSmtpMarketingConfig(c => ({ ...c, password: e.target.value }))}
                      placeholder="(déjalo vacío para conservar la existente)"
                      data-testid="smtp-password-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Nombre remitente</Label>
                    <Input
                      value={smtpMarketingConfig.from_name}
                      onChange={(e) => setSmtpMarketingConfig(c => ({ ...c, from_name: e.target.value }))}
                      placeholder="QAsesores Marketing"
                      data-testid="smtp-fromname-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Email remitente</Label>
                    <Input
                      value={smtpMarketingConfig.from_email}
                      onChange={(e) => setSmtpMarketingConfig(c => ({ ...c, from_email: e.target.value }))}
                      placeholder="marketing@empresa.com"
                      data-testid="smtp-fromemail-input"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-6 p-3 rounded border bg-slate-50">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox
                      checked={smtpMarketingConfig.use_tls}
                      onCheckedChange={(v) => setSmtpMarketingConfig(c => ({ ...c, use_tls: !!v, use_ssl: v ? false : c.use_ssl }))}
                      data-testid="smtp-tls-checkbox"
                    />
                    Usar STARTTLS (puerto 587)
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox
                      checked={smtpMarketingConfig.use_ssl}
                      onCheckedChange={(v) => setSmtpMarketingConfig(c => ({ ...c, use_ssl: !!v, use_tls: v ? false : c.use_tls }))}
                      data-testid="smtp-ssl-checkbox"
                    />
                    Usar SSL directo (puerto 465)
                  </label>
                </div>

                {/* ========== Throttling / Envío por lotes ========== */}
                <div className="p-3 rounded-lg border border-emerald-200 bg-emerald-50 space-y-3" data-testid="smtp-throttle-section">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-emerald-900 flex items-center gap-1.5">
                        <Clock className="w-4 h-4" />
                        Envío por lotes (anti-spam)
                      </p>
                      <p className="text-xs text-emerald-700 mt-0.5">
                        Divide envíos masivos en lotes con pausas intermedias para evitar bloqueos por sospecha de spam de Gmail/Outlook/Office365.
                      </p>
                    </div>
                    <label className="flex items-center gap-2 text-xs cursor-pointer shrink-0">
                      <Checkbox
                        checked={smtpMarketingConfig.throttle_enabled}
                        onCheckedChange={(v) => setSmtpMarketingConfig(c => ({ ...c, throttle_enabled: !!v }))}
                        data-testid="smtp-throttle-enabled-checkbox"
                      />
                      Activar
                    </label>
                  </div>

                  {smtpMarketingConfig.throttle_enabled && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Correos por lote</Label>
                        <Input
                          type="number"
                          min={1}
                          max={1000}
                          value={smtpMarketingConfig.batch_size}
                          onChange={(e) => setSmtpMarketingConfig(c => ({ ...c, batch_size: Math.max(1, parseInt(e.target.value || "50", 10)) }))}
                          className="bg-white"
                          data-testid="smtp-batch-size-input"
                        />
                        <p className="text-[10px] text-slate-500 mt-0.5">
                          Cantidad de correos a enviar antes de la pausa. Recomendado: 50.
                        </p>
                      </div>
                      <div>
                        <Label className="text-xs">Pausa entre lotes (segundos)</Label>
                        <Input
                          type="number"
                          min={0}
                          max={3600}
                          value={smtpMarketingConfig.batch_pause_seconds}
                          onChange={(e) => setSmtpMarketingConfig(c => ({ ...c, batch_pause_seconds: Math.max(0, parseInt(e.target.value || "60", 10)) }))}
                          className="bg-white"
                          data-testid="smtp-batch-pause-input"
                        />
                        <p className="text-[10px] text-slate-500 mt-0.5">
                          Tiempo de espera antes del siguiente lote. Recomendado: 60s.
                        </p>
                      </div>
                      <div className="col-span-2 px-3 py-2 rounded bg-white border border-emerald-200 text-xs text-emerald-900">
                        <span className="font-medium">Ritmo aproximado:</span>{" "}
                        {(() => {
                          const bs = smtpMarketingConfig.batch_size || 1;
                          const bp = smtpMarketingConfig.batch_pause_seconds || 0;
                          // Each email takes ~0.1s + smtp overhead, ~1s/email avg
                          const perBatchSec = bs * 1 + bp;
                          const perHour = Math.round((3600 / perBatchSec) * bs);
                          return `${perHour.toLocaleString()} correos/hora (${bs} cada ${Math.round(perBatchSec)}s)`;
                        })()}
                      </div>
                    </div>
                  )}
                </div>
                {/* ========================================== */}

                {/* Test block */}
                <div className="p-3 rounded-lg border border-blue-200 bg-blue-50 space-y-2">
                  <p className="text-sm font-medium text-blue-900">Enviar correo de prueba</p>
                  <p className="text-xs text-blue-700">Guarda primero, luego prueba. El correo se enviará usando la configuración almacenada.</p>
                  <div className="flex gap-2">
                    <Input
                      value={smtpTestEmail}
                      onChange={(e) => setSmtpTestEmail(e.target.value)}
                      placeholder="destinatario@ejemplo.com"
                      className="bg-white"
                      data-testid="smtp-test-email-input"
                    />
                    <Button
                      variant="outline"
                      className="shrink-0"
                      disabled={smtpTesting}
                      onClick={testSmtpMarketing}
                      data-testid="smtp-test-btn"
                    >
                      {smtpTesting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                      Enviar prueba
                    </Button>
                  </div>
                </div>

                {/* Deliverability check: SPF / DKIM / DMARC */}
                <div className="p-3 rounded-lg border border-indigo-200 bg-indigo-50 space-y-2" data-testid="dns-check-block">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium text-indigo-900 flex items-center gap-1.5">
                      <ShieldCheck className="w-4 h-4" />
                      Entregabilidad del dominio (SPF · DKIM · DMARC)
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="shrink-0 bg-white"
                      disabled={dnsChecking}
                      onClick={checkMarketingDns}
                      data-testid="dns-check-btn"
                    >
                      {dnsChecking ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
                      Verificar
                    </Button>
                  </div>
                  <p className="text-xs text-indigo-700">
                    Si SPF, DKIM o DMARC faltan, tus artículos pueden llegar a la carpeta de <b>SPAM</b> aunque el envío sea exitoso. Guarda primero la configuración para verificar el dominio remitente.
                  </p>

                  {dnsCheck && (
                    <div className="space-y-2" data-testid="dns-check-result">
                      <p className="text-xs text-indigo-900">
                        Dominio verificado: <b>{dnsCheck.domain}</b>
                      </p>
                      {[
                        { key: "spf", label: "SPF", ok: dnsCheck.spf?.present && dnsCheck.spf?.covers_provider !== false, detail: dnsCheck.spf?.record || "No encontrado" },
                        { key: "dkim", label: "DKIM", ok: dnsCheck.dkim?.present, detail: dnsCheck.dkim?.present ? `Selectores: ${dnsCheck.dkim.selectors_found.join(", ")}` : "No detectado" },
                        { key: "dmarc", label: "DMARC", ok: dnsCheck.dmarc?.present, detail: dnsCheck.dmarc?.present ? `Política: p=${dnsCheck.dmarc.policy}` : "No encontrado" },
                      ].map((row) => (
                        <div key={row.key} className="flex items-start gap-2 px-2 py-1.5 rounded bg-white border border-indigo-100" data-testid={`dns-row-${row.key}`}>
                          {row.ok
                            ? <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                            : <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />}
                          <div className="min-w-0">
                            <p className="text-xs font-semibold text-gray-800">{row.label}{" "}
                              <span className={row.ok ? "text-emerald-600" : "text-red-600"}>
                                {row.ok ? "OK" : "Atención"}
                              </span>
                            </p>
                            <p className="text-[11px] text-gray-500 break-words font-mono">{row.detail}</p>
                          </div>
                        </div>
                      ))}
                      {dnsCheck.warnings && dnsCheck.warnings.length > 0 && (
                        <ul className="list-disc ml-5 space-y-0.5 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2" data-testid="dns-warnings">
                          {dnsCheck.warnings.map((w, i) => (<li key={i}>{w}</li>))}
                        </ul>
                      )}
                      {dnsCheck.all_ok && (
                        <p className="text-xs text-emerald-700 font-medium flex items-center gap-1">
                          <CheckCircle2 className="w-3.5 h-3.5" /> Tu dominio está correctamente autenticado para enviar correos.
                        </p>
                      )}
                    </div>
                  )}
                </div>


                {/* Ayuda para Office 365 */}
                {/^smtp\.office365\.com$/i.test(smtpMarketingConfig.smtp_server?.trim() || "") && (
                  <div className="p-3 rounded-lg border border-amber-300 bg-amber-50 text-xs text-amber-900">
                    <p className="font-semibold mb-1 flex items-center gap-1">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      Importante con Microsoft 365
                    </p>
                    <p>Microsoft deshabilita <b>SMTP AUTH</b> por defecto en los tenants desde 2022. Si la prueba falla con el error <code>5.7.139 SmtpClientAuthentication is disabled</code>, el admin del tenant debe:</p>
                    <ul className="list-disc ml-5 mt-1 space-y-0.5">
                      <li>Habilitar SMTP AUTH <b>solo para el buzón</b> <code>{smtpMarketingConfig.username || "usuario"}</code> desde el portal de Microsoft 365 (<a className="underline" href="https://aka.ms/smtp_auth_disabled" target="_blank" rel="noreferrer">aka.ms/smtp_auth_disabled</a>).</li>
                      <li>O usar un buzón dedicado con SMTP AUTH permitido a nivel de política.</li>
                      <li>Alternativa: servicios externos como SendGrid, Amazon SES o Mailgun (mayor deliverability para envíos masivos).</li>
                    </ul>
                  </div>
                )}
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShowSmtpMarketingDialog(false)} data-testid="smtp-cancel-btn">Cerrar</Button>
              <Button
                className="bg-purple-600 hover:bg-purple-700"
                disabled={smtpMarketingSaving}
                onClick={saveSmtpMarketingConfig}
                data-testid="smtp-save-btn"
              >
                {smtpMarketingSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                Guardar configuración
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={showReporteMensualDialog} onOpenChange={setShowReporteMensualDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="reporte-mensual-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-blue-600" />
                Reporte Mensual Automatizado
              </DialogTitle>
              <DialogDescription>
                Recibe por correo un PDF con los KPIs del mes anterior: envíos, aperturas, clicks, top artículos y dominios más engaged.
              </DialogDescription>
            </DialogHeader>

            {reporteMensualLoading || !reporteMensualConfig ? (
              <div className="py-10 text-center">
                <RefreshCw className="w-6 h-6 animate-spin mx-auto text-slate-400 mb-2" />
                <p className="text-sm text-slate-500">Cargando configuración...</p>
              </div>
            ) : (
              <div className="space-y-5 py-2">
                {/* Activación */}
                <div className="flex items-start justify-between gap-4 p-4 rounded-lg border bg-slate-50 dark:bg-slate-800/50">
                  <div className="flex-1">
                    <Label className="text-sm font-semibold">Envío automático mensual</Label>
                    <p className="text-xs text-slate-500 mt-1">
                      Cuando está activo, el sistema enviará el reporte automáticamente cada mes en el día y hora configurados.
                    </p>
                  </div>
                  <Switch
                    checked={!!reporteMensualConfig.activo}
                    onCheckedChange={(v) => setReporteMensualConfig({ ...reporteMensualConfig, activo: v })}
                    data-testid="reporte-mensual-activo"
                  />
                </div>

                {/* Día y hora */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm">Día del mes</Label>
                    <Input
                      type="number"
                      min={1}
                      max={28}
                      value={reporteMensualConfig.dia_envio || 1}
                      onChange={(e) => setReporteMensualConfig({ ...reporteMensualConfig, dia_envio: Number(e.target.value) })}
                      data-testid="reporte-mensual-dia"
                    />
                    <p className="text-xs text-slate-400 mt-1">Del 1 al 28 (para evitar meses cortos)</p>
                  </div>
                  <div>
                    <Label className="text-sm">Hora de envío (UTC)</Label>
                    <Input
                      type="time"
                      value={reporteMensualConfig.hora_envio || "09:00"}
                      onChange={(e) => setReporteMensualConfig({ ...reporteMensualConfig, hora_envio: e.target.value })}
                      data-testid="reporte-mensual-hora"
                    />
                    <p className="text-xs text-slate-400 mt-1">Se chequea cada hora; usa UTC</p>
                  </div>
                </div>

                {/* Destinatarios */}
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Destinatarios</Label>

                  <div className="flex items-start gap-2 p-3 rounded-lg border bg-blue-50 dark:bg-blue-900/20">
                    <Checkbox
                      checked={reporteMensualConfig.incluir_admin_original !== false}
                      onCheckedChange={(v) => setReporteMensualConfig({ ...reporteMensualConfig, incluir_admin_original: !!v })}
                      data-testid="reporte-mensual-incluir-admin"
                      className="mt-0.5"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium">Enviar también al administrador configurador</p>
                      <p className="text-xs text-slate-500">
                        {reporteMensualConfig.admin_email
                          ? `Correo: ${reporteMensualConfig.admin_email}`
                          : "Se usará el correo del usuario que guarda la configuración"}
                      </p>
                    </div>
                  </div>

                  {/* Lista de correos adicionales */}
                  <div>
                    <p className="text-xs text-slate-500 mb-2">Correos adicionales ({(reporteMensualConfig.emails_adicionales || []).length})</p>
                    <div className="flex flex-wrap gap-2 mb-2 min-h-[32px]">
                      {(reporteMensualConfig.emails_adicionales || []).map((email) => (
                        <Badge
                          key={email}
                          variant="secondary"
                          className="pl-3 pr-1.5 py-1 flex items-center gap-1.5 bg-blue-100 text-blue-800 hover:bg-blue-200"
                          data-testid={`email-chip-${email}`}
                        >
                          <Mail className="w-3 h-3" />
                          <span className="text-sm">{email}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveEmailReporte(email)}
                            className="ml-1 hover:bg-blue-300 rounded-full w-5 h-5 inline-flex items-center justify-center"
                            data-testid={`remove-email-${email}`}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>
                      ))}
                      {(reporteMensualConfig.emails_adicionales || []).length === 0 && (
                        <span className="text-xs italic text-slate-400">Aún no hay correos adicionales</span>
                      )}
                    </div>

                    <div className="flex gap-2">
                      <Input
                        type="email"
                        placeholder="ejemplo@empresa.com"
                        value={nuevoEmailReporte}
                        onChange={(e) => setNuevoEmailReporte(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddEmailReporte(); } }}
                        data-testid="nuevo-email-reporte"
                      />
                      <Button type="button" variant="outline" onClick={handleAddEmailReporte} data-testid="add-email-reporte">
                        <Plus className="w-4 h-4 mr-1" />
                        Agregar
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Último envío */}
                {reporteMensualConfig.ultimo_envio && (
                  <div className="p-3 rounded-lg border border-green-200 bg-green-50 dark:bg-green-900/20 text-sm">
                    <p className="font-medium text-green-800 dark:text-green-300 flex items-center gap-1.5">
                      <Check className="w-4 h-4" />
                      Último envío: {(() => { try { return new Date(reporteMensualConfig.ultimo_envio).toLocaleString('es-DO', { dateStyle: 'medium', timeStyle: 'short' }); } catch { return reporteMensualConfig.ultimo_envio; } })()}
                    </p>
                    {reporteMensualConfig.ultimo_mes_enviado && (
                      <p className="text-xs text-green-700 dark:text-green-400 mt-0.5">
                        Reporte correspondiente a: {reporteMensualConfig.ultimo_mes_enviado}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            <DialogFooter className="gap-2 flex-wrap">
              <Button variant="outline" onClick={handlePreviewReporte} data-testid="preview-reporte-btn">
                <Eye className="w-4 h-4 mr-1.5" />
                Ver PDF de muestra
              </Button>
              <Button
                variant="outline"
                onClick={handleEnviarReporteAhora}
                disabled={reporteMensualSending || reporteMensualLoading}
                data-testid="enviar-ahora-btn"
              >
                {reporteMensualSending ? (
                  <><RefreshCw className="w-4 h-4 mr-1.5 animate-spin" /> Enviando...</>
                ) : (
                  <><Mail className="w-4 h-4 mr-1.5" /> Enviar ahora (prueba)</>
                )}
              </Button>
              <Button
                onClick={handleSaveReporteMensual}
                disabled={reporteMensualSaving || reporteMensualLoading}
                data-testid="guardar-reporte-mensual"
              >
                {reporteMensualSaving ? (
                  <><RefreshCw className="w-4 h-4 mr-1.5 animate-spin" /> Guardando...</>
                ) : (
                  <>Guardar configuración</>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal: Vista previa del correo completo */}
        <Dialog open={showEmailPreview} onOpenChange={setShowEmailPreview}>
          <DialogContent className="max-w-4xl h-[90vh] flex flex-col p-4" data-testid="email-preview-modal">
            <DialogHeader>
              <DialogTitle>Vista previa del correo</DialogTitle>
              <DialogDescription>Así lo recibirá el cliente (variables de contacto con datos de ejemplo)</DialogDescription>
            </DialogHeader>
            <iframe
              srcDoc={showEmailPreview ? buildEmailPreviewHtml() : ""}
              title="Vista previa del correo"
              className="flex-1 w-full border rounded-md bg-white"
              sandbox="allow-same-origin"
              data-testid="email-preview-iframe"
            />
            <div className="flex items-center gap-2 pt-3 border-t">
              <span className="text-xs text-muted-foreground whitespace-nowrap">Envía este correo tal cual a tu bandeja antes del envío masivo:</span>
              <Input
                type="email"
                placeholder={user?.email || "tu@correo.com"}
                value={pruebaEmail}
                onChange={(e) => setPruebaEmail(e.target.value)}
                className="flex-1"
                data-testid="prueba-email-input"
              />
              <Button onClick={enviarCorreoPrueba} disabled={enviandoPrueba} data-testid="enviar-prueba-btn">
                {enviandoPrueba ? "Enviando..." : "Enviarme prueba"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Modal: Artículos del Mes (recopilatorio) */}
        <Dialog open={showRecapModal} onOpenChange={setShowRecapModal}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="articulos-mes-modal">
            <DialogHeader>
              <DialogTitle>Artículos del Mes</DialogTitle>
              <DialogDescription>
                Selecciona los artículos a incluir en el correo recopilatorio. El cuerpo generado se insertará en el contenido (tú agregas el header y footer).
              </DialogDescription>
            </DialogHeader>

            {/* Formato */}
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setRecapFormato("profesional")}
                className={`text-left border-2 rounded-lg p-3 transition-colors ${recapFormato === "profesional" ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20" : "border-slate-200 hover:border-emerald-300"}`}
                data-testid="recap-formato-profesional"
              >
                <p className="text-sm font-semibold">Opción Profesional</p>
                <p className="text-xs text-muted-foreground mt-0.5">1er artículo destacado a ancho completo + el resto en cuadrícula de 2 columnas</p>
              </button>
              <button
                type="button"
                onClick={() => setRecapFormato("columna")}
                className={`text-left border-2 rounded-lg p-3 transition-colors ${recapFormato === "columna" ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20" : "border-slate-200 hover:border-emerald-300"}`}
                data-testid="recap-formato-columna"
              >
                <p className="text-sm font-semibold">Opción Columna</p>
                <p className="text-xs text-muted-foreground mt-0.5">Lista vertical: cada artículo con imagen, título y botón, uno debajo del otro</p>
              </button>
            </div>

            {/* Filtros */}
            <div className="flex gap-2">
              <Input type="month" value={recapMes} onChange={(e) => setRecapMes(e.target.value)} className="w-56 shrink-0" data-testid="recap-mes-input" />
              <Input placeholder="Buscar por título..." value={recapBusqueda} onChange={(e) => setRecapBusqueda(e.target.value)} className="flex-1" data-testid="recap-busqueda-input" />
            </div>

            {/* Lista de artículos */}
            <div className="border rounded-lg divide-y max-h-72 overflow-y-auto">
              {recapLoading ? (
                <div className="p-6 text-center text-sm text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Cargando artículos...</div>
              ) : (() => {
                const filtrados = recapLista.filter(a =>
                  (!recapMes || (a.created_at || "").slice(0, 7) === recapMes) &&
                  (!recapBusqueda || (a.titulo || "").toLowerCase().includes(recapBusqueda.toLowerCase()))
                );
                if (filtrados.length === 0) return <div className="p-6 text-center text-sm text-muted-foreground">No hay artículos para este mes/búsqueda</div>;
                return filtrados.map(a => {
                  const orden = recapSeleccion.indexOf(a.id);
                  return (
                    <label key={a.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800" data-testid={`recap-articulo-${a.id}`}>
                      <Checkbox
                        checked={orden !== -1}
                        onCheckedChange={(ck) => setRecapSeleccion(prev => ck ? [...prev, a.id] : prev.filter(x => x !== a.id))}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{a.titulo}</p>
                        <p className="text-[11px] text-muted-foreground">{(a.created_at || "").slice(0, 10)} · {a.categoria || "Sin categoría"} · {a.estado}</p>
                      </div>
                      {orden !== -1 && <Badge className="bg-emerald-600 text-white shrink-0">{orden + 1}º</Badge>}
                    </label>
                  );
                });
              })()}
            </div>

            <DialogFooter className="items-center">
              <span className="text-xs text-muted-foreground mr-auto" data-testid="recap-contador">{recapSeleccion.length} artículo{recapSeleccion.length !== 1 ? "s" : ""} seleccionado{recapSeleccion.length !== 1 ? "s" : ""}</span>
              <Button variant="outline" onClick={() => setShowRecapModal(false)}>Cancelar</Button>
              <Button onClick={insertarRecap} data-testid="recap-insertar-btn">Insertar en el cuerpo</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

      </main>
    </div>
  );
}


const ENGAGEMENT_COLORS_DEFAULT = ["#6366f1","#10b981","#f59e0b","#ef4444","#8b5cf6","#ec4899","#14b8a6","#f97316","#06b6d4","#84cc16"];

// Wall-clock read kept out of the component render body so it isn't treated as
// an impure read during render. Used only for cosmetic live countdown/staleness
// labels, recomputed on each poll-driven re-render.
const readNowMs = () => Date.now();

// Tooltip for the engagement pie chart. Defined at module scope (static) so it
// does not recreate on every render of EngagementSection. Receives the chart
// total as a prop to compute the percentage.
const EngagementTooltip = ({ active, payload, total }) => {
  if (!active || !payload?.[0]) return null;
  const d = payload[0].payload;
  const pct = total ? Math.round((d.value / total) * 100) : 0;
  return (
    <div className="bg-white rounded-lg shadow-lg border px-3 py-2">
      <p className="text-xs font-semibold text-slate-800">{d.name}</p>
      <p className="text-sm font-bold" style={{ color: d.fill }}>{d.value} ({pct}%)</p>
    </div>
  );
};

function EngagementSection({ title, subtitle, icon, data, colors = ENGAGEMENT_COLORS_DEFAULT, testId, iconMap, onItemClick }) {
  if (!data || data.length === 0) return null;

  const chartData = data.map((d, i) => ({ name: d.nombre, value: d.cantidad, fill: colors[i % colors.length] }));
  const chartTotal = data.reduce((s, x) => s + (x.cantidad || 0), 0);

  return (
    <Card data-testid={testId}>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          {icon}
          {title}
        </CardTitle>
        <p className="text-sm text-slate-500">{subtitle}</p>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Donut Chart */}
          <div className="flex items-center justify-center">
            <ResponsiveContainer width="100%" height={220}>
              <RechartsPieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={85}
                  paddingAngle={2}
                  dataKey="value"
                  nameKey="name"
                >
                  {chartData.map((entry, i) => (
                    <Cell key={i} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip content={<EngagementTooltip total={chartTotal} />} />
              </RechartsPieChart>
            </ResponsiveContainer>
          </div>
          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100">
                <tr>
                  <th className="p-2 text-left">{title === "Zona Geografica" ? "Pais" : title.replace(/s$/, "")}</th>
                  <th className="p-2 text-center">Cantidad</th>
                  <th className="p-2 text-center">%</th>
                  <th className="p-2 text-right">Distribucion</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {data.map((item, i) => {
                  const IconComp = iconMap?.[item.nombre];
                  return (
                    <tr
                      key={item.nombre}
                      className={`hover:bg-slate-50 ${onItemClick ? "cursor-pointer hover:bg-blue-50" : ""}`}
                      onClick={onItemClick ? () => onItemClick(item.nombre) : undefined}
                      data-testid={`${testId}-row-${i}`}
                      title={onItemClick ? "Ver quiénes son" : undefined}
                    >
                      <td className={`p-2 font-medium flex items-center gap-1.5 ${onItemClick ? "text-blue-700" : ""}`}>
                        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: colors[i % colors.length] }} />
                        {IconComp && <IconComp className="w-3.5 h-3.5 text-slate-500" />}
                        {item.nombre}
                      </td>
                      <td className="p-2 text-center font-semibold">{item.cantidad}</td>
                      <td className="p-2 text-center">
                        <Badge variant="secondary">{item.porcentaje}%</Badge>
                      </td>
                      <td className="p-2">
                        <div className="w-full bg-slate-100 rounded-full h-2">
                          <div
                            className="h-2 rounded-full transition-all"
                            style={{ width: `${Math.max(item.porcentaje, 3)}%`, backgroundColor: colors[i % colors.length] }}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Clasifica un "navegador/cliente de correo" dentro de una categoría Escritorio / Móvil / Otra
function classifyClient(name) {
  const n = (name || "").toLowerCase();
  if (/(gmail|yahoo|outlook|hotmail)/.test(n)) {
    // Clientes web de correo → "Otra" (categoría en referencia)
    if (/(gmail|yahoo|outlook|hotmail)/.test(n)) return "otra";
  }
  if (/(iphone|ios|ipad|android|mobile safari|android webview|samsung|mobile)/.test(n)) return "movil";
  if (/(chrome|firefox|edge|safari|opera|brave|microsoft office|thunderbird|apple mail|macbook|windows)/.test(n)) return "escritorio";
  if (/(bot|crawler|spider|preview|mail\.ru|yandex)/.test(n)) return "otra";
  return "otra";
}

const CATEGORY_COLORS = {
  movil: "#14b8a6",       // teal
  escritorio: "#8b5cf6",  // purple
  otra: "#fb923c",        // orange
};
const CATEGORY_LABELS = {
  movil: "Móvil",
  escritorio: "Equipo de escritorio",
  otra: "Otra",
};

// Module-scope (static) tooltips so they don't recreate on each render of their
// parent chart section.
const BarTooltip = ({ active, payload }) => {
  if (!active || !payload?.[0]) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white rounded-lg shadow-lg border px-3 py-2" data-testid="bar-tooltip">
      <p className="text-xs font-semibold text-slate-800">{d.name}</p>
      <p className="text-sm font-bold" style={{ color: CATEGORY_COLORS[d.category] }}>
        {d.value} · {d.percent}%
      </p>
      <p className="text-[10px] text-slate-500 mt-0.5">{CATEGORY_LABELS[d.category]}</p>
    </div>
  );
};

const TimelineTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white rounded-lg shadow-lg border px-3 py-2 text-xs">
      <p className="font-semibold text-slate-800 mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} className="flex items-center gap-1.5" style={{ color: p.color }}>
          <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: p.color }} />
          <span className="font-medium">{p.name}:</span>
          <span className="font-bold">{p.value}</span>
        </p>
      ))}
    </div>
  );
};

function EngagementBarSection({ title, subtitle, icon, data, testId, onItemClick }) {
  if (!data || data.length === 0) return null;

  const total = data.reduce((s, d) => s + (d.cantidad || 0), 0) || 1;
  const chartData = data
    .map((d) => ({
      name: d.nombre,
      value: d.cantidad,
      percent: Math.round((d.cantidad / total) * 1000) / 10, // 1 decimal
      category: classifyClient(d.nombre),
    }))
    .sort((a, b) => b.value - a.value);

  const categoriesPresent = Array.from(new Set(chartData.map((d) => d.category)));

  const height = Math.max(220, chartData.length * 36 + 50);

  return (
    <Card data-testid={testId}>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              {icon}
              {title}
            </CardTitle>
            <p className="text-sm text-slate-500">{subtitle}{onItemClick ? " · Haz clic en una barra para ver quiénes" : ""}</p>
          </div>
          {/* Leyenda de categorías (solo las que aparecen) */}
          <div className="flex items-center gap-3 flex-wrap text-xs">
            {categoriesPresent.map((cat) => (
              <span key={cat} className="inline-flex items-center gap-1.5 text-slate-600 dark:text-slate-300">
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: CATEGORY_COLORS[cat] }} />
                {CATEGORY_LABELS[cat]}
              </span>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={height}>
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 5, right: 60, left: 10, bottom: 5 }}
            barCategoryGap={8}
          >
            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e5e7eb" />
            <XAxis
              type="number"
              tickFormatter={(v) => `${v}%`}
              domain={[0, 100]}
              tick={{ fontSize: 11, fill: "#64748b" }}
            />
            <YAxis
              type="category"
              dataKey="name"
              width={150}
              tick={{ fontSize: 12, fill: "#475569" }}
            />
            <Tooltip content={<BarTooltip />} cursor={{ fill: "rgba(148, 163, 184, 0.1)" }} />
            <Bar
              dataKey="percent"
              radius={[0, 4, 4, 0]}
              cursor={onItemClick ? "pointer" : undefined}
              onClick={onItemClick ? (entry) => entry?.name && onItemClick(entry.name) : undefined}
            >
              {chartData.map((entry, i) => (
                <Cell key={i} fill={CATEGORY_COLORS[entry.category]} />
              ))}
              <LabelList
                dataKey="percent"
                position="right"
                formatter={(v) => `${v}%`}
                style={{ fontSize: 11, fontWeight: 600, fill: "#334155" }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

// ====== Interacción a lo largo del tiempo ======
const RANGO_LABELS = {
  "24h": "Últimas 24 horas",
  "48h": "Últimas 48 horas",
  "72h": "Últimas 72 horas",
  "7d": "Últimos 7 días",
  "30d": "Últimos 30 días",
};

function InteractionTimelineCard({ data, loading, rango, onRangoChange }) {
  // Convert each bucket's start_iso (UTC) into a label in the viewer's local
  // timezone so the X axis shows the user's wall clock (e.g. 9:00 a.m. for a
  // send at 9:00 AM DO time, instead of 13:00 UTC).
  const unit = data?.unidad || "hour";
  const chartData = (data?.buckets || []).map((b) => {
    let label = b.label;
    if (b.start_iso) {
      try {
        const d = new Date(b.start_iso);
        if (unit === "hour") {
          if (rango === "24h") {
            label = d.toLocaleTimeString("es-DO", { hour: "2-digit", minute: "2-digit", hour12: false });
          } else {
            // 48h/72h cruzan días → incluir día para evitar ambigüedad
            const dia = d.toLocaleDateString("es-DO", { day: "2-digit", month: "2-digit" });
            const hora = d.toLocaleTimeString("es-DO", { hour: "2-digit", hour12: false });
            label = `${dia} ${hora}h`;
          }
        } else {
          label = d.toLocaleDateString("es-DO", { day: "2-digit", month: "2-digit" });
        }
      } catch (_) { /* keep server label as fallback */ }
    }
    return {
      label,
      aperturas: b.aperturas,
      clicks: b.clicks,
    };
  });
  const totalAp = data?.total_aperturas || 0;
  const totalClk = data?.total_clicks || 0;

  return (
    <Card data-testid="interaction-timeline-card">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="w-5 h-5 text-teal-600" />
              Interacción a lo largo del tiempo
            </CardTitle>
            <p className="text-sm text-slate-500">
              Evolución reciente de aperturas y clicks · {RANGO_LABELS[rango] || ""} · {totalAp} aperturas · {totalClk} clicks
            </p>
          </div>
          <Select value={rango} onValueChange={onRangoChange}>
            <SelectTrigger className="w-[220px] h-9 text-sm" data-testid="timeline-rango-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(RANGO_LABELS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-12 text-center">
            <RefreshCw className="w-6 h-6 animate-spin mx-auto text-slate-400 mb-2" />
            <p className="text-sm text-slate-500">Cargando serie temporal...</p>
          </div>
        ) : !data?.t0 ? (
          <div className="py-10 text-center">
            <Clock className="w-8 h-8 mx-auto text-slate-300 mb-2" />
            <p className="text-sm text-slate-500">Aún no hay datos suficientes para generar la serie temporal.</p>
          </div>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11, fill: "#64748b" }}
                  interval="preserveStartEnd"
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fontSize: 11, fill: "#64748b" }}
                />
                <Tooltip content={<TimelineTooltip />} />
                <Legend
                  wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
                  iconType="circle"
                />
                <Line
                  type="monotone"
                  dataKey="aperturas"
                  name="Aperturas"
                  stroke="#14b8a6"
                  strokeWidth={2.5}
                  dot={{ r: 3, strokeWidth: 1, fill: "#fff" }}
                  activeDot={{ r: 5 }}
                />
                <Line
                  type="monotone"
                  dataKey="clicks"
                  name="Clicks"
                  stroke="#c084fc"
                  strokeWidth={2.5}
                  dot={{ r: 3, strokeWidth: 1, fill: "#fff" }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
            <p className="text-xs text-slate-400 mt-2 italic">
              Ventana: {(() => {
                try {
                  const ini = new Date(data.t0).toLocaleString('es-DO', { dateStyle: 'medium', timeStyle: 'short' });
                  const fin = data.ventana_fin ? new Date(data.ventana_fin).toLocaleString('es-DO', { dateStyle: 'medium', timeStyle: 'short' }) : 'ahora';
                  return `${ini} → ${fin}`;
                } catch { return data.t0; }
              })()} · {RANGO_LABELS[rango] || ""}
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}



function SchedulerHealthBanner({ health }) {
  if (!health) return null;

  const formatRelative = (iso) => {
    if (!iso) return "nunca";
    try {
      const then = new Date(iso).getTime();
      const diffSec = Math.max(0, Math.floor((readNowMs() - then) / 1000));
      if (diffSec < 60) return `hace ${diffSec}s`;
      if (diffSec < 3600) return `hace ${Math.floor(diffSec / 60)}m`;
      if (diffSec < 86400) return `hace ${Math.floor(diffSec / 3600)}h`;
      return `hace ${Math.floor(diffSec / 86400)}d`;
    } catch {
      return iso;
    }
  };

  const formatAbs = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString("es-DO", { dateStyle: "medium", timeStyle: "short" });
    } catch {
      return iso;
    }
  };

  // Status logic: healthy if last_tick_at within 90s. If never_ran, degraded.
  let status = "healthy";
  let statusLabel = "Operativo";
  let Icon = CheckCircle2;
  let colorBg = "bg-emerald-50 dark:bg-emerald-950/30";
  let colorBorder = "border-emerald-200 dark:border-emerald-900";
  let colorText = "text-emerald-800 dark:text-emerald-300";

  if (health.never_ran) {
    status = "never";
    statusLabel = "Nunca ejecutado";
    Icon = AlertCircle;
    colorBg = "bg-slate-50 dark:bg-slate-900";
    colorBorder = "border-slate-200 dark:border-slate-800";
    colorText = "text-slate-700 dark:text-slate-300";
  } else if (health.last_tick_at) {
    const ageSec = Math.max(0, (readNowMs() - new Date(health.last_tick_at).getTime()) / 1000);
    if (ageSec > 180) {
      status = "stale";
      statusLabel = "Scheduler inactivo";
      Icon = AlertCircle;
      colorBg = "bg-red-50 dark:bg-red-950/30";
      colorBorder = "border-red-200 dark:border-red-900";
      colorText = "text-red-800 dark:text-red-300";
    } else if (health.error || health.skipped_smtp) {
      status = "warning";
      statusLabel = health.skipped_smtp ? "SMTP no configurado" : "Error en última ejecución";
      Icon = AlertTriangle;
      colorBg = "bg-amber-50 dark:bg-amber-950/30";
      colorBorder = "border-amber-200 dark:border-amber-900";
      colorText = "text-amber-800 dark:text-amber-300";
    }
  }

  return (
    <div
      className={`mb-4 rounded-lg border ${colorBorder} ${colorBg} p-4`}
      data-testid="scheduler-health-banner"
    >
      <div className="flex flex-wrap items-start gap-4">
        <div className="flex items-center gap-2 min-w-fit">
          <Activity className={`w-5 h-5 ${colorText}`} />
          <div>
            <div className={`text-sm font-semibold ${colorText}`} data-testid="scheduler-health-status">
              {statusLabel}
            </div>
            <div className="text-[11px] text-slate-500 dark:text-slate-400">
              Scheduler de envíos programados
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-4 text-xs flex-1">
          <div>
            <div className="text-slate-500 dark:text-slate-400">Último tick</div>
            <div className="font-medium text-slate-800 dark:text-slate-200" data-testid="scheduler-last-tick" title={formatAbs(health.last_tick_at)}>
              {health.last_tick_at ? formatRelative(health.last_tick_at) : "nunca"}
            </div>
          </div>
          <div>
            <div className="text-slate-500 dark:text-slate-400">Último despacho</div>
            <div className="font-medium text-slate-800 dark:text-slate-200" data-testid="scheduler-last-dispatch" title={formatAbs(health.last_dispatch_at)}>
              {health.last_dispatch_at ? formatRelative(health.last_dispatch_at) : "sin despachos"}
            </div>
          </div>
          <div>
            <div className="text-slate-500 dark:text-slate-400">Resumen</div>
            <div className="font-medium text-slate-800 dark:text-slate-200">
              <span data-testid="scheduler-candidates">{health.candidates}</span> candidatos ·
              {" "}<span data-testid="scheduler-due">{health.due}</span> due ·
              {" "}<span data-testid="scheduler-dispatched">{health.dispatched}</span> despachados
              {health.failed > 0 && (
                <span className="text-red-600 dark:text-red-400">
                  {" · "}<span data-testid="scheduler-failed">{health.failed}</span> fallidos
                </span>
              )}
            </div>
          </div>
        </div>
        <Icon className={`w-5 h-5 ${colorText} ml-auto`} />
      </div>
      {health.error && (
        <div className="mt-2 text-xs text-red-700 dark:text-red-400" data-testid="scheduler-error-msg">
          Error: {health.error}
        </div>
      )}
    </div>
  );
}
